import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAdvisorDashboard } from "../hooks/useAdvisorDashboard";
import { advisorService } from "../services/advisorService";
import { assistantService } from "../services/assistantService";
import {
  LeadQualifyingQuestionsResponse,
  qualifyingQuestionsService,
} from "../services/qualifyingQuestionsService";
import { useAuth } from "../context/AuthContext";
import {
  CASE_STAGE_BADGE_BY_STATUS,
  SOURCE_OPTIONS,
} from "../constants/dashboard.constants";
import {
  AdvisorAssignedTaskItem,
  AdvisorCaseTaskItem,
  AdvisorCaseEventItem,
  AdvisorCaseListItem,
  AdvisorDirectoryUser,
  CaseCurrentStageDocumentsResponse,
  CaseCollatedDetailsResponse,
  LeadCurrentStageDocumentsResponse,
  LeadDocumentReviewAction,
  LeadDocumentReviewPayload,
  AdvisorLeadDetail,
  AdvisorLeadListItem,
  AdvisorLeadTaskItem,
  AdvisorPaginationMeta,
  LeadStageDocumentFileItem,
  LeadStageDocumentItem,
  ConsultationNoShowPayload,
  LeadQualificationPayload,
  DocumentDetailsResponse,
} from "../types/advisor.types";
import { WorkflowTemplateResponse } from "../types/workflowTemplate.types";
import {
  deriveCaseWorkflow,
  normalizeCaseStageToken,
  type DerivedCaseWorkflow,
} from "../utils/caseWorkflow";
import {
  DerivedLeadWorkflow,
  deriveLeadWorkflow,
} from "../utils/leadWorkflow";
import {
  currentDateTimeLocalInputValue,
  dateTimeLocalToIso,
  isoToDateTimeLocalInputValue,
} from "../utils/dateTime";
import UserMenu from "../ui/components/UserMenu";

type Tone = "blue" | "green" | "amber" | "red" | "purple" | "teal" | "gray";
type QualificationOutcome = LeadQualificationPayload["qualification_outcome"];
type NoShowFollowupOutcome = ConsultationNoShowPayload["followup_outcome"];
type EligibilityAssessment = "ELIGIBLE" | "BORDERLINE" | "INELIGIBLE";
type InternalReviewOutcome = "PASSED" | "REWORK_REQUIRED";
type ClientReviewApproval = "APPROVED" | "CHANGES_REQUESTED" | "DECLINED";
type DecisionOutcome = "APPROVED" | "DECLINED" | "WITHDRAWN";
type InitialLeadContactOutcome = "CONTACTED" | "NO_ANSWER" | "WRONG_NUMBER";
type ActiveSelection = "lead" | "case";
type DocumentModalSource = "lead" | "case";

interface LeadTaskContext {
  lead_id: string;
  status: string;
  template_id: string | null;
}

const dotColor: Record<Tone, string> = {
  blue: "var(--blue-500)",
  green: "var(--green-500)",
  amber: "var(--amber-600)",
  red: "var(--red-500)",
  purple: "var(--purple-600)",
  teal: "var(--teal-600)",
  gray: "var(--gray-500)",
};

const leadBadgeByStatus: Record<string, string> = {
  NEW: "badge-amber",
  CONSULTATION_BOOKED: "badge-blue",
  QUALIFIED: "badge-green",
  ELIGIBLE: "badge-green",
  BORDERLINE: "badge-amber",
  INELIGIBLE: "badge-red",
  DISQUALIFIED: "badge-red",
  MERGED: "badge-gray",
  CONVERTED: "badge-green",
  lead_capture: "badge-amber",
  lead_captured: "badge-amber",
  contacted: "badge-blue",
  consultation: "badge-blue",
  consultation_booked: "badge-blue",
  eligibility_assessment: "badge-green",
  engagement: "badge-green",
  engaged_client: "badge-green",
  closure: "badge-gray",
};

const avatarTonePool: Tone[] = [
  "blue",
  "green",
  "amber",
  "red",
  "purple",
  "teal",
  "gray",
];

const toLabel = (value: string) =>
  value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const toTwoLineStatus = (value: string) => {
  const label = toLabel(value);
  const words = label.split(" ").filter(Boolean);
  if (words.length <= 1) return [label];
  const splitIndex = Math.ceil(words.length / 2);
  return [words.slice(0, splitIndex).join(" "), words.slice(splitIndex).join(" ")];
};

const toInitials = (value: string) => {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "NA";
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
};

const toneFromSeed = (seed: string): Tone => {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash + seed.charCodeAt(index) * (index + 1)) % avatarTonePool.length;
  }
  return avatarTonePool[hash] ?? "gray";
};
const qualificationOutcomeOptions: QualificationOutcome[] = [
  "PROCEED",
  "DECLINE",
  "REFER",
];
const noShowFollowupOutcomeOptions: NoShowFollowupOutcome[] = [
  "REACHED",
  "NOT_REACHED",
  "FOLLOWUP",
];
const noShowFollowupOutcomeLabels: Record<NoShowFollowupOutcome, string> = {
  REACHED: "Reached",
  NOT_REACHED: "Not reached (No show)",
  WRONG_NUMBER: "Wrong number",
  FOLLOWUP: "Followup",
};
const eligibilityAssessmentOptions: EligibilityAssessment[] = [
  "ELIGIBLE",
  "BORDERLINE",
  "INELIGIBLE",
];
const agreementSentStatuses = new Set(["AGREEMENT_SENT", "ENGAGEMENT_LETTER_SENT"]);
const consultationBookingLinkSharedStatuses = new Set([
  "CONSULTATION_BOOKING_INVITE_SENT",
  "CONSULTATION_BOOKING_EMAIL_SENT",
]);
const consultationBookingLinkPendingStatuses = new Set([
  "CONSULTATION_BOOKING_INVITE_PENDING",
  "CONSULTATION_BOOKING_EMAIL_PENDING",
]);
const qualifyingQuestionsAnsweredStatuses = new Set([
  "QUALIFYING_QUESTIONS_ANSWERED",
]);

const workflowTemplateLabel = (template: WorkflowTemplateResponse) => template.name;

const nextActionByLeadStatus: Record<string, { title: string; detail: string }> = {
  NEW: {
    title: "Book consultation",
    detail:
      "Review enquiry details, contact the lead, and confirm a consultation slot to advance this lead.",
  },
  CONSULTATION_BOOKED: {
    title: "Capture consultation status",
    detail:
      "Mark consultation attendance or no-show so assistant follow-up and rebooking can start.",
  },
  QUALIFIED: {
    title: "Prepare onboarding handoff",
    detail:
      "Lead is qualified. Confirm onboarding readiness and coordinate conversion into an active case.",
  },
  ELIGIBLE: {
    title: "Engagement ready",
    detail:
      "Eligibility is complete. Assistant should send engagement letter and track client confirmation.",
  },
  BORDERLINE: {
    title: "Engagement with review",
    detail:
      "Lead is borderline. Proceed with engagement while ensuring adviser review and sign-off.",
  },
  INELIGIBLE: {
    title: "Close out lead communication",
    detail:
      "Lead is ineligible. Record rationale and complete final communication with the client.",
  },
  DISQUALIFIED: {
    title: "Close out lead communication",
    detail:
      "Lead is disqualified. Ensure final communication and supporting notes are complete.",
  },
  CONVERTED: {
    title: "Track active case",
    detail:
      "Lead has been converted into a case. Continue follow-up from the case workflow.",
  },
  MERGED: {
    title: "Review merge reference",
    detail:
      "This lead has been merged. Confirm the canonical lead/case record for further actions.",
  },
  lead_capture: {
    title: "Book consultation",
    detail:
      "Review enquiry details, contact the lead, and confirm a consultation slot to advance this lead.",
  },
  lead_captured: {
    title: "Book consultation",
    detail:
      "Review enquiry details, contact the lead, and confirm a consultation slot to advance this lead.",
  },
  contacted: {
    title: "Book consultation",
    detail:
      "Contact has been established. Confirm consultation timing and capture booking details.",
  },
  consultation: {
    title: "Capture consultation outcome",
    detail:
      "Record consultation notes, set outcome, or mark no-show so assistant follow-up and rebooking can start.",
  },
  consultation_booked: {
    title: "Capture consultation status",
    detail:
      "Mark consultation attendance or no-show so assistant follow-up and rebooking can start.",
  },
  eligibility_assessment: {
    title: "Prepare onboarding handoff",
    detail:
      "Confirm onboarding readiness and coordinate conversion into an active case.",
  },
  engagement: {
    title: "Engagement ready",
    detail:
      "Engagement is in progress. Assistant should send engagement letter and track client confirmation.",
  },
  engaged_client: {
    title: "Engagement ready",
    detail:
      "Engagement is in progress. Assistant should send engagement letter and track client confirmation.",
  },
  closure: {
    title: "Close out lead communication",
    detail:
      "Record final notes and ensure all closure communication is completed.",
  },
};

const normalizeQualificationOutcome = (value: string | null): QualificationOutcome => {
  if (value && qualificationOutcomeOptions.includes(value as QualificationOutcome)) {
    return value as QualificationOutcome;
  }
  return "PROCEED";
};

const normalizeNoShowFollowupOutcome = (
  value: string | null | undefined
): NoShowFollowupOutcome => {
  if (value && noShowFollowupOutcomeOptions.includes(value as NoShowFollowupOutcome)) {
    return value as NoShowFollowupOutcome;
  }
  return "REACHED";
};

const toEligibilityAssessment = (value: string | null): EligibilityAssessment => {
  if (value === "DECLINE") return "INELIGIBLE";
  if (value === "REFER") return "BORDERLINE";
  return "ELIGIBLE";
};

const toQualificationOutcomeFromEligibility = (
  value: EligibilityAssessment
): QualificationOutcome => {
  if (value === "INELIGIBLE") return "DECLINE";
  if (value === "BORDERLINE") return "REFER";
  return "PROCEED";
};

const normalizeInternalReviewOutcome = (
  value: unknown
): InternalReviewOutcome | "" => {
  if (value === "PASSED" || value === "REWORK_REQUIRED") {
    return value;
  }
  return "";
};

const normalizeClientReviewApproval = (
  value: unknown
): ClientReviewApproval | "" => {
  if (
    value === "APPROVED" ||
    value === "CHANGES_REQUESTED" ||
    value === "DECLINED"
  ) {
    return value;
  }
  return "";
};

const normalizeDecisionOutcome = (value: unknown): DecisionOutcome | "" => {
  if (value === "APPROVED" || value === "DECLINED" || value === "WITHDRAWN") {
    return value;
  }
  return "";
};

const readSummaryString = (summary: Record<string, unknown>, key: string): string => {
  const value = summary[key];
  if (typeof value !== "string") return "";
  return value.trim();
};

const normalizeSubmissionReferenceNumber = (value: unknown): string => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const normalizeSubmissionDate = (value: unknown): string => {
  if (typeof value !== "string") return "";
  const normalized = value.trim();
  if (!normalized) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return normalized;
  }
  const datePrefixMatch = normalized.match(/^(\d{4}-\d{2}-\d{2})T/);
  return datePrefixMatch ? datePrefixMatch[1] : "";
};

const normalizeInzRfiPpiRequest = (value: unknown): string => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const normalizeInzRfiPpiDeadline = (value: unknown): string => {
  return normalizeSubmissionDate(value);
};

const ELIGIBILITY_SCORE_PREFIX = "Eligibility score:\n";
const ELIGIBILITY_PATHWAY_PREFIX = "Pathway:\n";

const ELIGIBILITY_NOTE_PREFIXES = [
  [ELIGIBILITY_SCORE_PREFIX, "eligibilityScore"],
  [ELIGIBILITY_PATHWAY_PREFIX, "pathway"],
] as const;

const composeEligibilityNotes = (
  eligibilityScore: string,
  pathway: string
): string | undefined => {
  const score = eligibilityScore.trim();
  const pathwayValue = pathway.trim();

  const sections: string[] = [];
  if (score) sections.push(`${ELIGIBILITY_SCORE_PREFIX}${score}`);
  if (pathwayValue) sections.push(`${ELIGIBILITY_PATHWAY_PREFIX}${pathwayValue}`);

  if (sections.length === 0) return undefined;
  return sections.join("\n\n");
};

const parseEligibilityNotes = (
  notes: string | null
): {
  eligibilityScore: string;
  pathway: string;
} => {
  const empty = {
    eligibilityScore: "",
    pathway: "",
  };
  if (!notes || !notes.trim()) {
    return empty;
  }

  const normalized = notes.trim();
  const hasKnownPrefix = ELIGIBILITY_NOTE_PREFIXES.some(([prefix]) =>
    normalized.includes(prefix)
  );
  if (!hasKnownPrefix) {
    return {
      ...empty,
      pathway: normalized,
    };
  }

  const parsed = { ...empty };
  for (const [prefix, key] of ELIGIBILITY_NOTE_PREFIXES) {
    const start = normalized.indexOf(prefix);
    if (start < 0) continue;

    const valueStart = start + prefix.length;
    let valueEnd = normalized.length;
    for (const [nextPrefix] of ELIGIBILITY_NOTE_PREFIXES) {
      if (nextPrefix === prefix) continue;
      const nextStart = normalized.indexOf(nextPrefix, valueStart);
      if (nextStart >= 0 && nextStart < valueEnd) {
        valueEnd = nextStart;
      }
    }
    parsed[key] = normalized.slice(valueStart, valueEnd).trim();
  }
  return parsed;
};

const toErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === "object" && error !== null && "response" in error) {
    const response = (error as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  return fallback;
};

const readDetailsString = (details: Record<string, unknown>, keys: string[]): string | null => {
  for (const key of keys) {
    const value = details[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return null;
};

const resolveAssignedTaskFullName = (
  task: AdvisorAssignedTaskItem,
  leadNameById: Map<string, string>,
  caseNameById: Map<string, string>
): string | null => {
  if (task.source === "LEAD") {
    const leadName = leadNameById.get(task.source_id);
    if (leadName) return leadName;
  }
  if (task.source === "CASE") {
    const caseName = caseNameById.get(task.source_id);
    if (caseName) return caseName;
  }

  const details = task.details_json ?? {};
  const fullName = readDetailsString(details, [
    "full_name",
    "lead_full_name",
    "client_full_name",
    "client_name",
    "lead_name",
    "applicant_name",
    "name",
  ]);
  if (fullName) return fullName;

  const firstName = readDetailsString(details, [
    "first_name",
    "lead_first_name",
    "client_first_name",
  ]);
  const lastName = readDetailsString(details, [
    "last_name",
    "lead_last_name",
    "client_last_name",
  ]);
  const composedName = `${firstName ?? ""} ${lastName ?? ""}`.trim();
  return composedName || null;
};

const formatDateTime = (value: string | null) => {
  if (!value) return "No due date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (value: string | null) => {
  if (!value) return "Not scheduled";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const formatOptionalDateTime = (value: string | null | undefined, fallback: string) => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatOptionalAnswer = (value: string | null | undefined) => {
  const normalized = value?.trim();
  return normalized ? normalized : "Not provided";
};

const formatConsultationTimeSlot = (value: string | null | undefined) => {
  if (!value) return "";
  const normalized = value.trim();
  if (!normalized) return "";
  if (!/^\d{2}:\d{2}$/.test(normalized)) return normalized;

  const [rawHour, minute] = normalized.split(":");
  const hour = Number(rawHour);
  if (Number.isNaN(hour)) return normalized;

  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12.toString().padStart(2, "0")}:${minute} ${suffix}`;
};

const formatConsultationSlotLabel = (
  dateValue: string | null | undefined,
  timeSlotValue: string | null | undefined
) => {
  if (!dateValue) return null;
  const dateLabel = formatDate(dateValue);
  const timeSlotLabel = formatConsultationTimeSlot(timeSlotValue);
  return timeSlotLabel ? `${dateLabel}, ${timeSlotLabel}` : dateLabel;
};

const documentStatusLabel = (status: string) => status.replaceAll("_", " ");

const documentStatusBadgeClass = (status: string) => {
  switch (status) {
    case "VERIFIED":
      return "badge-green";
    case "REJECTED":
      return "badge-red";
    case "UPLOADED":
    case "UNDER_REVIEW":
      return "badge-blue";
    case "WAIVED":
      return "badge-purple";
    case "NOT_REQUESTED":
    case "REQUESTED":
    case "EXPIRED":
    default:
      return "badge-gray";
  }
};

const latestDocumentFile = (document: LeadStageDocumentItem): LeadStageDocumentFileItem | null => {
  if (!Array.isArray(document.files) || document.files.length === 0) {
    return null;
  }
  return document.files[document.files.length - 1] ?? null;
};

const stringifyCiqValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const ciqResponseToDraft = (response: DocumentDetailsResponse | null): Record<string, string> => {
  if (!response) return {};
  return Object.fromEntries(
    Object.entries(response).map(([key, value]) => [key, stringifyCiqValue(value)])
  );
};

const ciqDraftEntries = (
  draft: Record<string, string>
): Array<{ key: string; value: string }> => {
  return Object.entries(draft).map(([key, value]) => ({
    key,
    value,
  }));
};

const ciqDraftToPayload = (draft: Record<string, string>): DocumentDetailsResponse => {
  const payload: DocumentDetailsResponse = {};
  Object.entries(draft).forEach(([key, value]) => {
    const normalized = value.trim();
    if (!normalized) {
      payload[key] = "";
      return;
    }
    try {
      payload[key] = JSON.parse(normalized);
    } catch {
      payload[key] = value;
    }
  });
  return payload;
};

const extractedDataToDraft = (value: unknown): Record<string, string> => {
  if (value === null || value === undefined) return {};
  if (typeof value === "object" && !Array.isArray(value)) {
    return ciqResponseToDraft(value as DocumentDetailsResponse);
  }
  return { extracted_data: stringifyCiqValue(value) };
};

const hasExtractedDataValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value as Record<string, unknown>).length > 0;
  return true;
};

type AdvisorFact = { label: string; value: string };
type AdvisorFactSection = { title: string; facts: AdvisorFact[] };
type PdfRowStyle = "meta" | "section" | "subsection" | "kv" | "bullet" | "spacer";
type PdfRow = {
  style: PdfRowStyle;
  text?: string;
  indent?: number;
  keepWithNext?: boolean;
};

const toPdfSafeAscii = (value: string): string =>
  value
    .replace(/[^\x20-\x7E]/g, "?")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");

const wrapPdfLine = (line: string, maxChars = 96): string[] => {
  const normalized = line.replace(/\s+/g, " ").trim();
  if (!normalized) return [""];
  const words = normalized.split(" ");
  const wrapped: string[] = [];
  let current = "";
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxChars) {
      current = candidate;
      return;
    }
    if (current) {
      wrapped.push(current);
    }
    current = word.length > maxChars ? word.slice(0, maxChars) : word;
  });
  if (current) wrapped.push(current);
  return wrapped.length > 0 ? wrapped : [""];
};

const pdfStyleConfig: Record<
  PdfRowStyle,
  { fontRef: "F1" | "F2"; fontSize: number; lineHeight: number; before: number; after: number }
> = {
  meta: { fontRef: "F1", fontSize: 9, lineHeight: 12, before: 1, after: 1 },
  section: { fontRef: "F2", fontSize: 13, lineHeight: 16, before: 8, after: 5 },
  subsection: { fontRef: "F2", fontSize: 11, lineHeight: 14, before: 5, after: 3 },
  kv: { fontRef: "F1", fontSize: 10, lineHeight: 13, before: 1, after: 1 },
  bullet: { fontRef: "F1", fontSize: 10, lineHeight: 13, before: 1, after: 1 },
  spacer: { fontRef: "F1", fontSize: 10, lineHeight: 8, before: 0, after: 0 },
};

const buildReadablePdfBlob = (title: string, rows: PdfRow[]): Blob => {
  const pageWidth = 595;
  const pageHeight = 842;
  const marginLeft = 48;
  const marginRight = 48;
  const marginTop = 56;
  const marginBottom = 48;

  const normalizedRows: PdfRow[] = [
    { style: "section", text: title },
    ...rows,
  ];

  const estimateRowHeight = (row: PdfRow): number => {
    const style = pdfStyleConfig[row.style];
    if (row.style === "spacer") {
      return style.lineHeight;
    }
    const indent = row.indent ?? 0;
    const maxChars = Math.max(
      24,
      Math.floor((pageWidth - marginLeft - marginRight - indent) / (style.fontSize * 0.55))
    );
    const wrapped = wrapPdfLine(row.text ?? "", maxChars);
    return style.before + style.after + wrapped.length * style.lineHeight + (row.style === "section" ? 4 : 0);
  };

  const pages: string[] = [];
  let pageCommands: string[] = [];
  let y = pageHeight - marginTop;

  const startPage = () => {
    pageCommands = [];
    y = pageHeight - marginTop;
  };

  const commitPage = () => {
    if (pageCommands.length === 0) {
      pageCommands.push(
        `BT /F1 10 Tf 1 0 0 1 ${marginLeft} ${pageHeight - marginTop} Tm (${toPdfSafeAscii(title)}) Tj ET`
      );
    }
    pages.push(pageCommands.join("\n"));
  };

  startPage();
  normalizedRows.forEach((row, index) => {
    const style = pdfStyleConfig[row.style];
    const nextRow = row.keepWithNext ? normalizedRows[index + 1] : undefined;
    const rowHeight = estimateRowHeight(row);
    const nextHeight = nextRow ? estimateRowHeight(nextRow) : 0;
    if (y - rowHeight - nextHeight < marginBottom) {
      commitPage();
      startPage();
    }

    if (row.style === "spacer") {
      y -= style.lineHeight;
      return;
    }

    const indent = row.indent ?? 0;
    const maxChars = Math.max(
      24,
      Math.floor((pageWidth - marginLeft - marginRight - indent) / (style.fontSize * 0.55))
    );
    const wrapped = wrapPdfLine(row.text ?? "", maxChars);
    y -= style.before;
    wrapped.forEach((line) => {
      const x = marginLeft + indent;
      pageCommands.push(
        `BT /${style.fontRef} ${style.fontSize} Tf 1 0 0 1 ${x.toFixed(2)} ${y.toFixed(2)} Tm (${toPdfSafeAscii(
          line
        )}) Tj ET`
      );
      y -= style.lineHeight;
    });
    if (row.style === "section") {
      const lineY = y + 2;
      pageCommands.push(`0.4 w ${marginLeft} ${lineY.toFixed(2)} m ${pageWidth - marginRight} ${lineY.toFixed(2)} l S`);
      y -= 2;
    }
    y -= style.after;
  });
  commitPage();

  const fontRegularObjectId = 3;
  const fontBoldObjectId = 4;
  const firstPageObjectId = 5;
  const contentObjectStartId = firstPageObjectId + pages.length;
  const totalObjects = contentObjectStartId + pages.length - 1;

  const objects: string[] = [];
  objects[1] = `1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n`;
  const kids = Array.from({ length: pages.length }, (_, index) => `${firstPageObjectId + index} 0 R`).join(" ");
  objects[2] = `2 0 obj\n<< /Type /Pages /Count ${pages.length} /Kids [ ${kids} ] >>\nendobj\n`;
  objects[fontRegularObjectId] =
    `${fontRegularObjectId} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n`;
  objects[fontBoldObjectId] =
    `${fontBoldObjectId} 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>\nendobj\n`;

  pages.forEach((pageStream, pageIndex) => {
    const pageObjectId = firstPageObjectId + pageIndex;
    const contentObjectId = contentObjectStartId + pageIndex;
    objects[pageObjectId] =
      `${pageObjectId} 0 obj\n` +
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] ` +
      `/Resources << /Font << /F1 ${fontRegularObjectId} 0 R /F2 ${fontBoldObjectId} 0 R >> >> /Contents ${contentObjectId} 0 R >>\n` +
      `endobj\n`;
    objects[contentObjectId] =
      `${contentObjectId} 0 obj\n<< /Length ${pageStream.length} >>\nstream\n${pageStream}\nendstream\nendobj\n`;
  });

  let pdf = "%PDF-1.4\n";
  const offsets: number[] = [];
  for (let objectId = 1; objectId <= totalObjects; objectId += 1) {
    offsets[objectId] = pdf.length;
    pdf += objects[objectId];
  }
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${totalObjects + 1}\n`;
  pdf += "0000000000 65535 f \n";
  for (let objectId = 1; objectId <= totalObjects; objectId += 1) {
    pdf += `${String(offsets[objectId]).padStart(10, "0")} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${totalObjects + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return new Blob([pdf], { type: "application/pdf" });
};

const readNestedValue = (source: Record<string, unknown>, path: string[]): unknown => {
  let current: unknown = source;
  path.forEach((key) => {
    if (current && typeof current === "object") {
      current = (current as Record<string, unknown>)[key];
      return;
    }
    current = undefined;
  });
  return current;
};

const toDisplayValue = (
  value: unknown,
  kind: "text" | "status" | "date" | "datetime" | "boolean" = "text"
): string | null => {
  if (value === null || value === undefined) return null;
  if (kind === "boolean") {
    if (typeof value !== "boolean") return null;
    return value ? "Yes" : "No";
  }
  if (typeof value === "number") return String(value);
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  if (!normalized) return null;
  if (kind === "date") return formatDate(normalized);
  if (kind === "datetime") return formatDateTime(normalized);
  if (kind === "status") return toLabel(normalized);
  return normalized;
};

const buildCaseSummaryFacts = (summary: Record<string, unknown>): AdvisorFact[] => {
  const candidates: Array<{ label: string; key: string; kind?: "text" | "status" | "date" | "datetime" }> = [
    { label: "Client name", key: "client_name" },
    { label: "Source channel", key: "source_channel", kind: "status" },
    { label: "Converted from lead", key: "converted_from_lead_number" },
    { label: "Converted at", key: "converted_at", kind: "datetime" },
    { label: "Internal review outcome", key: "internal_review_outcome", kind: "status" },
    { label: "Client review approval", key: "client_review_approval", kind: "status" },
    { label: "Submission reference", key: "submission_reference_number" },
    { label: "Submission date", key: "submission_date", kind: "date" },
    { label: "INZ RFI/PPI request", key: "inz_rfi_ppi_request" },
    { label: "INZ RFI/PPI deadline", key: "inz_rfi_ppi_deadline", kind: "date" },
    { label: "Decision outcome", key: "decision_outcome", kind: "status" },
  ];
  return candidates
    .map((candidate) => {
      const value = toDisplayValue(summary[candidate.key], candidate.kind ?? "text");
      return value ? { label: candidate.label, value } : null;
    })
    .filter((item): item is AdvisorFact => item !== null);
};

const buildLeadMetadataSections = (metadata: Record<string, unknown>): AdvisorFactSection[] => {
  const sections: Array<{
    title: string;
    fields: Array<{
      label: string;
      path: string[];
      kind?: "text" | "status" | "date" | "datetime" | "boolean";
    }>;
  }> = [
    {
      title: "Contact",
      fields: [
        { label: "Initial contact outcome", path: ["contact", "initialContactOutcome"], kind: "status" },
        { label: "Call logged", path: ["contact", "initialContactCallLoggedAt"], kind: "datetime" },
        { label: "Free eligibility consultation offered", path: ["contact", "freeEligibilityConsultationOffered"], kind: "boolean" },
        { label: "Free eligibility consultation done", path: ["contact", "freeEligibilityConsultationDoneAt"], kind: "datetime" },
      ],
    },
    {
      title: "Consultation",
      fields: [
        { label: "Outcome", path: ["consultation", "outcome"], kind: "status" },
        { label: "Follow-up outcome", path: ["consultation", "followupOutcome"], kind: "status" },
        { label: "Follow-up reached at", path: ["consultation", "followupReachedAt"], kind: "datetime" },
        { label: "Booking invite status", path: ["consultation", "bookingInviteStatus"], kind: "status" },
        { label: "Booking invite sent", path: ["consultation", "bookingInviteSentAt"], kind: "datetime" },
      ],
    },
    {
      title: "Engagement",
      fields: [
        { label: "Delivery status", path: ["engagement", "deliveryStatus"], kind: "status" },
        { label: "Agreement sent", path: ["engagement", "agreementSentAt"], kind: "datetime" },
      ],
    },
    {
      title: "Conversion",
      fields: [
        { label: "Case number", path: ["conversion", "caseNumber"] },
        { label: "Converted at", path: ["conversion", "convertedAt"], kind: "datetime" },
        { label: "Welcome email status", path: ["conversion", "clientPortalWelcomeEmailStatus"], kind: "status" },
      ],
    },
    {
      title: "Qualifying Questions",
      fields: [
        { label: "Goals", path: ["qualifyingQuestions", "goals"] },
        { label: "Current visa", path: ["qualifyingQuestions", "currentVisa"] },
        { label: "Timeline", path: ["qualifyingQuestions", "timeline"] },
        { label: "Location", path: ["qualifyingQuestions", "location"] },
        { label: "Submitted at", path: ["qualifyingQuestions", "submittedAt"], kind: "datetime" },
      ],
    },
  ];

  return sections
    .map((section) => {
      const facts = section.fields
        .map((field) => {
          const raw = readNestedValue(metadata, field.path);
          const value = toDisplayValue(raw, field.kind ?? "text");
          return value ? { label: field.label, value } : null;
        })
        .filter((item): item is AdvisorFact => item !== null);
      return { title: section.title, facts };
    })
    .filter((section) => section.facts.length > 0);
};

const resolveCaseLeadId = (caseItem: AdvisorCaseListItem | null): string | null => {
  if (!caseItem) return null;
  const summary = caseItem.summary_json;
  const convertedFromLeadId = summary.converted_from_lead_id;
  if (typeof convertedFromLeadId === "string" && convertedFromLeadId.trim()) {
    return convertedFromLeadId.trim();
  }

  const directLeadId = caseItem.lead_id?.trim();
  if (directLeadId) {
    return directLeadId;
  }
  return null;
};

const resolveCiqDocumentType = (document: LeadStageDocumentItem): string => {
  const rawType = document.source_requirement_json?.document_type;
  if (typeof rawType === "string") {
    const normalized = rawType.trim().toLowerCase();
    if (normalized) return normalized;
  }
  return "passport";
};

const resolveVerificationMode = (document: LeadStageDocumentItem): string | null => {
  const source = document.source_requirement_json;
  if (!source || typeof source !== "object") return null;

  const rootMode = (source as Record<string, unknown>).mode;
  if (typeof rootMode === "string" && rootMode.trim()) {
    return rootMode.trim().toUpperCase();
  }

  const verification = (source as Record<string, unknown>).verification;
  if (!verification || typeof verification !== "object") return null;
  const nestedMode = (verification as Record<string, unknown>).mode;
  if (typeof nestedMode === "string" && nestedMode.trim()) {
    return nestedMode.trim().toUpperCase();
  }

  return null;
};

const isAiReviewDocument = (document: LeadStageDocumentItem): boolean =>
  resolveVerificationMode(document) === "AI_REVIEW";

const ownershipRoles = (
  document: LeadStageDocumentItem,
  key: "submitted_by" | "verified_by"
): string[] => {
  const source = document.source_requirement_json;
  if (!source || typeof source !== "object") return [];
  const rawOwnership = (source as Record<string, unknown>).ownership;
  if (!rawOwnership || typeof rawOwnership !== "object") return [];
  const rawValue = (rawOwnership as Record<string, unknown>)[key];
  if (!Array.isArray(rawValue)) return [];
  return rawValue
    .map((value) => (typeof value === "string" ? value.trim().toLowerCase() : ""))
    .filter((value) => Boolean(value));
};

const advisorCanUploadDocument = (document: LeadStageDocumentItem) => {
  const roles = ownershipRoles(document, "submitted_by");
  return roles.length === 0 || roles.includes("adviser") || roles.includes("advisor");
};

const advisorCanReviewDocument = (document: LeadStageDocumentItem) => {
  const roles = ownershipRoles(document, "verified_by");
  return roles.length === 0 || roles.includes("adviser") || roles.includes("advisor");
};

const taskSourceBadgeClass = (source: string) => (source === "CASE" ? "badge-purple" : "badge-blue");

const readEventString = (eventJson: Record<string, unknown>, key: string): string | null => {
  const value = eventJson[key];
  if (typeof value !== "string") return null;
  const normalized = value.trim();
  return normalized || null;
};

const activityToneForEventType = (eventType: string): Tone => {
  if (eventType.includes("rejected")) return "red";
  if (eventType.includes("verified") || eventType.includes("email_sent")) return "green";
  if (eventType.includes("no_show")) return "amber";
  if (eventType.includes("consultation") || eventType.includes("qualification")) return "amber";
  if (eventType.includes("uploaded") || eventType.includes("stage_changed")) return "blue";
  return "gray";
};

const activityTextForEvent = (event: AdvisorCaseEventItem): string => {
  const payload = event.event_json ?? {};
  const eventType = (event.event_type || "").trim();

  switch (eventType) {
    case "lead.created":
      return "Lead created";
    case "lead.document_uploaded":
      return `${readEventString(payload, "document_label") ?? "Document"} uploaded`;
    case "lead.document_verified":
      return `${readEventString(payload, "document_label") ?? "Document"} reviewed`;
    case "lead.document_rejected":
      return `${readEventString(payload, "document_label") ?? "Document"} rejected`;
    case "lead.task_status_changed":
      return `${readEventString(payload, "task_title") ?? "Lead task"} marked ${toLabel(
        readEventString(payload, "to_status") ?? "updated"
      )}`;
    case "lead.stage_changed":
      return `Lead moved ${toLabel(
        readEventString(payload, "from_status") ?? "unknown"
      )} -> ${toLabel(readEventString(payload, "to_status") ?? "unknown")}`;
    case "lead.consultation_booked":
      return `Consultation booked for ${formatDate(readEventString(payload, "consultation_date"))}`;
    case "lead.contacted_details_updated":
      return "Contacted-stage details saved";
    case "lead.qualifying_questions_email_sent":
      return "Qualifying questions email sent";
    case "lead.consultation_booking_invite_triggered":
      return "Consultation booking link shared with lead";
    case "lead.consultation_booking_invite_todo":
      return "Booking invite TODO recorded; provisional consultation slot set";
    case "lead.stage_transition_blocked":
      return "Lead stage transition blocked by pending stage requirements";
    case "lead.qualifying_questions_email_blocked":
      return "Qualifying questions email send blocked";
    case "lead.qualification_recorded":
      return `Qualification recorded: ${toLabel(
        readEventString(payload, "qualification_outcome") ?? "unknown"
      )}`;
    case "lead.no_show_marked":
      return `Consultation marked no show (${toLabel(
        readEventString(payload, "followup_outcome") ?? "unknown"
      )})`;
    case "lead.email_sent":
      return `${toLabel(readEventString(payload, "email_type") ?? "email")} sent`;
    case "lead.converted_to_client":
      return `Lead converted to client (${readEventString(payload, "case_number") ?? "Case created"})`;
    case "case.created_from_lead":
      return `Client case created from lead ${readEventString(payload, "source_lead_number") ?? ""}`.trim();
    case "case.task_status_changed":
      return `${readEventString(payload, "task_title") ?? "Case task"} marked ${toLabel(
        readEventString(payload, "to_status") ?? "updated"
      )}`;
    case "case.stage_changed":
      return `Case moved ${toLabel(
        readEventString(payload, "from_lifecycle_status") ?? "unknown"
      )} -> ${toLabel(readEventString(payload, "to_lifecycle_status") ?? "unknown")}`;
    default:
      return toLabel(eventType.replaceAll(".", "_"));
  }
};

const StageBar = ({
  stage,
  totalStages,
  compact = false,
}: {
  stage: number;
  totalStages: number;
  compact?: boolean;
}) => (
  <div className={compact ? "stage-bar stage-bar-lg" : "stage-bar"}>
    {Array.from({ length: totalStages }).map((_, index) => {
      const step = index + 1;
      const state =
        step < stage ? "s-done" : step === stage ? "s-active" : "s-pending";
      return <div key={step} className={`s-step ${state}`} />;
    })}
  </div>
);

const AdviserSidebar = ({
  selectedCaseId,
  onSelectCase,
  selectedLeadId,
  onSelectLead,
  cases,
  leads,
  loadingCaseload,
  caseloadError,
  leadPagination,
  casePagination,
  onNextLeadPage,
  onPrevLeadPage,
  onNextCasePage,
  onPrevCasePage,
  workflowTemplatesById,
}: {
  selectedCaseId: string;
  onSelectCase: (caseId: string) => void;
  selectedLeadId: string | null;
  onSelectLead: (leadId: string) => void;
  cases: AdvisorCaseListItem[];
  leads: AdvisorLeadListItem[];
  loadingCaseload: boolean;
  caseloadError: string | null;
  leadPagination: AdvisorPaginationMeta | null;
  casePagination: AdvisorPaginationMeta | null;
  onNextLeadPage: () => void;
  onPrevLeadPage: () => void;
  onNextCasePage: () => void;
  onPrevCasePage: () => void;
  workflowTemplatesById: Record<string, WorkflowTemplateResponse>;
}) => (
  <aside className="adv-sidebar">
    <section className="adv-section">
      <div className="section-label">Open cases</div>
      {cases.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-3)" }}>No assigned cases found.</div>
      ) : (
        cases.map((item) => {
          const caseTemplate = item.template_id
            ? workflowTemplatesById[item.template_id] ?? null
            : null;
          const caseWorkflow = deriveCaseWorkflow(
            item.lifecycle_status,
            item.current_stage_label,
            caseTemplate
          );
          const stage = item.closed_at ? caseWorkflow.totalStages : caseWorkflow.currentStageNumber;
          const stageName = caseWorkflow.currentStageLabel;
          const stageLabel = `Stage ${stage} — ${stageName}`;
          const tone = toneFromSeed(item.case_id);
          const caseName = item.title || item.case_number;
          const caseStatusLines = toTwoLineStatus(item.stage_status);

          return (
            <div
              className={`case-row ${selectedCaseId === item.case_id ? "selected" : ""}`}
              style={{ cursor: "pointer" }}
              key={item.case_id}
              role="button"
              tabIndex={0}
              onClick={() => onSelectCase(item.case_id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectCase(item.case_id);
                }
              }}
            >
              <div className={`avatar av-${tone}`}>{toInitials(caseName)}</div>
              <div className="case-info">
                <div className="case-name">{caseName}</div>
                <div className="case-detail">
                  {item.visa_type} · {item.case_number}
                </div>
                <div className="case-stage-wrap">
                  <StageBar stage={stage} totalStages={caseWorkflow.totalStages} />
                  <div className="stage-lbl">{stageLabel}</div>
                </div>
              </div>
              <span
                className={`badge ${CASE_STAGE_BADGE_BY_STATUS[item.stage_status] ?? "badge-gray"}`}
                style={{ whiteSpace: "normal", lineHeight: 1.1, textAlign: "center" }}
              >
                <span>
                  {caseStatusLines[0]}
                  {caseStatusLines[1] ? <br /> : null}
                  {caseStatusLines[1] ?? null}
                </span>
              </span>
            </div>
          );
        })
      )}
      {(casePagination ? casePagination.total > 5 : cases.length > 5) ? (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button
            className="btn btn-sm"
            onClick={onPrevCasePage}
            disabled={!casePagination || casePagination.offset === 0}
          >
            Prev
          </button>
          <button
            className="btn btn-sm"
            onClick={onNextCasePage}
            disabled={!casePagination || !casePagination.has_more}
          >
            Next
          </button>
        </div>
      ) : null}
      {casePagination ? (
        <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>
          Showing {casePagination.returned} of {casePagination.total}
        </div>
      ) : null}
    </section>
    
    <section className="adv-section">
      <div className="section-label">Assigned leads</div>
      {leads.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-3)" }}>No assigned leads found.</div>
      ) : (
        leads.map((lead) => {
          const leadName = `${lead.first_name} ${lead.last_name}`.trim();
          const tone = toneFromSeed(lead.lead_id);
          const leadTemplate = lead.template_id
            ? workflowTemplatesById[lead.template_id] ?? null
            : null;
          const leadWorkflow = deriveLeadWorkflow(lead.status ?? "", leadTemplate);
          const leadStage = leadWorkflow.currentStageNumber;
          const leadStageLabel = `Stage ${leadStage} — ${leadWorkflow.currentStageLabel}`;
          const leadStatusLines = toTwoLineStatus(lead.status);
          return (
            <div
              className={`case-row ${selectedLeadId === lead.lead_id ? "selected" : ""}`}
              style={{ cursor: "pointer" }}
              key={lead.lead_id}
              role="button"
              tabIndex={0}
              onClick={() => onSelectLead(lead.lead_id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  onSelectLead(lead.lead_id);
                }
              }}
            >
              <div className={`avatar av-${tone}`}>{toInitials(leadName)}</div>
              <div className="case-info">
                <div className="case-name">{leadName}</div>
                <div className="case-detail">
                  {lead.visa_type} · {lead.lead_number}
                </div>
                <div className="case-stage-wrap">
                  <StageBar stage={leadStage} totalStages={leadWorkflow.totalStages} />
                  <div className="stage-lbl">{leadStageLabel}</div>
                </div>
                <div className="stage-lbl" style={{ marginTop: 4 }}>
                  {lead.consultation_date
                    ? `Consultation: ${lead.consultation_date}`
                    : "Consultation: Not booked"}
                </div>
              </div>
              <span
                className={`badge ${leadBadgeByStatus[lead.status] ?? "badge-gray"}`}
                style={{ whiteSpace: "normal", lineHeight: 1.1, textAlign: "center" }}
              >
                <span>
                  {leadStatusLines[0]}
                  {leadStatusLines[1] ? <br /> : null}
                  {leadStatusLines[1] ?? null}
                </span>
              </span>
            </div>
          );
        })
      )}
      {(leadPagination ? leadPagination.total > 5 : leads.length > 5) ? (
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button
            className="btn btn-sm"
            onClick={onPrevLeadPage}
            disabled={!leadPagination || leadPagination.offset === 0}
          >
            Prev
          </button>
          <button
            className="btn btn-sm"
            onClick={onNextLeadPage}
            disabled={!leadPagination || !leadPagination.has_more}
          >
            Next
          </button>
        </div>
      ) : null}
      {leadPagination ? (
        <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 4 }}>
          Showing {leadPagination.returned} of {leadPagination.total}
        </div>
      ) : null}
    </section>
    {caseloadError ? (
      <div className="notice notice-red" style={{ marginTop: 6 }}>
        <i className="ti ti-alert-triangle" />
        <span>{caseloadError}</span>
      </div>
    ) : null}
    {loadingCaseload ? (
      <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 8 }}>
        Loading caseload...
      </div>
    ) : null}
  </aside>
);

const AdviserRightPanel = ({
  assignedTasks,
  assignedTasksLoading,
  assignedTasksError,
  updatingAssignedTaskIds,
  caseNameById,
  resolveTaskFullName,
  onToggleTask,
  recentActivity,
  recentActivityLoading,
  recentActivityError,
}: {
  assignedTasks: AdvisorAssignedTaskItem[];
  assignedTasksLoading: boolean;
  assignedTasksError: string | null;
  updatingAssignedTaskIds: Record<string, boolean>;
  caseNameById: Map<string, string>;
  resolveTaskFullName: (task: AdvisorAssignedTaskItem) => string | null;
  onToggleTask: (task: AdvisorAssignedTaskItem) => void;
  recentActivity: AdvisorCaseEventItem[];
  recentActivityLoading: boolean;
  recentActivityError: string | null;
}) => (
  <aside className="adv-right-panel">
    <section className="adv-section">
      <div className="section-label">Tasks Queue</div>
      {assignedTasksLoading ? (
        <div style={{ fontSize: 12, color: "var(--text-3)" }}>Loading tasks...</div>
      ) : null}
      {assignedTasksError ? (
        <section className="notice notice-red" style={{ marginBottom: 0 }}>
          <i className="ti ti-alert-triangle" />
          <span>{assignedTasksError}</span>
        </section>
      ) : null}
      {!assignedTasksLoading && !assignedTasksError && assignedTasks.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-3)" }}>
          No open tasks assigned to you.
        </div>
      ) : null}
      {!assignedTasksLoading && !assignedTasksError
        ? assignedTasks.map((task) => {
            const dueTimestamp = task.due_at ? new Date(task.due_at).getTime() : NaN;
            const isOverdue = Number.isFinite(dueTimestamp) && dueTimestamp < Date.now();
            const isDone = task.status === "DONE";
            const isUpdating = updatingAssignedTaskIds[task.task_id] ?? false;
            const taskFullName = resolveTaskFullName(task);
            const taskTitle = taskFullName ? `${task.title} - ${taskFullName}` : task.title;

            return (
              <div className="task-item" key={task.task_id}>
                <label className={`task-check ${isDone ? "done" : isOverdue ? "overdue" : ""}`}>
                  <input
                    type="checkbox"
                    className="task-checkbox-input"
                    checked={isDone}
                    disabled={isUpdating}
                    onChange={() => onToggleTask(task)}
                    aria-label={`Toggle task: ${taskTitle}`}
                  />
                  {isDone ? <i className="ti ti-check" style={{ fontSize: 11 }} /> : null}
                </label>
                <div className="task-body">
                  <div className={`task-text ${isDone ? "done-text" : ""}`}>{taskTitle}</div>
                  {task.description ? <div className="task-sub">{task.description}</div> : null}
                  <div className={`task-sub ${isOverdue ? "overdue" : ""}`}>
                    <span className={`badge ${taskSourceBadgeClass(task.source)}`}>
                      {task.source}
                    </span>{" "}
                    {isUpdating
                      ? "Updating..."
                      : `${isOverdue ? "Overdue" : "Due"} · ${formatDateTime(task.due_at)}`}
                  </div>
                </div>
              </div>
            );
          })
        : null}
    </section>

    <section className="adv-section">
      <div className="section-label">Recent activity</div>
      <div style={{ fontSize: 11, color: "var(--text-3)", marginBottom: 6 }}>
        Showing latest activity across your caseload
      </div>
      {recentActivityLoading ? (
        <div style={{ fontSize: 12, color: "var(--text-3)" }}>Loading activity...</div>
      ) : null}
      {recentActivityError ? (
        <section className="notice notice-red" style={{ marginBottom: 0 }}>
          <i className="ti ti-alert-triangle" />
          <span>{recentActivityError}</span>
        </section>
      ) : null}
      {!recentActivityLoading && !recentActivityError && recentActivity.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-3)" }}>No recent activity yet.</div>
      ) : null}
      {!recentActivityLoading && !recentActivityError
        ? recentActivity.map((item) => {
            const entityType = readEventString(item.event_json ?? {}, "entity_type");
            const caseName =
              caseNameById.get(item.case_id) ??
              readEventString(item.event_json ?? {}, "case_number") ??
              "Unknown case";
            const leadName =
              readEventString(item.event_json ?? {}, "lead_number") ??
              readEventString(item.event_json ?? {}, "lead_id") ??
              "Unknown lead";
            const actorName =
              item.actor_name?.trim() ||
              readEventString(item.event_json ?? {}, "actor_name") ||
              (item.actor_user_id ? "Unknown user" : "System");
            const entityLabel = entityType === "lead" ? "Lead" : "Case";
            const entityName = entityType === "lead" ? leadName : caseName;

            return (
              <div className="timeline-item" key={item.event_id}>
                <div className="tl-dot-col">
                  <div
                    className="tl-dot"
                    style={{ background: dotColor[activityToneForEventType(item.event_type)] }}
                  />
                </div>
                <div className="tl-body">
                  <div className="tl-text">
                    {activityTextForEvent(item)} · {entityLabel}: {entityName}
                  </div>
                  <div className="tl-time">
                    {formatDateTime(item.event_at)} · By {actorName}
                  </div>
                </div>
              </div>
            );
          })
        : null}
    </section>
  </aside>
);

const LeadNextActionCard = ({
  selectedLead,
  leadDetail,
  leadWorkflow,
  currentAdvisorId,
  currentAdvisorName,
  leadStageTasks,
  leadTasksLoading,
  leadTasksError,
  updatingLeadTaskIds,
  loading,
  error,
  consultationNotes,
  qualificationOutcome,
  noShowFollowupOutcome,
  noShowReachedAt,
  savingQualification,
  sendingEngagementLetter,
  convertingLead,
  conversionError,
  conversionSuccess,
  agreementSentAt,
  eligibilityScore,
  pathway,
  onboardingEligibility,
  qualificationError,
  qualificationSuccess,
  currentStageDocumentsData,
  leadDocumentsLoading,
  leadDocumentsError,
  uploadingDocumentIds,
  reviewingDocumentIds,
  reviewModalDocument,
  reviewModalMode,
  reviewModalSource,
  reviewModalError,
  reviewRejectReason,
  ciqDocumentDetailsLoading,
  ciqDocumentDetailsError,
  ciqDocumentDetailsResponse,
  ciqDocumentDetailsDraft,
  ciqDocumentDetailsConfirmed,
  onCiqDocumentDetailsConfirmedChange,
  onCiqDocumentDetailsFieldChange,
  availableWorkflowTemplates,
  workflowTemplatesLoading,
  workflowTemplatesError,
  assignmentAdvisors,
  assignmentAssistants,
  assigneesLoading,
  assigneesError,
  leadCaptureTemplateId,
  leadCaptureSourceChannel,
  leadCaptureContactOutcome,
  leadCaptureCallLoggedAt,
  leadCapturePhone,
  leadCaptureAssignedAdvisorId,
  leadCaptureAssignedAssistantId,
  contactedFreeEligibilityConsultationOffered,
  contactedFreeEligibilityConsultationDoneAt,
  contactedFreeEligibilityConsultationDetails,
  contactedDetailsSaving,
  sendQualifyingQuestionsEmailLoading,
  contactedDetailsError,
  contactedDetailsSuccess,
  qualifyingQuestions,
  qualifyingQuestionsLoading,
  qualifyingQuestionsError,
  leadCaptureSaving,
  leadCaptureError,
  leadCaptureSuccess,
  onToggleLeadTask,
  onConsultationNotesChange,
  onQualificationOutcomeChange,
  onNoShowFollowupOutcomeChange,
  onNoShowReachedAtChange,
  onEligibilityScoreChange,
  onPathwayChange,
  onOnboardingEligibilityChange,
  onSaveConsultationOutcome,
  onSaveEligibilityDetails,
  onSendEngagementLetter,
  onUploadDocument,
  onOpenDocumentReview,
  onOpenDocumentView,
  onCloseDocumentModal,
  onReviewRejectReasonChange,
  onReviewAction,
  onLeadCaptureTemplateChange,
  onLeadCaptureSourceChannelChange,
  onLeadCaptureContactOutcomeChange,
  onLeadCaptureCallLoggedAtChange,
  onLeadCapturePhoneChange,
  onLeadCaptureAssignedAdvisorChange,
  onLeadCaptureAssignedAssistantChange,
  onContactedFreeEligibilityConsultationOfferedChange,
  onContactedFreeEligibilityConsultationDoneAtChange,
  onContactedFreeEligibilityConsultationDetailsChange,
  onSaveLeadCapture,
  onSaveContactedDetails,
  onSendQualifyingQuestionsEmail,
  onConvertLeadToClient,
}: {
  selectedLead: AdvisorLeadListItem | null;
  leadDetail: AdvisorLeadDetail | null;
  leadWorkflow: DerivedLeadWorkflow | null;
  currentAdvisorId: string | null;
  currentAdvisorName: string | null;
  leadStageTasks: AdvisorLeadTaskItem[];
  leadTasksLoading: boolean;
  leadTasksError: string | null;
  updatingLeadTaskIds: Record<string, boolean>;
  loading: boolean;
  error: string | null;
  consultationNotes: string;
  qualificationOutcome: QualificationOutcome;
  noShowFollowupOutcome: NoShowFollowupOutcome;
  noShowReachedAt: string;
  savingQualification: boolean;
  sendingEngagementLetter: boolean;
  convertingLead: boolean;
  conversionError: string | null;
  conversionSuccess: string | null;
  agreementSentAt: string | null;
  eligibilityScore: string;
  pathway: string;
  onboardingEligibility: EligibilityAssessment;
  qualificationError: string | null;
  qualificationSuccess: string | null;
  currentStageDocumentsData: LeadCurrentStageDocumentsResponse | null;
  leadDocumentsLoading: boolean;
  leadDocumentsError: string | null;
  uploadingDocumentIds: Record<string, boolean>;
  reviewingDocumentIds: Record<string, boolean>;
  reviewModalDocument: LeadStageDocumentItem | null;
  reviewModalMode: "review" | "view";
  reviewModalSource: DocumentModalSource;
  reviewModalError: string | null;
  reviewRejectReason: string;
  ciqDocumentDetailsLoading: boolean;
  ciqDocumentDetailsError: string | null;
  ciqDocumentDetailsResponse: DocumentDetailsResponse | null;
  ciqDocumentDetailsDraft: Record<string, string>;
  ciqDocumentDetailsConfirmed: boolean;
  onCiqDocumentDetailsConfirmedChange: (value: boolean) => void;
  onCiqDocumentDetailsFieldChange: (key: string, value: string) => void;
  availableWorkflowTemplates: WorkflowTemplateResponse[];
  workflowTemplatesLoading: boolean;
  workflowTemplatesError: string | null;
  assignmentAdvisors: AdvisorDirectoryUser[];
  assignmentAssistants: AdvisorDirectoryUser[];
  assigneesLoading: boolean;
  assigneesError: string | null;
  leadCaptureTemplateId: string;
  leadCaptureSourceChannel: string;
  leadCaptureContactOutcome: InitialLeadContactOutcome | "";
  leadCaptureCallLoggedAt: string;
  leadCapturePhone: string;
  leadCaptureAssignedAdvisorId: string;
  leadCaptureAssignedAssistantId: string;
  contactedFreeEligibilityConsultationOffered: "YES" | "NO" | "";
  contactedFreeEligibilityConsultationDoneAt: string;
  contactedFreeEligibilityConsultationDetails: string;
  contactedDetailsSaving: boolean;
  sendQualifyingQuestionsEmailLoading: boolean;
  contactedDetailsError: string | null;
  contactedDetailsSuccess: string | null;
  qualifyingQuestions: LeadQualifyingQuestionsResponse | null;
  qualifyingQuestionsLoading: boolean;
  qualifyingQuestionsError: string | null;
  leadCaptureSaving: boolean;
  leadCaptureError: string | null;
  leadCaptureSuccess: string | null;
  onToggleLeadTask: (task: AdvisorLeadTaskItem) => void;
  onConsultationNotesChange: (value: string) => void;
  onQualificationOutcomeChange: (value: QualificationOutcome) => void;
  onNoShowFollowupOutcomeChange: (value: NoShowFollowupOutcome) => void;
  onNoShowReachedAtChange: (value: string) => void;
  onEligibilityScoreChange: (value: string) => void;
  onPathwayChange: (value: string) => void;
  onOnboardingEligibilityChange: (value: EligibilityAssessment) => void;
  onSaveConsultationOutcome: () => void;
  onSaveEligibilityDetails: () => void;
  onSendEngagementLetter: () => void;
  onUploadDocument: (document: LeadStageDocumentItem, file: File) => void;
  onOpenDocumentReview: (document: LeadStageDocumentItem, source?: DocumentModalSource) => void;
  onOpenDocumentView: (document: LeadStageDocumentItem, source?: DocumentModalSource) => void;
  onCloseDocumentModal: () => void;
  onReviewRejectReasonChange: (value: string) => void;
  onReviewAction: (action: LeadDocumentReviewAction) => void;
  onLeadCaptureTemplateChange: (value: string) => void;
  onLeadCaptureSourceChannelChange: (value: string) => void;
  onLeadCaptureContactOutcomeChange: (value: InitialLeadContactOutcome | "") => void;
  onLeadCaptureCallLoggedAtChange: (value: string) => void;
  onLeadCapturePhoneChange: (value: string) => void;
  onLeadCaptureAssignedAdvisorChange: (value: string) => void;
  onLeadCaptureAssignedAssistantChange: (value: string) => void;
  onContactedFreeEligibilityConsultationOfferedChange: (value: "YES" | "NO" | "") => void;
  onContactedFreeEligibilityConsultationDoneAtChange: (value: string) => void;
  onContactedFreeEligibilityConsultationDetailsChange: (value: string) => void;
  onSaveLeadCapture: () => void;
  onSaveContactedDetails: () => void;
  onSendQualifyingQuestionsEmail: () => void;
  onConvertLeadToClient: () => void;
}) => {
  const [showMoreLeadDetails, setShowMoreLeadDetails] = useState(false);
  const currentStageDocuments = currentStageDocumentsData?.documents ?? [];
  const reviewModalFile = reviewModalDocument ? latestDocumentFile(reviewModalDocument) : null;
  const isReviewActionInFlight = reviewModalDocument
    ? (reviewingDocumentIds[reviewModalDocument.document_item_id] ?? false)
    : false;
  const shouldUseCiqReviewFlow = Boolean(
    reviewModalMode === "review" && reviewModalDocument && isAiReviewDocument(reviewModalDocument)
  );
  const ciqDetails = ciqDraftEntries(ciqDocumentDetailsDraft);
  const shouldShowExtractedDetailsPanel = Boolean(
    shouldUseCiqReviewFlow || (reviewModalMode === "view" && ciqDetails.length > 0)
  );
  const canReviewWithCiqValidation =
    Boolean(reviewModalFile) &&
    (!shouldUseCiqReviewFlow ||
      (!ciqDocumentDetailsLoading &&
        !ciqDocumentDetailsError &&
        ciqDocumentDetailsResponse !== null &&
        ciqDocumentDetailsConfirmed));
  const canConfirmCiqDetails =
    shouldUseCiqReviewFlow &&
    !ciqDocumentDetailsLoading &&
    !ciqDocumentDetailsError &&
    ciqDocumentDetailsResponse !== null;

  useEffect(() => {
    setShowMoreLeadDetails(false);
  }, [selectedLead?.lead_id]);

  if (!selectedLead) {
    return (
      <section className="card">
        <div className="card-hd">
          <i className="ti ti-user-question" /> Lead details & next action
        </div>
        <div style={{ fontSize: 13, color: "var(--text-2)" }}>
          Select a lead from the left panel to view details and next action.
        </div>
      </section>
    );
  }

  const resolvedStatus = leadDetail?.status ?? selectedLead.status;
  const resolvedStatusLabel = toLabel(resolvedStatus);
  const leadName = leadDetail
    ? `${leadDetail.first_name} ${leadDetail.last_name}`.trim()
    : `${selectedLead.first_name} ${selectedLead.last_name}`.trim();
  const stage = leadWorkflow?.currentStageNumber ?? 1;
  const currentStageKey = (leadWorkflow?.currentStageKey ?? "").trim().toLowerCase();
  const isLeadCaptureStage =
    currentStageKey === "lead_capture" || currentStageKey === "lead_captured";
  const isContactedStage = currentStageKey === "contacted";
  const isConsultationStage = currentStageKey.includes("consultation");
  const isConsultationBookedStage = currentStageKey === "consultation_booked";
  const isEligibilityStage = currentStageKey.includes("eligibility");
  const normalizedFollowupEmailStatus = String(
    leadDetail?.followup_email_status ?? selectedLead.followup_email_status ?? ""
  ).toUpperCase();
  const isConsultationBookingLinkShared = consultationBookingLinkSharedStatuses.has(
    normalizedFollowupEmailStatus
  );
  const isConsultationBookingLinkPending = consultationBookingLinkPendingStatuses.has(
    normalizedFollowupEmailStatus
  );
  const consultationSlotLabel = formatConsultationSlotLabel(
    leadDetail?.consultation_date ?? selectedLead.consultation_date,
    leadDetail?.consultation_time_slot ?? selectedLead.consultation_time_slot
  );
  const effectiveAgreementSentAt = agreementSentAt ?? selectedLead.agreement_sent_at ?? null;
  const isAgreementSent =
    agreementSentStatuses.has(normalizedFollowupEmailStatus) || Boolean(effectiveAgreementSentAt);
  const stageLabel = `Stage ${stage} — ${
    leadWorkflow?.currentStageLabel ?? toLabel(resolvedStatus)
  }`;
  const statusLines = toTwoLineStatus(resolvedStatus);
  const actionKey = currentStageKey || resolvedStatus;
  const action =
    nextActionByLeadStatus[actionKey] ??
    nextActionByLeadStatus[resolvedStatus] ??
    nextActionByLeadStatus.lead_capture;
  const avatarTone = toneFromSeed(selectedLead.lead_id);
  const openLeadStageTasks = leadStageTasks.filter((task) => task.status === "OPEN");
  const hasOpenLeadStageTasks = openLeadStageTasks.length > 0;
  const isUpdatingLeadTasks = Object.values(updatingLeadTaskIds).some(Boolean);
  const isNoShowFollowupAction = noShowFollowupOutcome !== "REACHED";
  const hasSubmittedQualifyingQuestions =
    qualifyingQuestionsAnsweredStatuses.has(normalizedFollowupEmailStatus) ||
    Boolean(qualifyingQuestions?.submitted || qualifyingQuestions?.submitted_at);
  const isContactedStageBlockedByQualifyingQuestions =
    isContactedStage &&
    !hasOpenLeadStageTasks &&
    !qualifyingQuestionsLoading &&
    !qualifyingQuestionsError &&
    !hasSubmittedQualifyingQuestions;
  const blocksQualificationSubmit =
    hasOpenLeadStageTasks &&
    qualificationOutcome !== "REFER" &&
    !isNoShowFollowupAction;
  const documentsPendingReview = currentStageDocuments.filter((document) => {
    if (document.status === "VERIFIED" || document.status === "WAIVED") {
      return false;
    }
    return (
      document.verification_required ||
      document.blocks_stage_exit ||
      document.is_required
    );
  });
  const allLeadDocumentsReviewed = documentsPendingReview.length === 0;
  const canConvertLeadToClient =
    Boolean(leadDetail) &&
    isEligibilityStage &&
    !leadTasksLoading &&
    !leadDocumentsLoading &&
    !isUpdatingLeadTasks &&
    !hasOpenLeadStageTasks &&
    allLeadDocumentsReviewed;
  const isLeadCaptureInitialStage = isLeadCaptureStage && stage === 1;
  const selectedLeadCaptureTemplate = availableWorkflowTemplates.find(
    (template) => template.id === leadCaptureTemplateId
  );
  const assignedAdvisorId = leadDetail?.assigned_advisor_id ?? selectedLead.assigned_advisor_id;
  const assignedAdvisorLabel =
    !assignedAdvisorId
      ? "Unassigned"
      : currentAdvisorId && assignedAdvisorId.toLowerCase() === currentAdvisorId.toLowerCase()
      ? (currentAdvisorName?.trim() || "You")
      : assignedAdvisorId;
  const assignedAssistantId =
    leadDetail?.assigned_assistant_id ?? selectedLead.assigned_assistant_id;
  const assignedAssistantLabel = assignedAssistantId || "Unassigned";
  const requiresAdvisorAssignment = !assignedAdvisorId;
  const requiresAssistantAssignment = !assignedAssistantId;
  const showAssignmentSelectors =
    isLeadCaptureInitialStage && (requiresAdvisorAssignment || requiresAssistantAssignment);
  return (
    <>
    <section className="card">
      <div className="card-hd">
        <i className="ti ti-road" /> Lead workflow
      </div>

      <div className="lead-action-header">
        <div className={`avatar av-${avatarTone}`}>{toInitials(leadName)}</div>
        <div className="lead-action-meta">
          <div className="lead-action-name">{leadName}</div>
          <div className="lead-action-sub">
            {selectedLead.visa_type} · {selectedLead.lead_number}
          </div>
          <div className="lead-action-stage-wrap">
            <StageBar stage={stage} totalStages={leadWorkflow?.totalStages ?? 1} />
            <div className="stage-lbl">{stageLabel}</div>
          </div>
        </div>
        <span
          className={`badge ${leadBadgeByStatus[resolvedStatus] ?? "badge-gray"} lead-action-badge`}
          style={{ whiteSpace: "normal", lineHeight: 1.1, textAlign: "center" }}
        >
          <span>
            {statusLines[0]}
            {statusLines[1] ? <br /> : null}
            {statusLines[1] ?? null}
          </span>
        </span>
      </div>

      {loading ? (
        <div style={{ fontSize: 13, color: "var(--text-2)" }}>Loading lead details...</div>
      ) : null}
      {error ? (
        <div className="notice notice-red" style={{ marginTop: 12, marginBottom: 0 }}>
          <i className="ti ti-alert-triangle" />
          <span>{error}</span>
        </div>
      ) : null}
    </section>
      {!loading && !error && leadDetail ? (
          <div className="g2" style={{ marginTop: 12 }}>
            <div className="stack">
              <section className="card">
                <div className="card-hd">
                  <i className="ti ti-user-circle" /> Lead details
                </div>
                <div className="assistant-stage-detail">
                  <div className="assistant-stage-row">Name: {leadName}</div>
                  <div className="assistant-stage-row">Lead number: {selectedLead.lead_number}</div>
                  <div className="assistant-stage-row">
                    Assigned advisor: {assignedAdvisorLabel}
                  </div>
                  <div className="assistant-stage-row">
                    Assigned assistant: {assignedAssistantLabel}
                  </div>
                  <div className="assistant-stage-row">Lead status: {resolvedStatusLabel}</div>
                  <div className="assistant-stage-row">
                    Next stage: {leadWorkflow?.nextStageLabel ?? "No further stage"}
                  </div>
                  {showMoreLeadDetails ? (
                    <>
                      <div className="assistant-stage-row" style={{ marginTop: 8 }}>
                        Current stage: {leadWorkflow?.currentStageLabel ?? resolvedStatusLabel}
                      </div>
                      <div className="assistant-stage-row">Email: {leadDetail.email}</div>
                      <div className="assistant-stage-row">
                        Phone: {leadDetail.phone ?? "Not provided"}
                      </div>
                      <div className="assistant-stage-row">Visa type: {leadDetail.visa_type}</div>
                      <div className="assistant-stage-row">
                        Source: {leadDetail.source_channel ?? "Not provided"}
                      </div>
                      <div className="assistant-stage-row">
                        Consultation date: {formatDate(leadDetail.consultation_date)}
                      </div>
                      <div className="assistant-stage-row">
                        Current outcome:{" "}
                        {leadDetail.qualification_outcome
                          ? toLabel(leadDetail.qualification_outcome)
                          : "Not set"}
                      </div>
                    </>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreLeadDetails((prev) => !prev);
                    }}
                    style={{
                      marginTop: 8,
                      padding: 0,
                      border: "none",
                      background: "transparent",
                      color: "var(--blue-600)",
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {showMoreLeadDetails ? "Show less" : "Show more"}
                  </button>
                </div>
              </section>

              <section className="card">
                <div className="card-hd">
                  <i className="ti ti-list-check" /> Current stage tasks
                </div>
                {leadTasksLoading ? (
                  <div className="assistant-stage-row">Loading stage tasks...</div>
                ) : null}
                {leadTasksError ? (
                  <div className="notice notice-red" style={{ marginBottom: 0 }}>
                    <i className="ti ti-alert-triangle" />
                    <span>{leadTasksError}</span>
                  </div>
                ) : null}
                {!leadTasksLoading && !leadTasksError && leadStageTasks.length === 0 ? (
                  <div className="assistant-stage-row">No tasks are configured for this stage.</div>
                ) : null}
                {!leadTasksLoading && !leadTasksError
                  ? leadStageTasks.map((task) => {
                      const isDone = task.status === "DONE";
                      return (
                        <div className="task-item" key={task.task_id}>
                          <label className={`task-check ${isDone ? "done" : ""}`}>
                            <input
                              type="checkbox"
                              className="task-checkbox-input"
                              checked={isDone}
                              disabled={updatingLeadTaskIds[task.task_id] ?? false}
                              onChange={() => onToggleLeadTask(task)}
                              aria-label={`Toggle stage task: ${task.title}`}
                            />
                            {isDone ? <i className="ti ti-check" style={{ fontSize: 11 }} /> : null}
                          </label>
                          <div className="task-body">
                            <div className={`task-text ${isDone ? "done-text" : ""}`}>
                              {task.title}
                            </div>
                            {task.description ? (
                              <div className="task-sub">{task.description}</div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })
                  : null}
                {hasOpenLeadStageTasks ? (
                  <div className="notice notice-amber" style={{ marginBottom: 0 }}>
                    <i className="ti ti-lock" />
                    <span>
                      Complete all current-stage tasks before moving this lead to the next stage.
                    </span>
                  </div>
                ) : null}
              </section>

              <section className="card">
                <div className="card-hd">
                  <i className="ti ti-files" /> Documents
                </div>
                {leadDocumentsLoading ? (
                  <div className="assistant-stage-row">Loading stage documents...</div>
                ) : leadDocumentsError ? (
                  <div className="notice notice-red" style={{ marginBottom: 0 }}>
                    <i className="ti ti-alert-triangle" />
                    <span>{leadDocumentsError}</span>
                  </div>
                ) : currentStageDocuments.length === 0 ? (
                  <div className="assistant-stage-row">
                    No documents are configured for this stage.
                  </div>
                ) : (
                  <>
                    <div className="assistant-stage-row" style={{ marginTop: 0 }}>
                      Current stage:{" "}
                      {currentStageDocumentsData?.current_stage_label ??
                        leadWorkflow?.currentStageLabel ??
                        resolvedStatusLabel}
                    </div>
                    <div className="advisor-documents-table-wrap">
                      <table className="advisor-documents-table">
                        <thead>
                          <tr>
                            <th>Document</th>
                            <th>Required</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentStageDocuments.map((document) => {
                            const latestFile = latestDocumentFile(document);
                            const canUpload = advisorCanUploadDocument(document);
                            const canDownload = Boolean(latestFile);
                            const canReview =
                              advisorCanReviewDocument(document) &&
                              Boolean(latestFile) &&
                              document.status !== "VERIFIED";
                            const canView = Boolean(latestFile) && document.status === "VERIFIED";

                            return (
                              <tr key={document.document_item_id}>
                                <td>
                                  <div className="advisor-documents-label">{document.label}</div>
                                  {document.description ? (
                                    <div className="advisor-documents-description">
                                      {document.description}
                                    </div>
                                  ) : null}
                                </td>
                                <td>
                                  <span
                                    className={`badge ${document.is_required ? "badge-amber" : "badge-gray"}`}
                                  >
                                    {document.is_required ? "Required" : "Optional"}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${documentStatusBadgeClass(document.status)}`}>
                                    {documentStatusLabel(document.status)}
                                  </span>
                                </td>
                                <td>
                                  <div className="advisor-documents-actions">
                                    {canUpload ? (
                                      <label
                                        className="btn btn-sm advisor-documents-action-btn"
                                        title={
                                          uploadingDocumentIds[document.document_item_id]
                                            ? "Uploading..."
                                            : "Upload"
                                        }
                                        aria-label={
                                          uploadingDocumentIds[document.document_item_id]
                                            ? "Uploading..."
                                            : "Upload"
                                        }
                                        style={{ position: "relative", overflow: "hidden" }}
                                      >
                                        <i
                                          className={
                                            uploadingDocumentIds[document.document_item_id]
                                              ? "ti ti-loader-2"
                                              : "ti ti-upload"
                                          }
                                        />
                                        <input
                                          type="file"
                                          style={{
                                            position: "absolute",
                                            inset: 0,
                                            opacity: 0,
                                            cursor: "pointer",
                                          }}
                                          disabled={uploadingDocumentIds[document.document_item_id]}
                                          onChange={(event) => {
                                            const selectedFile = event.target.files?.[0];
                                            if (selectedFile) {
                                              onUploadDocument(document, selectedFile);
                                            }
                                            event.currentTarget.value = "";
                                          }}
                                        />
                                      </label>
                                    ) : null}
                                    {canDownload && latestFile ? (
                                      <a
                                        className="btn btn-sm advisor-documents-action-btn"
                                        href={latestFile.blob_url}
                                        download={latestFile.file_name}
                                        target="_blank"
                                        rel="noreferrer"
                                        title="Download"
                                        aria-label="Download"
                                      >
                                        <i className="ti ti-download" />
                                      </a>
                                    ) : null}
                                    {canReview ? (
                                      <button
                                        className="btn btn-sm advisor-documents-action-btn"
                                        disabled={reviewingDocumentIds[document.document_item_id]}
                                        onClick={() => onOpenDocumentReview(document)}
                                        title="Review"
                                        aria-label="Review"
                                      >
                                        <i className="ti ti-search" />
                                      </button>
                                    ) : null}
                                    {canView ? (
                                      <button
                                        className="btn btn-sm advisor-documents-action-btn"
                                        onClick={() => onOpenDocumentView(document)}
                                        title="View"
                                        aria-label="View"
                                      >
                                        <i className="ti ti-eye" />
                                      </button>
                                    ) : null}
                                    {!canUpload && !canDownload && !canReview && !canView ? (
                                      <span className="advisor-documents-muted">No actions</span>
                                    ) : null}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </section>
            </div>

            <section className="card">
            <div className="lead-action-form" style={{ marginTop: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)" }}>
                Actions
              </div>
              <div className="notice notice-blue" style={{ marginBottom: 0 }}>
                <i className="ti ti-bulb" />
                <span>
                  <strong>Next action:</strong> {action.title}. {action.detail}
                </span>
              </div>
              {isLeadCaptureInitialStage ? (
                <div className="lead-action-form" style={{ marginTop: 10 }}>
                  <section className="notice notice-blue">
                    <i className="ti ti-phone-call" />
                    <span>
                      Capture lead details and first contact outcome for this Lead Captured stage.
                    </span>
                  </section>
                  {showAssignmentSelectors ? (
                    <>
                      {requiresAdvisorAssignment ? (
                        <div className="field">
                          <label htmlFor="lead-capture-assigned-advisor">Assign advisor</label>
                          <select
                            id="lead-capture-assigned-advisor"
                            value={leadCaptureAssignedAdvisorId}
                            onChange={(event) =>
                              onLeadCaptureAssignedAdvisorChange(event.target.value)
                            }
                            disabled={assigneesLoading}
                          >
                            <option value="">
                              {assigneesLoading ? "Loading advisors..." : "Select advisor"}
                            </option>
                            {assignmentAdvisors.map((advisor) => (
                              <option key={advisor.id} value={advisor.id}>
                                {`${advisor.first_name} ${advisor.last_name}`.trim()}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : null}
                      {requiresAssistantAssignment ? (
                        <div className="field">
                          <label htmlFor="lead-capture-assigned-assistant">Assign assistant</label>
                          <select
                            id="lead-capture-assigned-assistant"
                            value={leadCaptureAssignedAssistantId}
                            onChange={(event) =>
                              onLeadCaptureAssignedAssistantChange(event.target.value)
                            }
                            disabled={assigneesLoading}
                          >
                            <option value="">
                              {assigneesLoading ? "Loading assistants..." : "Select assistant"}
                            </option>
                            {assignmentAssistants.map((assistant) => (
                              <option key={assistant.id} value={assistant.id}>
                                {`${assistant.first_name} ${assistant.last_name}`.trim()}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : null}
                    </>
                  ) : null}
                  <div className="field">
                    <label htmlFor="lead-capture-template">Visa type / workflow template</label>
                    <select
                      id="lead-capture-template"
                      value={leadCaptureTemplateId}
                      onChange={(event) => onLeadCaptureTemplateChange(event.target.value)}
                      disabled={workflowTemplatesLoading}
                    >
                      <option value="">
                        {workflowTemplatesLoading
                          ? "Loading templates..."
                          : availableWorkflowTemplates.length > 0
                            ? "Select workflow template"
                            : "No workflow templates available"}
                      </option>
                      {availableWorkflowTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {workflowTemplateLabel(template)}
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedLeadCaptureTemplate ? (
                    <div className="assistant-stage-row">
                      {selectedLeadCaptureTemplate.visa_type} ·{" "}
                      {selectedLeadCaptureTemplate.country_code}
                    </div>
                  ) : null}
                  <div className="field">
                    <label htmlFor="lead-capture-source">Lead source</label>
                    <select
                      id="lead-capture-source"
                      value={leadCaptureSourceChannel}
                      onChange={(event) => onLeadCaptureSourceChannelChange(event.target.value)}
                    >
                      {SOURCE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label htmlFor="lead-capture-outcome">Contact outcome</label>
                    <select
                      id="lead-capture-outcome"
                      value={leadCaptureContactOutcome}
                      onChange={(event) =>
                        onLeadCaptureContactOutcomeChange(
                          event.target.value as InitialLeadContactOutcome | ""
                        )
                      }
                    >
                      <option value="">Select outcome</option>
                      <option value="CONTACTED">Contacted</option>
                      <option value="NO_ANSWER">No answer</option>
                      <option value="WRONG_NUMBER">Wrong number</option>
                    </select>
                  </div>
                  {leadCaptureContactOutcome === "CONTACTED" ||
                  leadCaptureContactOutcome === "NO_ANSWER" ? (
                    <div className="field">
                      <label htmlFor="lead-capture-call-logged-at">
                        Call log date and time
                      </label>
                      <input
                        id="lead-capture-call-logged-at"
                        type="datetime-local"
                        value={leadCaptureCallLoggedAt}
                        onChange={(event) =>
                          onLeadCaptureCallLoggedAtChange(event.target.value)
                        }
                      />
                    </div>
                  ) : null}
                  {leadCaptureContactOutcome === "WRONG_NUMBER" ? (
                    <div className="field">
                      <label htmlFor="lead-capture-phone">Correct phone number</label>
                      <input
                        id="lead-capture-phone"
                        type="text"
                        value={leadCapturePhone}
                        onChange={(event) => onLeadCapturePhoneChange(event.target.value)}
                        placeholder="Enter correct phone number"
                      />
                    </div>
                  ) : null}
                  {workflowTemplatesError ? (
                    <div className="notice notice-red" style={{ marginBottom: 0 }}>
                      <i className="ti ti-alert-triangle" />
                      <span>{workflowTemplatesError}</span>
                    </div>
                  ) : null}
                  {assigneesError ? (
                    <div className="notice notice-red" style={{ marginBottom: 0 }}>
                      <i className="ti ti-alert-triangle" />
                      <span>{assigneesError}</span>
                    </div>
                  ) : null}
                  {leadCaptureError ? (
                    <div className="notice notice-red" style={{ marginBottom: 0 }}>
                      <i className="ti ti-alert-triangle" />
                      <span>{leadCaptureError}</span>
                    </div>
                  ) : null}
                  {leadCaptureSuccess ? (
                    <div className="notice notice-green" style={{ marginBottom: 0 }}>
                      <i className="ti ti-check" />
                      <span>{leadCaptureSuccess}</span>
                    </div>
                  ) : null}
                  {hasOpenLeadStageTasks ? (
                    <div className="notice notice-amber" style={{ marginBottom: 0 }}>
                      <i className="ti ti-lock" />
                      <span>
                        Complete all current-stage tasks before saving contact outcome and
                        moving this lead to the next stage.
                      </span>
                    </div>
                  ) : null}
                  <div className="lead-action-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={onSaveLeadCapture}
                      disabled={
                        leadCaptureSaving ||
                        leadTasksLoading ||
                        isUpdatingLeadTasks ||
                        workflowTemplatesLoading
                      }
                    >
                      <i className="ti ti-device-mobile-check" />
                      {leadCaptureSaving ? "Saving..." : "Save lead capture outcome"}
                    </button>
                  </div>
                </div>
              ) : null}

              {isContactedStage ? (
                <div className="lead-action-form" style={{ marginTop: 10 }}>
                  <section className="notice notice-blue">
                    <i className="ti ti-user-question" />
                    <span>
                      Capture contacted-stage details and send qualifying questions email.
                    </span>
                  </section>
                  {isContactedStageBlockedByQualifyingQuestions ? (
                    <section className="notice notice-amber" style={{ marginTop: 10 }}>
                      <i className="ti ti-lock" />
                      <span>
                        Moving to the next stage is blocked because qualifying questions are not
                        submitted yet by the lead.
                      </span>
                    </section>
                  ) : null}
                  <div className="field">
                    <label htmlFor="free-eligibility-consultation-offered">
                      Free eligibility consultation offered
                    </label>
                    <select
                      id="free-eligibility-consultation-offered"
                      value={contactedFreeEligibilityConsultationOffered}
                      onChange={(event) =>
                        onContactedFreeEligibilityConsultationOfferedChange(
                          event.target.value as "YES" | "NO" | ""
                        )
                      }
                    >
                      <option value="">Select option</option>
                      <option value="YES">Yes</option>
                      <option value="NO">No</option>
                    </select>
                  </div>
                  {contactedFreeEligibilityConsultationOffered === "YES" ? (
                    <>
                      <div className="field">
                        <label htmlFor="free-eligibility-consultation-done-at">
                          Consultation completed at
                        </label>
                        <input
                          id="free-eligibility-consultation-done-at"
                          type="datetime-local"
                          value={contactedFreeEligibilityConsultationDoneAt}
                          onChange={(event) =>
                            onContactedFreeEligibilityConsultationDoneAtChange(
                              event.target.value
                            )
                          }
                        />
                      </div>
                      <div className="field">
                        <label htmlFor="free-eligibility-consultation-details">
                          Consultation details
                        </label>
                        <textarea
                          id="free-eligibility-consultation-details"
                          rows={4}
                          value={contactedFreeEligibilityConsultationDetails}
                          onChange={(event) =>
                            onContactedFreeEligibilityConsultationDetailsChange(
                              event.target.value
                            )
                          }
                          placeholder="Capture what was discussed and outcomes."
                        />
                      </div>
                    </>
                  ) : null}
                  {contactedDetailsError ? (
                    <div className="notice notice-red" style={{ marginBottom: 0 }}>
                      <i className="ti ti-alert-triangle" />
                      <span>{contactedDetailsError}</span>
                    </div>
                  ) : null}
                  {contactedDetailsSuccess ? (
                    <div className="notice notice-green" style={{ marginBottom: 0 }}>
                      <i className="ti ti-check" />
                      <span>{contactedDetailsSuccess}</span>
                    </div>
                  ) : null}
                  <div className="lead-action-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={onSaveContactedDetails}
                      disabled={
                        contactedDetailsSaving ||
                        leadTasksLoading ||
                        isUpdatingLeadTasks
                      }
                    >
                      <i className="ti ti-device-floppy" />
                      {contactedDetailsSaving ? "Saving..." : "Save details"}
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={onSendQualifyingQuestionsEmail}
                      disabled={
                        sendQualifyingQuestionsEmailLoading ||
                        contactedDetailsSaving ||
                        leadTasksLoading ||
                        isUpdatingLeadTasks
                      }
                    >
                      <i className="ti ti-mail" />
                      {sendQualifyingQuestionsEmailLoading
                        ? "Sending..."
                        : "Send qualifying questions email"}
                    </button>
                  </div>
                </div>
              ) : null}

              {isConsultationStage ? (
                <div className="lead-action-form" style={{ marginTop: 10 }}>
                  <section className="notice notice-blue">
                    <i className="ti ti-calendar-time" />
                    <span>
                      {isConsultationBookingLinkShared
                        ? consultationSlotLabel
                          ? `Booking link shared with lead. Lead selected consultation slot: ${consultationSlotLabel}.`
                          : "Booking link shared with lead. Waiting for lead to choose a slot."
                        : isConsultationBookingLinkPending
                          ? "Booking link email is queued for delivery to the lead."
                          : consultationSlotLabel
                            ? `Consultation slot selected: ${consultationSlotLabel}.`
                            : "Consultation slot is not selected yet."}
                    </span>
                  </section>
                  {isConsultationBookedStage ? (
                    <section className="card" style={{ marginTop: 10 }}>
                      <div className="card-hd">
                        <i className="ti ti-list-check" /> Qualifying questions
                      </div>
                      <div className="assistant-stage-detail">
                        <div className="assistant-stage-row">
                          Submitted at:{" "}
                          {formatOptionalDateTime(
                            qualifyingQuestions?.submitted_at,
                            qualifyingQuestions?.submitted
                              ? "Submitted"
                              : "Not submitted yet"
                          )}
                        </div>
                        {qualifyingQuestionsLoading ? (
                          <div className="assistant-stage-row">
                            Loading qualifying question responses...
                          </div>
                        ) : null}
                        {qualifyingQuestionsError ? (
                          <div className="notice notice-red" style={{ marginTop: 8 }}>
                            <i className="ti ti-alert-triangle" />
                            <span>{qualifyingQuestionsError}</span>
                          </div>
                        ) : null}
                        {!qualifyingQuestionsLoading && !qualifyingQuestionsError ? (
                          <>
                            <div className="assistant-stage-row">
                              Current visa:{" "}
                              {formatOptionalAnswer(qualifyingQuestions?.current_visa)}
                            </div>
                            <div className="assistant-stage-row">
                              Timeline: {formatOptionalAnswer(qualifyingQuestions?.timeline)}
                            </div>
                            <div className="assistant-stage-row">
                              Goals: {formatOptionalAnswer(qualifyingQuestions?.goals)}
                            </div>
                            <div className="assistant-stage-row">
                              Location: {formatOptionalAnswer(qualifyingQuestions?.location)}
                            </div>
                          </>
                        ) : null}
                      </div>
                    </section>
                  ) : null}
                  {!isConsultationBookedStage ? (
                    <>
                      <div className="field">
                        <label htmlFor="consultation-notes">Consultation notes</label>
                        <textarea
                          id="consultation-notes"
                          rows={5}
                          value={consultationNotes}
                          onChange={(event) => onConsultationNotesChange(event.target.value)}
                          placeholder="Capture consultation summary, concerns, and recommendation."
                        />
                      </div>
                      <div className="field">
                        <label htmlFor="consultation-outcome">Outcome</label>
                        <select
                          id="consultation-outcome"
                          value={qualificationOutcome}
                          onChange={(event) =>
                            onQualificationOutcomeChange(event.target.value as QualificationOutcome)
                          }
                        >
                          {qualificationOutcomeOptions.map((option) => (
                            <option key={option} value={option}>
                              {toLabel(option)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  ) : null}
                  <div className="field">
                    <label htmlFor="no-show-followup-outcome">Cunsultation attented</label>
                    <select
                      id="no-show-followup-outcome"
                      value={noShowFollowupOutcome}
                      onChange={(event) =>
                        onNoShowFollowupOutcomeChange(
                          event.target.value as NoShowFollowupOutcome
                        )
                      }
                    >
                      {noShowFollowupOutcomeOptions.map((option) => (
                        <option key={option} value={option}>
                          {noShowFollowupOutcomeLabels[option]}
                        </option>
                      ))}
                    </select>
                  </div>
                  {noShowFollowupOutcome === "REACHED" ? (
                    <div className="field">
                      <label htmlFor="no-show-reached-at">Reached date and time</label>
                      <input
                        id="no-show-reached-at"
                        type="datetime-local"
                        value={noShowReachedAt}
                        onChange={(event) => onNoShowReachedAtChange(event.target.value)}
                      />
                    </div>
                  ) : null}
                  {qualificationError ? (
                    <div className="notice notice-red" style={{ marginBottom: 0 }}>
                      <i className="ti ti-alert-triangle" />
                      <span>{qualificationError}</span>
                    </div>
                  ) : null}
                  {qualificationSuccess ? (
                    <div className="notice notice-green" style={{ marginBottom: 0 }}>
                      <i className="ti ti-check" />
                      <span>{qualificationSuccess}</span>
                    </div>
                  ) : null}
                  <div className="lead-action-actions">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={onSaveConsultationOutcome}
                      disabled={
                        savingQualification ||
                        leadTasksLoading ||
                        isUpdatingLeadTasks ||
                        blocksQualificationSubmit
                      }
                    >
                      <i className="ti ti-device-floppy" />
                      {savingQualification ? "Saving..." : "Save consultation details"}
                    </button>
                  </div>
                </div>
              ) : null}

              {isEligibilityStage ? (
                <div className="lead-action-form" style={{ marginTop: 10 }}>
                  <div className="field">
                    <label htmlFor="eligibility-score">Eligibility score</label>
                    <input
                      id="eligibility-score"
                      type="text"
                      value={eligibilityScore}
                      onChange={(event) => onEligibilityScoreChange(event.target.value)}
                      placeholder="Enter eligibility score"
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="pathway">Pathway</label>
                    <textarea
                      id="pathway"
                      rows={4}
                      value={pathway}
                      onChange={(event) => onPathwayChange(event.target.value)}
                      placeholder="Capture recommended pathway."
                    />
                  </div>
                  <div className="field">
                    <label htmlFor="eligibility-assessment">Eligibility outcome</label>
                    <select
                      id="eligibility-assessment"
                      value={onboardingEligibility}
                      onChange={(event) =>
                        onOnboardingEligibilityChange(event.target.value as EligibilityAssessment)
                      }
                    >
                      {eligibilityAssessmentOptions.map((option) => (
                        <option key={option} value={option}>
                          {toLabel(option)}
                        </option>
                      ))}
                    </select>
                  </div>
                  {onboardingEligibility === "ELIGIBLE" && isAgreementSent ? (
                    <div className="notice notice-green" style={{ marginBottom: 0 }}>
                      <i className="ti ti-check" />
                      <span>
                        Fee proposal and service agreement sent
                        {effectiveAgreementSentAt
                          ? ` on ${formatDateTime(effectiveAgreementSentAt)}.`
                          : "."}
                      </span>
                    </div>
                  ) : null}
                  {qualificationError ? (
                    <div className="notice notice-red" style={{ marginBottom: 0 }}>
                      <i className="ti ti-alert-triangle" />
                      <span>{qualificationError}</span>
                    </div>
                  ) : null}
                  {qualificationSuccess ? (
                    <div className="notice notice-green" style={{ marginBottom: 0 }}>
                      <i className="ti ti-check" />
                      <span>{qualificationSuccess}</span>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {isEligibilityStage ? (
                <>
                  {conversionError ? (
                    <div className="notice notice-red" style={{ marginTop: 12, marginBottom: 0 }}>
                      <i className="ti ti-alert-triangle" />
                      <span>{conversionError}</span>
                    </div>
                  ) : null}
                  {conversionSuccess ? (
                    <div
                      className="notice notice-green"
                      style={{ marginTop: 12, marginBottom: 0 }}
                    >
                      <i className="ti ti-check" />
                      <span>{conversionSuccess}</span>
                    </div>
                  ) : null}
                  {!canConvertLeadToClient ? (
                    <div
                      className="notice notice-amber"
                      style={{ marginTop: 12, marginBottom: 0 }}
                    >
                      <i className="ti ti-lock" />
                      <span>
                        Complete all current-stage tasks and review all required documents to
                        enable lead conversion.
                        {documentsPendingReview.length > 0
                          ? ` Pending documents: ${documentsPendingReview
                              .slice(0, 3)
                              .map((document) => document.label)
                              .join(", ")}.`
                          : ""}
                      </span>
                    </div>
                  ) : null}
                  <div
                    className="lead-action-actions eligibility-stage-actions"
                    style={{ marginTop: 12 }}
                  >
                    <button
                      className="btn btn-sm"
                      onClick={onSaveEligibilityDetails}
                      disabled={
                        savingQualification ||
                        leadTasksLoading ||
                        isUpdatingLeadTasks
                      }
                    >
                      <i className="ti ti-device-floppy" />
                      {savingQualification ? "Saving..." : "Save eligibility details"}
                    </button>
                    {onboardingEligibility === "ELIGIBLE" && !isAgreementSent ? (
                      <button
                        className="btn btn-sm"
                        onClick={onSendEngagementLetter}
                        disabled={
                          savingQualification ||
                          sendingEngagementLetter ||
                          leadTasksLoading ||
                          isUpdatingLeadTasks
                        }
                      >
                        <i className="ti ti-send" />
                        {sendingEngagementLetter
                          ? "Sending..."
                          : "Send fee proposal and service agreement"}
                      </button>
                    ) : null}
                    <button
                      className="btn btn-primary btn-sm"
                      disabled={convertingLead || !canConvertLeadToClient}
                      onClick={onConvertLeadToClient}
                    >
                      <i className="ti ti-user-check" />
                      {convertingLead ? "Converting..." : "Convert lead to client"}
                    </button>
                  </div>
                </>
              ) : null}
            </div>
            </section>
          </div>        
      ) : null}
      {reviewModalDocument && reviewModalSource === "lead" ? (
        <div
          className="assistant-modal-overlay"
          onClick={() => {
            if (!isReviewActionInFlight) {
              onCloseDocumentModal();
            }
          }}
        >
          <section
            className="assistant-modal card assistant-document-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Review uploaded document"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="assistant-modal-header">
              <div className="card-hd" style={{ marginBottom: 0 }}>
                <i className="ti ti-file-search" />{" "}
                {reviewModalMode === "view" ? "View document" : "Review document"}
              </div>
              <button className="btn btn-sm" disabled={isReviewActionInFlight} onClick={onCloseDocumentModal}>
                <i className="ti ti-x" /> Close
              </button>
            </div>

            <div className="assistant-stage-row" style={{ marginTop: 10 }}>
              {reviewModalDocument.label}
            </div>

            {reviewModalError ? (
              <section className="notice notice-red" style={{ marginTop: 12 }}>
                <i className="ti ti-alert-triangle" />
                <span>{reviewModalError}</span>
              </section>
            ) : null}

            <div
              className={`assistant-document-review-grid${
                shouldShowExtractedDetailsPanel ? "" : " assistant-document-review-grid-single"
              }`}
            >
              <div>
                <div className="assistant-document-preview">
                  {reviewModalFile ? (
                    reviewModalFile.content_type.startsWith("image/") ? (
                      <img src={reviewModalFile.blob_url} alt={reviewModalFile.file_name} />
                    ) : (
                      <iframe
                        title={`Preview ${reviewModalFile.file_name}`}
                        src={reviewModalFile.blob_url}
                      />
                    )
                  ) : (
                    <div className="assistant-stage-row">No uploaded file available for preview.</div>
                  )}
                </div>
                {reviewModalFile ? (
                  <div className="assistant-stage-row" style={{ marginTop: 8 }}>
                    File: {reviewModalFile.file_name} (
                    {Math.round(reviewModalFile.size_bytes / 1024)} KB)
                  </div>
                ) : null}
              </div>

              {shouldShowExtractedDetailsPanel ? (
                <section className="assistant-ciq-panel">
                  <div className="card-hd" style={{ marginBottom: 0, fontSize: 14 }}>
                    <i className="ti ti-scan" /> Extracted details
                  </div>
                  <div className="assistant-stage-row" style={{ marginTop: 8 }}>
                    Source:{" "}
                    {shouldUseCiqReviewFlow
                      ? "CIQ document-details API"
                      : "Stored extracted data"}
                  </div>
                  {ciqDocumentDetailsLoading ? (
                    <div className="assistant-stage-row" style={{ marginTop: 10 }}>
                      Fetching details...
                    </div>
                  ) : null}
                  {!ciqDocumentDetailsLoading && ciqDocumentDetailsError ? (
                    <section
                      className="notice notice-red"
                      style={{ marginTop: 10, marginBottom: 0 }}
                    >
                      <i className="ti ti-alert-triangle" />
                      <span>{ciqDocumentDetailsError}</span>
                    </section>
                  ) : null}
                  {!ciqDocumentDetailsLoading &&
                  !ciqDocumentDetailsError &&
                  ciqDetails.length === 0 ? (
                    <div className="assistant-stage-row" style={{ marginTop: 10 }}>
                      No extracted details returned.
                    </div>
                  ) : null}
                  {!ciqDocumentDetailsLoading &&
                  !ciqDocumentDetailsError &&
                  ciqDetails.length > 0 ? (
                    <div className="assistant-ciq-fields">
                      {ciqDetails.map((entry) => {
                        const renderedValue = entry.value || "";
                        const isMultiline =
                          renderedValue.length > 80 || renderedValue.includes("\n");
                        const isEditable = reviewModalMode === "review" && !isReviewActionInFlight;
                        return (
                          <div className="field assistant-ciq-field" key={entry.key}>
                            <label>{toLabel(entry.key)}</label>
                            {isMultiline ? (
                              <textarea
                                rows={4}
                                value={renderedValue}
                                readOnly={!isEditable}
                                onChange={(event) =>
                                  onCiqDocumentDetailsFieldChange(entry.key, event.target.value)
                                }
                              />
                            ) : (
                              <input
                                type="text"
                                value={renderedValue}
                                readOnly={!isEditable}
                                onChange={(event) =>
                                  onCiqDocumentDetailsFieldChange(entry.key, event.target.value)
                                }
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  {reviewModalMode === "review" ? (
                    <label className="assistant-ciq-confirm">
                      <input
                        type="checkbox"
                        checked={ciqDocumentDetailsConfirmed}
                        disabled={!canConfirmCiqDetails || isReviewActionInFlight}
                        onChange={(event) =>
                          onCiqDocumentDetailsConfirmedChange(event.target.checked)
                        }
                      />
                      <span>I confirm the extracted details are correct.</span>
                    </label>
                  ) : null}
                </section>
              ) : null}
            </div>

            {reviewModalMode === "review" ? (
              <div className="field" style={{ marginTop: 12 }}>
                <label>Rejection reason (only used when rejecting)</label>
                <textarea
                  rows={3}
                  value={reviewRejectReason}
                  disabled={isReviewActionInFlight}
                  onChange={(event) => onReviewRejectReasonChange(event.target.value)}
                  placeholder="Optional reason for rejection."
                />
              </div>
            ) : null}

            <div className="assistant-modal-actions">
              <button className="btn btn-sm" disabled={isReviewActionInFlight} onClick={onCloseDocumentModal}>
                Cancel
              </button>
              {reviewModalMode === "review" ? (
                <>
                  <button
                    className="btn btn-danger btn-sm"
                    disabled={isReviewActionInFlight || !reviewModalFile}
                    onClick={() => onReviewAction("REJECT")}
                  >
                    <i className="ti ti-x" /> {isReviewActionInFlight ? "Saving..." : "Reject"}
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={
                      isReviewActionInFlight ||
                      ciqDocumentDetailsLoading ||
                      !canReviewWithCiqValidation
                    }
                    onClick={() => onReviewAction("VERIFY")}
                  >
                    <i className="ti ti-check" /> {isReviewActionInFlight ? "Saving..." : "Review"}
                  </button>
                </>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
};

const KpiGrid = ({
  openCases,
  assignedLeads,
  openTasks,
  recentEvents,
}: {
  openCases: number;
  assignedLeads: number;
  openTasks: number;
  recentEvents: number;
}) => {
  const cards = [
    {
      label: "Open cases",
      value: String(openCases),
      tone: "blue",
      icon: "ti-briefcase",
      delta: "Live from caseload",
      trend: "up",
    },
    {
      label: "Assigned leads",
      value: String(assignedLeads),
      tone: "teal",
      icon: "ti-users",
      delta: "Live from caseload",
      trend: "up",
    },
    {
      label: "Open tasks",
      value: String(openTasks),
      tone: "amber",
      icon: "ti-checklist",
      delta: "Assigned to you",
      trend: "up",
    },
    {
      label: "Recent events",
      value: String(recentEvents),
      tone: "purple",
      icon: "ti-activity",
      delta: "Latest feed count",
      trend: "up",
    },
  ] as const;

  return (
    <section className="kpi-grid" aria-label="Advisor performance indicators">
      {cards.map((kpi) => (
        <div className="kpi" key={kpi.label}>
          <div className={`kpi-icon ${kpi.tone}`}>
            <i className={`ti ${kpi.icon}`} />
          </div>
          <div className="kpi-val">{kpi.value}</div>
          <div className="kpi-label">{kpi.label}</div>
          <div className={`kpi-delta ${kpi.trend}`}>
            {kpi.trend === "up" ? <i className="ti ti-trending-up" /> : null}
            {kpi.delta}
          </div>
        </div>
      ))}
    </section>
  );
};

const CaseWorkflowCard = ({
  selectedCase,
  currentAdvisorId,
  currentAdvisorName,
  seniorAdvisors,
  selectedSeniorAdvisorId,
  advisorsLoading,
  advisorsError,
  caseStageTasks,
  caseTasksLoading,
  caseTasksError,
  updatingCaseTaskIds,
  caseCurrentStageDocumentsData,
  caseDocumentsLoading,
  caseDocumentsError,
  caseCollatedDetails,
  caseCollatedDetailsLoading,
  caseCollatedDetailsError,
  uploadingDocumentIds,
  recentActivity,
  onToggleCaseTask,
  onUploadDocument,
  onOpenDocumentReview,
  onOpenDocumentView,
  reviewModalDocument,
  reviewModalMode,
  reviewModalSource,
  reviewModalError,
  reviewRejectReason,
  ciqDocumentDetailsLoading,
  ciqDocumentDetailsError,
  ciqDocumentDetailsResponse,
  ciqDocumentDetailsDraft,
  ciqDocumentDetailsConfirmed,
  onCiqDocumentDetailsConfirmedChange,
  onCiqDocumentDetailsFieldChange,
  reviewingDocumentIds,
  onCloseDocumentModal,
  onReviewRejectReasonChange,
  onReviewAction,
  transitioningCaseStage,
  caseTransitionError,
  caseTransitionSuccess,
  selectedCaseWorkflow,
  hasNextCaseStage,
  canAdvanceCaseStage,
  canCloseCase,
  advanceCaseButtonLabel,
  internalReviewOutcome,
  clientReviewApproval,
  clientReviewFeedback,
  decisionOutcome,
  submissionReferenceNumber,
  submissionDate,
  inzRfiPpiRequest,
  inzRfiPpiDeadline,
  onSelectedSeniorAdvisorChange,
  onInternalReviewOutcomeChange,
  onClientReviewApprovalChange,
  onClientReviewFeedbackChange,
  onDecisionOutcomeChange,
  onSubmissionReferenceNumberChange,
  onSubmissionDateChange,
  onInzRfiPpiRequestChange,
  onInzRfiPpiDeadlineChange,
  onMoveCaseToInternalReview,
  onSubmitInternalReviewDecision,
  onSubmitClientReviewDecision,
  onSaveSubmissionDetails,
  onSubmitSubmissionDecision,
  onSubmitFollowUpDecision,
  onSaveDecisionOutcome,
  onAdvanceCaseStage,
  onCloseCase,
}: {
  selectedCase: AdvisorCaseListItem | null;
  currentAdvisorId: string | null;
  currentAdvisorName: string | null;
  seniorAdvisors: AdvisorDirectoryUser[];
  selectedSeniorAdvisorId: string;
  advisorsLoading: boolean;
  advisorsError: string | null;
  caseStageTasks: AdvisorCaseTaskItem[];
  caseTasksLoading: boolean;
  caseTasksError: string | null;
  updatingCaseTaskIds: Record<string, boolean>;
  caseCurrentStageDocumentsData: CaseCurrentStageDocumentsResponse | null;
  caseDocumentsLoading: boolean;
  caseDocumentsError: string | null;
  caseCollatedDetails: CaseCollatedDetailsResponse | null;
  caseCollatedDetailsLoading: boolean;
  caseCollatedDetailsError: string | null;
  uploadingDocumentIds: Record<string, boolean>;
  recentActivity: AdvisorCaseEventItem[];
  onToggleCaseTask: (task: AdvisorCaseTaskItem) => void;
  onUploadDocument: (document: LeadStageDocumentItem, file: File) => void;
  onOpenDocumentReview: (document: LeadStageDocumentItem, source?: DocumentModalSource) => void;
  onOpenDocumentView: (document: LeadStageDocumentItem, source?: DocumentModalSource) => void;
  reviewModalDocument: LeadStageDocumentItem | null;
  reviewModalMode: "review" | "view";
  reviewModalSource: DocumentModalSource;
  reviewModalError: string | null;
  reviewRejectReason: string;
  ciqDocumentDetailsLoading: boolean;
  ciqDocumentDetailsError: string | null;
  ciqDocumentDetailsResponse: DocumentDetailsResponse | null;
  ciqDocumentDetailsDraft: Record<string, string>;
  ciqDocumentDetailsConfirmed: boolean;
  onCiqDocumentDetailsConfirmedChange: (value: boolean) => void;
  onCiqDocumentDetailsFieldChange: (key: string, value: string) => void;
  reviewingDocumentIds: Record<string, boolean>;
  onCloseDocumentModal: () => void;
  onReviewRejectReasonChange: (value: string) => void;
  onReviewAction: (action: LeadDocumentReviewAction) => void;
  transitioningCaseStage: boolean;
  caseTransitionError: string | null;
  caseTransitionSuccess: string | null;
  selectedCaseWorkflow: DerivedCaseWorkflow | null;
  hasNextCaseStage: boolean;
  canAdvanceCaseStage: boolean;
  canCloseCase: boolean;
  advanceCaseButtonLabel: string;
  internalReviewOutcome: InternalReviewOutcome | "";
  clientReviewApproval: ClientReviewApproval | "";
  clientReviewFeedback: string;
  decisionOutcome: DecisionOutcome | "";
  submissionReferenceNumber: string;
  submissionDate: string;
  inzRfiPpiRequest: string;
  inzRfiPpiDeadline: string;
  onSelectedSeniorAdvisorChange: (value: string) => void;
  onInternalReviewOutcomeChange: (value: InternalReviewOutcome | "") => void;
  onClientReviewApprovalChange: (value: ClientReviewApproval | "") => void;
  onClientReviewFeedbackChange: (value: string) => void;
  onDecisionOutcomeChange: (value: DecisionOutcome | "") => void;
  onSubmissionReferenceNumberChange: (value: string) => void;
  onSubmissionDateChange: (value: string) => void;
  onInzRfiPpiRequestChange: (value: string) => void;
  onInzRfiPpiDeadlineChange: (value: string) => void;
  onMoveCaseToInternalReview: () => void;
  onSubmitInternalReviewDecision: () => void;
  onSubmitClientReviewDecision: () => void;
  onSaveSubmissionDetails: () => void;
  onSubmitSubmissionDecision: () => void;
  onSubmitFollowUpDecision: () => void;
  onSaveDecisionOutcome: () => void;
  onAdvanceCaseStage: () => void;
  onCloseCase: () => void;
}) => {
  if (!selectedCase) {
    return (
      <section className="card">
        <div className="card-hd">
          <i className="ti ti-briefcase" /> Case details & next action
        </div>
        <div style={{ fontSize: 13, color: "var(--text-2)" }}>
          Select a case from the left panel to view details and recommended action.
        </div>
      </section>
    );
  }

  const caseName = selectedCase.title || selectedCase.case_number;
  const avatarTone = toneFromSeed(selectedCase.case_id);
  const stage = selectedCase.closed_at
    ? selectedCaseWorkflow?.totalStages ?? 1
    : selectedCaseWorkflow?.currentStageNumber ?? 1;
  const stageStatusLines = toTwoLineStatus(selectedCase.stage_status);
  const stageLabel = `Stage ${stage} — ${
    selectedCaseWorkflow?.currentStageLabel ??
    selectedCase.current_stage_label ??
    toLabel(selectedCase.lifecycle_status)
  }`;
  const lifecycleLabel =
    selectedCaseWorkflow?.currentStageLabel ?? toLabel(selectedCase.lifecycle_status);
  const selectedCaseStageKey = normalizeCaseStageToken(
    selectedCaseWorkflow?.currentStageKey ?? selectedCase.lifecycle_status
  );
  const caseAction = hasNextCaseStage
    ? {
        title: `Move to ${
          selectedCaseWorkflow?.nextStageLabel ??
          toLabel(selectedCaseWorkflow?.nextStageKey ?? "next_stage")
        }`,
        detail:
          "Complete current-stage tasks and required documents, then progress this case to the next workflow stage.",
      }
    : selectedCase.closed_at
    ? {
        title: "Case closed",
        detail: "This case is closed.",
      }
    : {
        title: "Close case when ready",
        detail:
          "This case is at the final workflow stage. Complete all final checks, then close it manually.",
      };
  const assignedAdvisorLabel =
    !selectedCase.assigned_advisor_id
      ? "Unassigned"
      : currentAdvisorId &&
        selectedCase.assigned_advisor_id.toLowerCase() === currentAdvisorId.toLowerCase()
      ? (currentAdvisorName?.trim() || "You")
      : selectedCase.assigned_advisor_id;
  const assignedAssistantLabel = selectedCase.assigned_assistant_id ?? "Unassigned";
  const selectedSeniorAdvisor = selectedCase.assigned_senior_advisor
    ? seniorAdvisors.find(
        (advisor) =>
          advisor.id.toLowerCase() === selectedCase.assigned_senior_advisor?.toLowerCase()
      ) ?? null
    : null;
  const assignedSeniorAdvisorLabel =
    !selectedCase.assigned_senior_advisor
      ? "Unassigned"
      : currentAdvisorId &&
        selectedCase.assigned_senior_advisor.toLowerCase() === currentAdvisorId.toLowerCase()
      ? (currentAdvisorName?.trim() || "You")
      : selectedSeniorAdvisor
      ? `${selectedSeniorAdvisor.first_name} ${selectedSeniorAdvisor.last_name}`.trim()
      : selectedCase.assigned_senior_advisor;
  const selectedCaseOpenTasks = caseStageTasks.filter((task) => task.status === "OPEN");
  const selectedCaseIsApplicationPreparation =
    selectedCaseStageKey === "application_preparation";
  const selectedCaseIsInternalReview = selectedCaseStageKey === "internal_review";
  const selectedCaseIsClientReview = selectedCaseStageKey === "client_review";
  const selectedCaseIsSubmission =
    selectedCaseStageKey === "submission" || selectedCaseStageKey === "visa_application";
  const selectedCaseIsFollowUpAndRfi = selectedCaseStageKey === "follow_up_and_rfi";
  const nextCaseStageActionLabel = selectedCaseWorkflow?.nextStageLabel
    ? selectedCaseWorkflow.nextStageLabel
    : toLabel(selectedCaseWorkflow?.nextStageKey ?? "next_stage");
  const previousCaseStageActionLabel = selectedCaseWorkflow?.previousStageLabel
    ? selectedCaseWorkflow.previousStageLabel
    : toLabel(selectedCaseWorkflow?.previousStageKey ?? "previous_stage");
  const shouldShowGenericAdvanceAction =
    hasNextCaseStage &&
    !selectedCaseIsApplicationPreparation &&
    !selectedCaseIsInternalReview &&
    !selectedCaseIsClientReview &&
    !selectedCaseIsSubmission &&
    !selectedCaseIsFollowUpAndRfi;
  const shouldShowCloseCaseAction = !hasNextCaseStage && !selectedCase.closed_at;
  const canMoveCaseToInternalReview =
    selectedCaseIsApplicationPreparation && selectedCaseOpenTasks.length === 0;
  const canMoveCaseToSubmission =
    selectedCaseIsClientReview &&
    selectedCaseOpenTasks.length === 0 &&
    clientReviewApproval === "APPROVED";
  const canMoveCaseToFollowUp =
    selectedCaseIsSubmission &&
    selectedCaseOpenTasks.length === 0 &&
    submissionReferenceNumber.trim().length > 0 &&
    submissionDate.trim().length > 0;
  const canMoveCaseToDecision =
    selectedCaseIsFollowUpAndRfi && selectedCaseOpenTasks.length === 0;
  const isUpdatingCaseTasks = Object.values(updatingCaseTaskIds).some(Boolean);
  const latestCaseEvent =
    recentActivity.find(
      (event) => event.case_id.toLowerCase() === selectedCase.case_id.toLowerCase()
    ) ?? null;
  const caseCurrentStageDocuments = caseCurrentStageDocumentsData?.documents ?? [];
  const reviewModalFile = reviewModalDocument ? latestDocumentFile(reviewModalDocument) : null;
  const isReviewActionInFlight = reviewModalDocument
    ? (reviewingDocumentIds[reviewModalDocument.document_item_id] ?? false)
    : false;
  const shouldUseCiqReviewFlow = Boolean(
    reviewModalMode === "review" && reviewModalDocument && isAiReviewDocument(reviewModalDocument)
  );
  const ciqDetails = ciqDraftEntries(ciqDocumentDetailsDraft);
  const shouldShowExtractedDetailsPanel = Boolean(
    shouldUseCiqReviewFlow || (reviewModalMode === "view" && ciqDetails.length > 0)
  );
  const canReviewWithCiqValidation =
    Boolean(reviewModalFile) &&
    (!shouldUseCiqReviewFlow ||
      (!ciqDocumentDetailsLoading &&
        !ciqDocumentDetailsError &&
        ciqDocumentDetailsResponse !== null &&
        ciqDocumentDetailsConfirmed));
  const canConfirmCiqDetails =
    shouldUseCiqReviewFlow &&
    !ciqDocumentDetailsLoading &&
    !ciqDocumentDetailsError &&
    ciqDocumentDetailsResponse !== null;
  const isVisaApplicationCaseDetails =
    normalizeCaseStageToken(selectedCase.lifecycle_status) === "visa_application";
  const collatedCase = caseCollatedDetails?.case ?? null;
  const collatedLead = caseCollatedDetails?.lead ?? null;
  const collatedDocuments = caseCollatedDetails?.documents ?? [];
  const collatedSummaryJson =
    collatedCase && typeof collatedCase.summary_json === "object" && collatedCase.summary_json
      ? collatedCase.summary_json
      : {};
  const caseSnapshot = collatedCase ?? selectedCase;
  const collatedLeadMetadata =
    collatedLead && typeof collatedLead.metadata_json === "object" && collatedLead.metadata_json
      ? collatedLead.metadata_json
      : {};
  const collatedSummaryFacts = buildCaseSummaryFacts(collatedSummaryJson);
  const collatedLeadMetadataSections = buildLeadMetadataSections(collatedLeadMetadata);
  const collatedDocumentsByStage = Object.entries(
    collatedDocuments.reduce<Record<string, LeadStageDocumentItem[]>>((acc, document) => {
      const stageKey = (document.stage_key || "unknown_stage").trim() || "unknown_stage";
      if (!acc[stageKey]) {
        acc[stageKey] = [];
      }
      acc[stageKey].push(document);
      return acc;
    }, {})
  ).sort(([left], [right]) => left.localeCompare(right));
  const canDownloadCaseDetailsPdf =
    isVisaApplicationCaseDetails &&
    !caseCollatedDetailsLoading &&
    !caseCollatedDetailsError;
  const handleDownloadCaseDetailsPdf = () => {
    if (!canDownloadCaseDetailsPdf) return;

    const rows: PdfRow[] = [];
    const pushSection = (text: string) => rows.push({ style: "section", text, keepWithNext: true });
    const pushSubsection = (text: string, indent = 6) =>
      rows.push({ style: "subsection", text, indent, keepWithNext: true });
    const pushFact = (label: string, value: string, indent = 12) =>
      rows.push({ style: "kv", text: `${label}: ${value}`, indent });
    const pushBullet = (text: string, indent = 20) =>
      rows.push({ style: "bullet", text: `- ${text}`, indent });
    const pushSpacer = () => rows.push({ style: "spacer" });

    rows.push({
      style: "meta",
      text: `Generated on ${formatDateTime(new Date().toISOString())}`,
    });
    rows.push({
      style: "meta",
      text: "Prepared for advisor sharing with client and reference.",
    });
    pushSpacer();

    pushSection("1. Case Overview");
    pushFact("Case number", caseSnapshot.case_number);
    pushFact("Case title", caseSnapshot.title);
    pushFact("Visa type", caseSnapshot.visa_type);
    pushFact("Country", caseSnapshot.country_code);
    pushFact("Lifecycle status", toLabel(caseSnapshot.lifecycle_status));
    pushFact("Stage status", toLabel(caseSnapshot.stage_status));
    pushFact("Assigned advisor", assignedAdvisorLabel);
    pushFact("Assigned senior advisor", assignedSeniorAdvisorLabel);
    pushFact("Assigned assistant", assignedAssistantLabel);
    pushFact("Opened", formatDate(caseSnapshot.opened_at));
    pushFact("Updated", formatDateTime(caseSnapshot.updated_at));
    pushSpacer();

    pushSection("2. Lead & Conversion Snapshot");
    if (collatedLead) {
      pushFact(
        "Lead",
        `${collatedLead.first_name} ${collatedLead.last_name} (${collatedLead.lead_number})`
      );
      pushFact("Email", collatedLead.email);
      pushFact("Phone", formatOptionalAnswer(collatedLead.phone));
      pushFact("Status", toLabel(collatedLead.status));
      pushFact(
        "Qualification outcome",
        formatOptionalAnswer(collatedLead.qualification_outcome)
      );
      pushFact(
        "Followup email status",
        formatOptionalAnswer(collatedLead.followup_email_status)
      );
      pushFact("Lead updated", formatDateTime(collatedLead.updated_at));
      collatedLeadMetadataSections.forEach((section) => {
        pushSubsection(section.title);
        section.facts.forEach((fact) => {
          pushFact(fact.label, fact.value, 18);
        });
      });
    } else {
      pushBullet("No linked lead details found for this case.", 12);
    }
    pushSpacer();

    pushSection("3. Workflow / Timeline");
    pushFact("Current stage", lifecycleLabel);
    pushFact("Opened", formatDateTime(caseSnapshot.opened_at));
    pushFact("Last updated", formatDateTime(caseSnapshot.updated_at));
    pushFact(
      "Last event",
      latestCaseEvent ? activityTextForEvent(latestCaseEvent) : "No recent case events"
    );
    pushFact("Last event time", formatDateTime(latestCaseEvent?.event_at ?? null));
    collatedSummaryFacts.forEach((fact) => {
      pushFact(fact.label, fact.value);
    });
    pushSpacer();

    pushSection("4. Documents By Stage");
    if (collatedDocumentsByStage.length === 0) {
      pushBullet("No documents found for this case.", 12);
    } else {
      collatedDocumentsByStage.forEach(([stageKey, documents]) => {
        pushSubsection(`${toLabel(stageKey)} (${documents.length})`, 8);
        documents.forEach((document) => {
          pushBullet(`${document.label} (${document.doc_code})`, 14);
          pushFact("Status", toLabel(document.status), 24);
          pushFact("Required", document.is_required ? "Yes" : "No", 24);
          pushFact("Uploaded", formatDateTime(document.uploaded_at), 24);
          pushFact("Verified", formatDateTime(document.verified_at), 24);
          const extractedEntries = ciqDraftEntries(extractedDataToDraft(document.extracted_data));
          if (hasExtractedDataValue(document.extracted_data) && extractedEntries.length > 0) {
            pushSubsection("Extracted data", 24);
            extractedEntries.forEach((entry) => {
              pushFact(toLabel(entry.key), entry.value || "-", 30);
            });
          } else {
            pushFact("Extracted data", "Not available", 24);
          }
        });
      });
    }

    const title = `Case Details - ${caseSnapshot.case_number}`;
    const pdfBlob = buildReadablePdfBlob(title, rows);
    const url = URL.createObjectURL(pdfBlob);
    const anchor = document.createElement("a");
    const fileStamp = new Date().toISOString().slice(0, 10);
    anchor.href = url;
    anchor.download = `${caseSnapshot.case_number}-case-details-${fileStamp}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <section className="card">
        <div className="card-hd">
          <i className="ti ti-road" /> Case workflow
        </div>
        <div className="lead-action-header">
          <div className={`avatar av-${avatarTone}`}>{toInitials(caseName)}</div>
          <div className="lead-action-meta">
            <div className="lead-action-name">{caseName}</div>
            <div className="lead-action-sub">
              {selectedCase.visa_type} · {selectedCase.case_number}
            </div>
            <div className="lead-action-stage-wrap">
              <StageBar stage={stage} totalStages={selectedCaseWorkflow?.totalStages ?? 1} />
              <div className="stage-lbl">{stageLabel}</div>
            </div>
          </div>
          <span
            className={`badge ${CASE_STAGE_BADGE_BY_STATUS[selectedCase.stage_status] ?? "badge-gray"} lead-action-badge`}
            style={{ whiteSpace: "normal", lineHeight: 1.1, textAlign: "center" }}
          >
            <span>
              {stageStatusLines[0]}
              {stageStatusLines[1] ? <br /> : null}
              {stageStatusLines[1] ?? null}
            </span>
          </span>
        </div>
      </section>

      <div className="g2" style={{ marginTop: 12, alignItems: "start" }}>
      <div className="stack">
        <section className="card">
          <div className="card-hd">
            <i className="ti ti-folder" /> Case details
          </div>
          {isVisaApplicationCaseDetails ? (
            <div className="assistant-stage-actions" style={{ marginTop: 8, justifyContent: "flex-end" }}>
              <button
                className="btn btn-sm"
                type="button"
                disabled={!canDownloadCaseDetailsPdf}
                onClick={handleDownloadCaseDetailsPdf}
              >
                <i className="ti ti-download" /> Download PDF
              </button>
            </div>
          ) : null}
          {isVisaApplicationCaseDetails ? (
            <div className="assistant-stage-detail advisor-collated-panel">
              {caseCollatedDetailsLoading ? (
                <div className="assistant-stage-row">Loading collated case details...</div>
              ) : null}
              {caseCollatedDetailsError ? (
                <div className="notice notice-red" style={{ marginTop: 8, marginBottom: 0 }}>
                  <i className="ti ti-alert-triangle" />
                  <span>{caseCollatedDetailsError}</span>
                </div>
              ) : null}
              {!caseCollatedDetailsLoading && !caseCollatedDetailsError ? (
                <>
                  <details className="advisor-collapsible-section" open>
                    <summary>Case Overview</summary>
                    <div className="advisor-collapsible-body">
                      <div className="assistant-stage-row">Case number: {caseSnapshot.case_number}</div>
                      <div className="assistant-stage-row">Case title: {caseSnapshot.title}</div>
                      <div className="assistant-stage-row">Visa type: {caseSnapshot.visa_type}</div>
                      <div className="assistant-stage-row">Country: {caseSnapshot.country_code}</div>
                      <div className="assistant-stage-row">
                        Lifecycle status: {toLabel(caseSnapshot.lifecycle_status)}
                      </div>
                      <div className="assistant-stage-row">
                        Stage status: {toLabel(caseSnapshot.stage_status)}
                      </div>
                      <div className="assistant-stage-row">Assigned advisor: {assignedAdvisorLabel}</div>
                      <div className="assistant-stage-row">
                        Assigned senior advisor: {assignedSeniorAdvisorLabel}
                      </div>
                      <div className="assistant-stage-row">
                        Assigned assistant: {assignedAssistantLabel}
                      </div>
                      <div className="assistant-stage-row">Opened: {formatDate(caseSnapshot.opened_at)}</div>
                      <div className="assistant-stage-row">Updated: {formatDateTime(caseSnapshot.updated_at)}</div>
                    </div>
                  </details>

                  <details className="advisor-collapsible-section">
                    <summary>Lead &amp; Conversion Snapshot</summary>
                    <div className="advisor-collapsible-body">
                      {collatedLead ? (
                        <>
                          <div className="assistant-stage-row">
                            Lead: {collatedLead.first_name} {collatedLead.last_name} ({collatedLead.lead_number})
                          </div>
                          <div className="assistant-stage-row">Email: {collatedLead.email}</div>
                          <div className="assistant-stage-row">
                            Phone: {formatOptionalAnswer(collatedLead.phone)}
                          </div>
                          <div className="assistant-stage-row">
                            Status: {toLabel(collatedLead.status)}
                          </div>
                          <div className="assistant-stage-row">
                            Qualification outcome: {formatOptionalAnswer(collatedLead.qualification_outcome)}
                          </div>
                          <div className="assistant-stage-row">
                            Followup email status: {formatOptionalAnswer(collatedLead.followup_email_status)}
                          </div>
                          <div className="assistant-stage-row">
                            Lead updated: {formatDateTime(collatedLead.updated_at)}
                          </div>
                          {collatedLeadMetadataSections.map((section) => (
                            <div key={section.title} className="advisor-metadata-section">
                              <div className="advisor-compact-key">{section.title}</div>
                              <div className="advisor-compact-grid">
                                {section.facts.map((fact) => (
                                  <div key={`${section.title}-${fact.label}`} className="advisor-compact-item">
                                    <div className="advisor-compact-key">{fact.label}</div>
                                    <div className="advisor-compact-value">{fact.value}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </>
                      ) : (
                        <div className="assistant-stage-row">No linked lead details found for this case.</div>
                      )}
                    </div>
                  </details>

                  <details className="advisor-collapsible-section">
                    <summary>Workflow / Timeline</summary>
                    <div className="advisor-collapsible-body">
                      <div className="assistant-stage-row">Current stage: {lifecycleLabel}</div>
                      <div className="assistant-stage-row">Opened: {formatDateTime(caseSnapshot.opened_at)}</div>
                      <div className="assistant-stage-row">Last updated: {formatDateTime(caseSnapshot.updated_at)}</div>
                      <div className="assistant-stage-row">
                        Last event: {latestCaseEvent ? activityTextForEvent(latestCaseEvent) : "No recent case events"}
                      </div>
                      <div className="assistant-stage-row">
                        Last event time: {formatDateTime(latestCaseEvent?.event_at ?? null)}
                      </div>
                      {collatedSummaryFacts.length > 0 ? (
                        <div className="advisor-compact-grid">
                          {collatedSummaryFacts.map((fact) => (
                            <div key={fact.label} className="advisor-compact-item">
                              <div className="advisor-compact-key">{fact.label}</div>
                              <div className="advisor-compact-value">{fact.value}</div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </details>

                  <details className="advisor-collapsible-section">
                    <summary>Documents By Stage</summary>
                    <div className="advisor-collapsible-body">
                      {collatedDocumentsByStage.length === 0 ? (
                        <div className="assistant-stage-row">No documents found for this case.</div>
                      ) : (
                        collatedDocumentsByStage.map(([stageKey, documents]) => (
                          <details key={stageKey} className="advisor-collapsible-nested">
                            <summary>{toLabel(stageKey)} ({documents.length})</summary>
                            <div className="advisor-collapsible-body">
                              {documents.map((document) => {
                                const extractedEntries = ciqDraftEntries(
                                  extractedDataToDraft(document.extracted_data)
                                );
                                return (
                                  <div key={document.document_item_id} className="advisor-document-collated-card">
                                    <div className="assistant-stage-row">
                                      <strong>{document.label}</strong> ({document.doc_code})
                                    </div>
                                    <div className="assistant-stage-row">
                                      Status: {toLabel(document.status)}
                                    </div>
                                    <div className="assistant-stage-row">
                                      Required: {document.is_required ? "Yes" : "No"}
                                    </div>
                                    <div className="assistant-stage-row">
                                      Uploaded: {formatDateTime(document.uploaded_at)}
                                    </div>
                                    <div className="assistant-stage-row">
                                      Verified: {formatDateTime(document.verified_at)}
                                    </div>
                                    {hasExtractedDataValue(document.extracted_data) ? (
                                      <div className="advisor-extracted-readonly-grid">
                                        {extractedEntries.map((entry) => (
                                          <div className="advisor-extracted-readonly-item" key={entry.key}>
                                            <div className="advisor-extracted-readonly-key">
                                              {toLabel(entry.key)}
                                            </div>
                                            <div className="advisor-extracted-readonly-value">
                                              {entry.value || "-"}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="assistant-stage-row">
                                        No extracted data available for this document.
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </details>
                        ))
                      )}
                    </div>
                  </details>
                </>
              ) : null}
            </div>
          ) : (
            <div className="assistant-stage-detail">
              <div className="assistant-stage-row">Case number: {selectedCase.case_number}</div>
              <div className="assistant-stage-row">Country: {selectedCase.country_code}</div>
              <div className="assistant-stage-row">Assigned advisor: {assignedAdvisorLabel}</div>
              <div className="assistant-stage-row">
                Assigned senior advisor: {assignedSeniorAdvisorLabel}
              </div>
              <div className="assistant-stage-row">Assigned assistant: {assignedAssistantLabel}</div>
              <div className="assistant-stage-row">Opened: {formatDate(selectedCase.opened_at)}</div>
            </div>
          )}
        </section>

        <section className="card">
          <div className="card-hd">
            <i className="ti ti-list-check" /> Current stage tasks
          </div>
          {caseTasksLoading ? (
            <div className="assistant-stage-row">Loading stage tasks...</div>
          ) : null}
          {caseTasksError ? (
            <div className="notice notice-red" style={{ marginBottom: 0 }}>
              <i className="ti ti-alert-triangle" />
              <span>{caseTasksError}</span>
            </div>
          ) : null}
          {!caseTasksLoading && !caseTasksError && caseStageTasks.length === 0 ? (
            <div className="assistant-stage-row">No tasks are configured for this stage.</div>
          ) : null}
          {!caseTasksLoading && !caseTasksError && caseStageTasks.length > 0 ? (
            caseStageTasks.map((task) => {
              const isUpdating = updatingCaseTaskIds[task.task_id] ?? false;
              const isDone = task.status === "DONE";
              const metaParts = [task.description, `Due ${formatDateTime(task.due_at)}`].filter(Boolean);
              return (
                <div className="task-item" key={task.task_id}>
                  <label className={`task-check ${isDone ? "done" : ""}`}>
                    <input
                      type="checkbox"
                      className="task-checkbox-input"
                      checked={isDone}
                      disabled={isUpdating}
                      onChange={() => onToggleCaseTask(task)}
                      aria-label={`Toggle case stage task: ${task.title}`}
                    />
                    {isDone ? <i className="ti ti-check" style={{ fontSize: 11 }} /> : null}
                  </label>
                  <div className="task-body">
                    <div className={`task-text ${isDone ? "done-text" : ""}`}>{task.title}</div>
                    {metaParts.length > 0 ? <div className="task-sub">{metaParts.join(" · ")}</div> : null}
                    {isUpdating ? <div className="task-sub">Saving...</div> : null}
                  </div>
                </div>
              );
            })
          ) : null}
        </section>
        <section className="card">
          <div className="card-hd">
            <i className="ti ti-file-check" /> Current stage documents
          </div>
          {caseDocumentsLoading ? (
            <div style={{ fontSize: 13, color: "var(--text-2)" }}>Loading current-stage documents...</div>
          ) : null}
          {caseDocumentsError ? (
            <div className="notice notice-red" style={{ marginBottom: 10 }}>
              <i className="ti ti-alert-triangle" />
              <span>{caseDocumentsError}</span>
            </div>
          ) : null}
          {!caseDocumentsLoading && !caseDocumentsError && caseCurrentStageDocuments.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--text-2)" }}>
              No current-stage documents configured for this case.
            </div>
          ) : null}
          {!caseDocumentsLoading && !caseDocumentsError && caseCurrentStageDocuments.length > 0 ? (
            <>
              <div className="assistant-stage-row" style={{ marginTop: 0 }}>
                Current stage:{" "}
                {caseCurrentStageDocumentsData?.current_stage_label ??
                  selectedCase.current_stage_label ??
                  lifecycleLabel}
              </div>
              <div className="advisor-documents-table-wrap">
                <table className="advisor-documents-table">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Required</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {caseCurrentStageDocuments.map((document) => {
                      const latestFile = latestDocumentFile(document);
                      const canUpload = advisorCanUploadDocument(document);
                      const canDownload = Boolean(latestFile);
                      const canReview =
                        advisorCanReviewDocument(document) &&
                        Boolean(latestFile) &&
                        document.status !== "VERIFIED";
                      const canView = Boolean(latestFile);
                      return (
                        <tr key={document.document_item_id}>
                          <td>
                            <div className="advisor-documents-label">{document.label}</div>
                            {document.description ? (
                              <div className="advisor-documents-description">
                                {document.description}
                              </div>
                            ) : null}
                          </td>
                          <td>
                            <span
                              className={`badge ${document.is_required ? "badge-amber" : "badge-gray"}`}
                            >
                              {document.is_required ? "Required" : "Optional"}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${documentStatusBadgeClass(document.status)}`}>
                              {documentStatusLabel(document.status)}
                            </span>
                          </td>
                          <td>
                            <div className="advisor-documents-actions">
                              {canUpload ? (
                                <label
                                  className="btn btn-sm advisor-documents-action-btn"
                                  title={
                                    uploadingDocumentIds[document.document_item_id]
                                      ? "Uploading..."
                                      : "Upload"
                                  }
                                  aria-label={
                                    uploadingDocumentIds[document.document_item_id]
                                      ? "Uploading..."
                                      : "Upload"
                                  }
                                  style={{ position: "relative", overflow: "hidden" }}
                                >
                                  <i
                                    className={
                                      uploadingDocumentIds[document.document_item_id]
                                        ? "ti ti-loader-2"
                                        : "ti ti-upload"
                                    }
                                  />
                                  <input
                                    type="file"
                                    style={{
                                      position: "absolute",
                                      inset: 0,
                                      opacity: 0,
                                      cursor: "pointer",
                                    }}
                                    disabled={uploadingDocumentIds[document.document_item_id]}
                                    onChange={(event) => {
                                      const selectedFile = event.target.files?.[0];
                                      if (selectedFile) {
                                        onUploadDocument(document, selectedFile);
                                      }
                                      event.currentTarget.value = "";
                                    }}
                                  />
                                </label>
                              ) : null}
                              {canDownload && latestFile ? (
                                <a
                                  className="btn btn-sm advisor-documents-action-btn"
                                  href={latestFile.blob_url}
                                  download={latestFile.file_name}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Download"
                                  aria-label="Download"
                                >
                                  <i className="ti ti-download" />
                                </a>
                              ) : null}
                              {canReview ? (
                                <button
                                  className="btn btn-sm advisor-documents-action-btn"
                                  disabled={reviewingDocumentIds[document.document_item_id]}
                                  onClick={() => onOpenDocumentReview(document, "case")}
                                  title="Review"
                                  aria-label="Review"
                                >
                                  <i className="ti ti-search" />
                                </button>
                              ) : null}
                              {canView && latestFile ? (
                                <button
                                  type="button"
                                  className="btn btn-sm advisor-documents-action-btn"
                                  onClick={() => onOpenDocumentView(document, "case")}
                                  title="View"
                                  aria-label="View"
                                >
                                  <i className="ti ti-eye" />
                                </button>
                              ) : null}
                              {!canUpload && !canDownload && !canReview && !canView ? (
                                <span className="advisor-documents-muted">No actions</span>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </section>

      </div>

      <section className="card">
        <div className="lead-action-form" style={{ marginTop: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)" }}>
            Actions
          </div>
          <div className="notice notice-blue" style={{ marginBottom: 0 }}>
            <i className="ti ti-bulb" />
            <span>
              <strong>Next action:</strong> {caseAction.title}. {caseAction.detail}
            </span>
          </div>
          {selectedCase.stage_status === "BLOCKED" ? (
            <div className="notice notice-red" style={{ marginBottom: 0, marginTop: 10 }}>
              <i className="ti ti-alert-triangle" />
              <span>
                Case is currently blocked. Resolve blockers and complete pending tasks to progress.
              </span>
            </div>
          ) : null}
          {/* {selectedCaseOpenTasks.length > 0 ? (
            <div className="notice notice-amber" style={{ marginBottom: 0, marginTop: 10 }}>
              <i className="ti ti-checklist" />
              <span>
                {selectedCaseOpenTasks.length} open case task
                {selectedCaseOpenTasks.length === 1 ? "" : "s"} assigned to you.
                {nextDueTask
                  ? ` Next due: ${nextDueTask.title} (${formatDateTime(nextDueTask.due_at)}).`
                  : ""}
              </span>
            </div>
          ) : (
            <div className="notice notice-green" style={{ marginBottom: 0, marginTop: 10 }}>
              <i className="ti ti-check" />
              <span>No open case tasks assigned to you for this case.</span>
            </div>
          )} */}
          {latestCaseEvent ? (
            <div className="notice notice-blue" style={{ marginBottom: 0, marginTop: 10 }}>
              <i className="ti ti-activity" />
              <span>
                Latest update: {activityTextForEvent(latestCaseEvent)} (
                {formatDateTime(latestCaseEvent.event_at)}).
              </span>
            </div>
          ) : null}
          {caseTransitionError ? (
            <div className="notice notice-red" style={{ marginBottom: 0, marginTop: 10 }}>
              <i className="ti ti-alert-triangle" />
              <span>{caseTransitionError}</span>
            </div>
          ) : null}
          {caseTransitionSuccess ? (
            <div className="notice notice-green" style={{ marginBottom: 0, marginTop: 10 }}>
              <i className="ti ti-check" />
              <span>{caseTransitionSuccess}</span>
            </div>
          ) : null}
          {selectedCaseIsApplicationPreparation ? (
            <>
              <div className="field" style={{ marginTop: 12 }}>
                <label htmlFor={`senior-advisor-${selectedCase.case_id}`}>Senior advisor</label>
                <select
                  id={`senior-advisor-${selectedCase.case_id}`}
                  value={selectedSeniorAdvisorId}
                  onChange={(event) => onSelectedSeniorAdvisorChange(event.target.value)}
                  disabled={transitioningCaseStage || advisorsLoading}
                >
                  <option value="">
                    {advisorsLoading ? "Loading advisors..." : "Select senior advisor"}
                  </option>
                  {seniorAdvisors.map((advisor) => (
                    <option key={advisor.id} value={advisor.id}>
                      {`${advisor.first_name} ${advisor.last_name}`.trim()}
                    </option>
                  ))}
                  {!advisorsLoading && seniorAdvisors.length === 0 ? (
                    <option value="" disabled>
                      No other advisors available
                    </option>
                  ) : null}
                </select>
              </div>
              {advisorsError ? (
                <div className="notice notice-red" style={{ marginBottom: 0, marginTop: 10 }}>
                  <i className="ti ti-alert-triangle" />
                  <span>{advisorsError}</span>
                </div>
              ) : null}
              {!canMoveCaseToInternalReview ? (
                <div className="notice notice-amber" style={{ marginBottom: 0, marginTop: 10 }}>
                  <i className="ti ti-lock" />
                  <span>
                    Complete all current-stage tasks before moving this case to Internal Review.
                  </span>
                </div>
              ) : null}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={transitioningCaseStage || caseTasksLoading || isUpdatingCaseTasks}
                  onClick={onMoveCaseToInternalReview}
                >
                  <i className="ti ti-arrow-big-right-lines" />{" "}
                  {transitioningCaseStage ? "Moving..." : `Move to ${nextCaseStageActionLabel}`}
                </button>
              </div>
            </>
          ) : null}
          {selectedCaseIsInternalReview ? (
            <>
              <div className="field" style={{ marginTop: 12 }}>
                <label htmlFor={`internal-review-outcome-${selectedCase.case_id}`}>
                  Review outcome
                </label>
                <select
                  id={`internal-review-outcome-${selectedCase.case_id}`}
                  value={internalReviewOutcome}
                  onChange={(event) =>
                    onInternalReviewOutcomeChange(
                      event.target.value as InternalReviewOutcome | ""
                    )
                  }
                  disabled={transitioningCaseStage}
                >
                  <option value="">Select review outcome</option>
                  <option value="PASSED">Passed</option>
                  <option value="REWORK_REQUIRED">Rework required</option>
                </select>
              </div>
              {internalReviewOutcome === "PASSED" ? (
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={transitioningCaseStage || caseTasksLoading || isUpdatingCaseTasks}
                    onClick={onSubmitInternalReviewDecision}
                  >
                    <i className="ti ti-arrow-big-right-lines" />{" "}
                    {transitioningCaseStage ? "Moving..." : `Move to ${nextCaseStageActionLabel}`}
                  </button>
                </div>
              ) : null}
              {internalReviewOutcome === "REWORK_REQUIRED" ? (
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={transitioningCaseStage}
                    onClick={onSubmitInternalReviewDecision}
                  >
                    <i className="ti ti-arrow-big-left-lines" />{" "}
                    {transitioningCaseStage
                      ? "Moving..."
                      : `Move to ${previousCaseStageActionLabel}`}
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
          {selectedCaseIsClientReview ? (
            <>
              <div className="field" style={{ marginTop: 12 }}>
                <label htmlFor={`client-review-approval-${selectedCase.case_id}`}>
                  Client approval
                </label>
                <select
                  id={`client-review-approval-${selectedCase.case_id}`}
                  value={clientReviewApproval}
                  onChange={(event) =>
                    onClientReviewApprovalChange(
                      event.target.value as ClientReviewApproval | ""
                    )
                  }
                  disabled={transitioningCaseStage}
                >
                  <option value="">Select approval status</option>
                  <option value="APPROVED">Approved</option>
                  <option value="CHANGES_REQUESTED">Changes requested</option>
                  <option value="DECLINED">Declined</option>
                </select>
              </div>
              <div className="field" style={{ marginTop: 12 }}>
                <label htmlFor={`client-review-feedback-${selectedCase.case_id}`}>
                  Client feedback
                </label>
                <textarea
                  id={`client-review-feedback-${selectedCase.case_id}`}
                  rows={4}
                  value={clientReviewFeedback}
                  onChange={(event) => onClientReviewFeedbackChange(event.target.value)}
                  disabled={transitioningCaseStage}
                  placeholder="Capture client comments or requested changes."
                />
              </div>
              {!canMoveCaseToSubmission ? (
                <div className="notice notice-amber" style={{ marginBottom: 0, marginTop: 10 }}>
                  <i className="ti ti-lock" />
                  <span>
                    Set client approval to Approved and complete all current-stage tasks before moving to Submission.
                  </span>
                </div>
              ) : null}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={transitioningCaseStage || caseTasksLoading || isUpdatingCaseTasks}
                  onClick={onSubmitClientReviewDecision}
                >
                  <i className="ti ti-arrow-big-right-lines" />{" "}
                  {transitioningCaseStage ? "Moving..." : `Move to ${nextCaseStageActionLabel}`}
                </button>
              </div>
            </>
          ) : null}
          {selectedCaseIsSubmission ? (
            <>
              <div className="field" style={{ marginTop: 12 }}>
                <label htmlFor={`submission-reference-${selectedCase.case_id}`}>
                  INZ Application Reference
                </label>
                <input
                  id={`submission-reference-${selectedCase.case_id}`}
                  type="text"
                  value={submissionReferenceNumber}
                  onChange={(event) => onSubmissionReferenceNumberChange(event.target.value)}
                  disabled={transitioningCaseStage}
                  placeholder="Enter INZ application reference"
                />
              </div>
              <div className="field" style={{ marginTop: 12 }}>
                <label htmlFor={`submission-date-${selectedCase.case_id}`}>Lodgement date</label>
                <input
                  id={`submission-date-${selectedCase.case_id}`}
                  type="date"
                  value={submissionDate}
                  onChange={(event) => onSubmissionDateChange(event.target.value)}
                  disabled={transitioningCaseStage}
                />
              </div>
              <div className="field" style={{ marginTop: 12 }}>
                <label htmlFor={`inz-rfi-ppi-request-${selectedCase.case_id}`}>
                  INZ RFI or PPI request
                </label>
                <textarea
                  id={`inz-rfi-ppi-request-${selectedCase.case_id}`}
                  rows={4}
                  value={inzRfiPpiRequest}
                  onChange={(event) => onInzRfiPpiRequestChange(event.target.value)}
                  disabled={transitioningCaseStage}
                  placeholder="Enter INZ RFI/PPI request details"
                />
              </div>
              <div className="field" style={{ marginTop: 12 }}>
                <label htmlFor={`inz-rfi-ppi-deadline-${selectedCase.case_id}`}>
                  INZ RFI/PPI deadline
                </label>
                <input
                  id={`inz-rfi-ppi-deadline-${selectedCase.case_id}`}
                  type="date"
                  value={inzRfiPpiDeadline}
                  onChange={(event) => onInzRfiPpiDeadlineChange(event.target.value)}
                  disabled={transitioningCaseStage}
                />
              </div>
              {!canMoveCaseToFollowUp ? (
                <div className="notice notice-amber" style={{ marginBottom: 0, marginTop: 10 }}>
                  <i className="ti ti-lock" />
                  <span>
                    Enter lodgement date and INZ application reference, and complete all current-stage tasks before moving to Follow-up & RFI.
                  </span>
                </div>
              ) : null}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  className="btn btn-sm"
                  disabled={transitioningCaseStage}
                  onClick={onSaveSubmissionDetails}
                >
                  <i className="ti ti-device-floppy" />{" "}
                  {transitioningCaseStage ? "Saving..." : "Save details"}
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={transitioningCaseStage || caseTasksLoading || isUpdatingCaseTasks}
                  onClick={onSubmitSubmissionDecision}
                >
                  <i className="ti ti-arrow-big-right-lines" />{" "}
                  {transitioningCaseStage ? "Moving..." : `Move to ${nextCaseStageActionLabel}`}
                </button>
              </div>
            </>
          ) : null}
          {selectedCaseIsFollowUpAndRfi ? (
            <>
              {!canMoveCaseToDecision ? (
                <div className="notice notice-amber" style={{ marginBottom: 0, marginTop: 10 }}>
                  <i className="ti ti-lock" />
                  <span>
                    Complete all current-stage tasks before moving this case to{" "}
                    {nextCaseStageActionLabel}.
                  </span>
                </div>
              ) : null}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={transitioningCaseStage || caseTasksLoading || isUpdatingCaseTasks}
                  onClick={onSubmitFollowUpDecision}
                >
                  <i className="ti ti-arrow-big-right-lines" />{" "}
                  {transitioningCaseStage ? "Moving..." : `Move to ${nextCaseStageActionLabel}`}
                </button>
              </div>
            </>
          ) : null}
          {shouldShowGenericAdvanceAction ? (
            <>
              {!canAdvanceCaseStage ? (
                <div className="notice notice-amber" style={{ marginBottom: 0, marginTop: 10 }}>
                  <i className="ti ti-lock" />
                  <span>
                    Complete all current-stage checks (required blocking docs reviewed and stage
                    tasks done) before moving to the next stage.
                  </span>
                </div>
              ) : null}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={
                    transitioningCaseStage ||
                    caseTasksLoading ||
                    caseDocumentsLoading ||
                    isUpdatingCaseTasks
                  }
                  onClick={onAdvanceCaseStage}
                >
                  <i className="ti ti-arrow-big-right-lines" />{" "}
                  {transitioningCaseStage ? "Moving..." : advanceCaseButtonLabel}
                </button>
              </div>
            </>
          ) : null}
          {shouldShowCloseCaseAction ? (
            <>
              <div className="field" style={{ marginTop: 12 }}>
                <label htmlFor={`decision-outcome-${selectedCase.case_id}`}>
                  Decision outcome
                </label>
                <select
                  id={`decision-outcome-${selectedCase.case_id}`}
                  value={decisionOutcome}
                  onChange={(event) =>
                    onDecisionOutcomeChange(event.target.value as DecisionOutcome | "")
                  }
                  disabled={transitioningCaseStage}
                >
                  <option value="">Select decision outcome</option>
                  <option value="APPROVED">Approved</option>
                  <option value="DECLINED">Declined</option>
                  <option value="WITHDRAWN">Withdrawn</option>
                </select>
              </div>
              {!canCloseCase ? (
                <div className="notice notice-amber" style={{ marginBottom: 0, marginTop: 10 }}>
                  <i className="ti ti-lock" />
                  <span>
                    Complete all current-stage checks (required documents reviewed and stage tasks
                    done) before closing this case.
                  </span>
                </div>
              ) : null}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button
                  className="btn btn-sm"
                  disabled={transitioningCaseStage || !decisionOutcome}
                  onClick={onSaveDecisionOutcome}
                >
                  <i className="ti ti-device-floppy" />{" "}
                  {transitioningCaseStage ? "Saving..." : "Save details"}
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={
                    transitioningCaseStage ||
                    caseTasksLoading ||
                    caseDocumentsLoading ||
                    isUpdatingCaseTasks ||
                    !canCloseCase
                  }
                  onClick={onCloseCase}
                >
                  <i className="ti ti-circle-check" />{" "}
                  {transitioningCaseStage ? "Closing..." : "Close case"}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </section>
      </div>

      {reviewModalDocument && reviewModalSource === "case" ? (
        <div
          className="assistant-modal-overlay"
          onClick={() => {
            if (!isReviewActionInFlight) {
              onCloseDocumentModal();
            }
          }}
        >
          <section
            className="assistant-modal card assistant-document-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Review uploaded document"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <div className="assistant-modal-header">
              <div className="card-hd" style={{ marginBottom: 0 }}>
                <i className="ti ti-file-search" />{" "}
                {reviewModalMode === "view" ? "View document" : "Review document"}
              </div>
              <button className="btn btn-sm" disabled={isReviewActionInFlight} onClick={onCloseDocumentModal}>
                <i className="ti ti-x" /> Close
              </button>
            </div>

            <div className="assistant-stage-row" style={{ marginTop: 10 }}>
              {reviewModalDocument.label}
            </div>

            {reviewModalError ? (
              <section className="notice notice-red" style={{ marginTop: 12 }}>
                <i className="ti ti-alert-triangle" />
                <span>{reviewModalError}</span>
              </section>
            ) : null}

            <div
              className={`assistant-document-review-grid${
                shouldShowExtractedDetailsPanel ? "" : " assistant-document-review-grid-single"
              }`}
            >
              <div>
                <div className="assistant-document-preview">
                  {reviewModalFile ? (
                    reviewModalFile.content_type.startsWith("image/") ? (
                      <img src={reviewModalFile.blob_url} alt={reviewModalFile.file_name} />
                    ) : (
                      <iframe
                        title={`Preview ${reviewModalFile.file_name}`}
                        src={reviewModalFile.blob_url}
                      />
                    )
                  ) : (
                    <div className="assistant-stage-row">No uploaded file available for preview.</div>
                  )}
                </div>

                {reviewModalFile ? (
                  <div className="assistant-stage-row" style={{ marginTop: 8 }}>
                    File: {reviewModalFile.file_name} (
                    {Math.round(reviewModalFile.size_bytes / 1024)} KB)
                  </div>
                ) : null}
              </div>

              {shouldShowExtractedDetailsPanel ? (
                <section className="assistant-ciq-panel">
                  <div className="card-hd" style={{ marginBottom: 0, fontSize: 14 }}>
                    <i className="ti ti-scan" /> Extracted details
                  </div>
                  <div className="assistant-stage-row" style={{ marginTop: 8 }}>
                    Source:{" "}
                    {shouldUseCiqReviewFlow
                      ? "CIQ document-details API"
                      : "Stored extracted data"}
                  </div>
                  {ciqDocumentDetailsLoading ? (
                    <div className="assistant-stage-row" style={{ marginTop: 10 }}>
                      Fetching details...
                    </div>
                  ) : null}
                  {!ciqDocumentDetailsLoading && ciqDocumentDetailsError ? (
                    <section
                      className="notice notice-red"
                      style={{ marginTop: 10, marginBottom: 0 }}
                    >
                      <i className="ti ti-alert-triangle" />
                      <span>{ciqDocumentDetailsError}</span>
                    </section>
                  ) : null}
                  {!ciqDocumentDetailsLoading &&
                  !ciqDocumentDetailsError &&
                  ciqDetails.length === 0 ? (
                    <div className="assistant-stage-row" style={{ marginTop: 10 }}>
                      No extracted details returned.
                    </div>
                  ) : null}
                  {!ciqDocumentDetailsLoading &&
                  !ciqDocumentDetailsError &&
                  ciqDetails.length > 0 ? (
                    <div className="assistant-ciq-fields">
                      {ciqDetails.map((entry) => {
                        const renderedValue = entry.value || "";
                        const isMultiline =
                          renderedValue.length > 80 || renderedValue.includes("\n");
                        const isEditable = reviewModalMode === "review" && !isReviewActionInFlight;
                        return (
                          <div className="field assistant-ciq-field" key={entry.key}>
                            <label>{toLabel(entry.key)}</label>
                            {isMultiline ? (
                              <textarea
                                rows={4}
                                value={renderedValue}
                                readOnly={!isEditable}
                                onChange={(event) =>
                                  onCiqDocumentDetailsFieldChange(entry.key, event.target.value)
                                }
                              />
                            ) : (
                              <input
                                type="text"
                                value={renderedValue}
                                readOnly={!isEditable}
                                onChange={(event) =>
                                  onCiqDocumentDetailsFieldChange(entry.key, event.target.value)
                                }
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  {reviewModalMode === "review" ? (
                    <label className="assistant-ciq-confirm">
                      <input
                        type="checkbox"
                        checked={ciqDocumentDetailsConfirmed}
                        disabled={!canConfirmCiqDetails || isReviewActionInFlight}
                        onChange={(event) =>
                          onCiqDocumentDetailsConfirmedChange(event.target.checked)
                        }
                      />
                      <span>I confirm the extracted details are correct.</span>
                    </label>
                  ) : null}
                </section>
              ) : null}
            </div>

            {reviewModalMode === "review" ? (
              <div className="field" style={{ marginTop: 12 }}>
                <label>Rejection reason (only used when rejecting)</label>
                <textarea
                  rows={3}
                  value={reviewRejectReason}
                  disabled={isReviewActionInFlight}
                  onChange={(event) => onReviewRejectReasonChange(event.target.value)}
                  placeholder="Optional reason for rejection."
                />
              </div>
            ) : null}

            <div className="assistant-modal-actions">
              <button className="btn btn-sm" disabled={isReviewActionInFlight} onClick={onCloseDocumentModal}>
                Cancel
              </button>
              {reviewModalMode === "review" ? (
                <>
                  <button
                    className="btn btn-danger btn-sm"
                    disabled={isReviewActionInFlight || !reviewModalFile}
                    onClick={() => onReviewAction("REJECT")}
                  >
                    <i className="ti ti-x" /> {isReviewActionInFlight ? "Saving..." : "Reject"}
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={
                      isReviewActionInFlight ||
                      ciqDocumentDetailsLoading ||
                      !canReviewWithCiqValidation
                    }
                    onClick={() => onReviewAction("VERIFY")}
                  >
                    <i className="ti ti-check" /> {isReviewActionInFlight ? "Saving..." : "Review"}
                  </button>
                </>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
};

export default function AdviserPage() {
  const { user } = useAuth();
  const {
    caseload,
    loading,
    error,
    nextLeadPage,
    prevLeadPage,
    nextCasePage,
    prevCasePage,
    refresh,
  } = useAdvisorDashboard();
  const [selectedCaseId, setSelectedCaseId] = useState<string>("");
  const [activeSelection, setActiveSelection] = useState<ActiveSelection>("lead");
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [selectedLeadDetail, setSelectedLeadDetail] = useState<AdvisorLeadDetail | null>(
    null
  );
  const [leadDetailLoading, setLeadDetailLoading] = useState(false);
  const [leadDetailError, setLeadDetailError] = useState<string | null>(null);
  const [consultationNotes, setConsultationNotes] = useState("");
  const [qualificationOutcome, setQualificationOutcome] =
    useState<QualificationOutcome>("PROCEED");
  const [savingQualification, setSavingQualification] = useState(false);
  const [sendingEngagementLetter, setSendingEngagementLetter] = useState(false);
  const [convertingLead, setConvertingLead] = useState(false);
  const [conversionError, setConversionError] = useState<string | null>(null);
  const [conversionSuccess, setConversionSuccess] = useState<string | null>(null);
  const [noShowFollowupOutcome, setNoShowFollowupOutcome] =
    useState<NoShowFollowupOutcome>("REACHED");
  const [noShowReachedAt, setNoShowReachedAt] = useState(currentDateTimeLocalInputValue());
  const [eligibilityScore, setEligibilityScore] = useState("");
  const [pathway, setPathway] = useState("");
  const [onboardingEligibility, setOnboardingEligibility] =
    useState<EligibilityAssessment>("ELIGIBLE");
  const [qualificationError, setQualificationError] = useState<string | null>(null);
  const [qualificationSuccess, setQualificationSuccess] = useState<string | null>(null);
  const [caseStageTasks, setCaseStageTasks] = useState<AdvisorCaseTaskItem[]>([]);
  const [caseTasksLoading, setCaseTasksLoading] = useState(false);
  const [caseTasksError, setCaseTasksError] = useState<string | null>(null);
  const [updatingCaseTaskIds, setUpdatingCaseTaskIds] = useState<Record<string, boolean>>({});
  const [caseCurrentStageDocumentsData, setCaseCurrentStageDocumentsData] =
    useState<CaseCurrentStageDocumentsResponse | null>(null);
  const [caseDocumentsLoading, setCaseDocumentsLoading] = useState(false);
  const [caseDocumentsError, setCaseDocumentsError] = useState<string | null>(null);
  const [caseCollatedDetails, setCaseCollatedDetails] =
    useState<CaseCollatedDetailsResponse | null>(null);
  const [caseCollatedDetailsLoading, setCaseCollatedDetailsLoading] = useState(false);
  const [caseCollatedDetailsError, setCaseCollatedDetailsError] = useState<string | null>(null);
  const [leadStageTasks, setLeadStageTasks] = useState<AdvisorLeadTaskItem[]>([]);
  const [leadTasksLoading, setLeadTasksLoading] = useState(false);
  const [leadTasksError, setLeadTasksError] = useState<string | null>(null);
  const [updatingLeadTaskIds, setUpdatingLeadTaskIds] = useState<Record<string, boolean>>({});
  const [assignedTasks, setAssignedTasks] = useState<AdvisorAssignedTaskItem[]>([]);
  const [assignedTasksLoading, setAssignedTasksLoading] = useState(false);
  const [assignedTasksError, setAssignedTasksError] = useState<string | null>(null);
  const [updatingAssignedTaskIds, setUpdatingAssignedTaskIds] = useState<Record<string, boolean>>(
    {}
  );
  const [workflowTemplatesById, setWorkflowTemplatesById] = useState<
    Record<string, WorkflowTemplateResponse>
  >({});
  const [currentStageDocumentsData, setCurrentStageDocumentsData] =
    useState<LeadCurrentStageDocumentsResponse | null>(null);
  const [leadDocumentsLoading, setLeadDocumentsLoading] = useState(false);
  const [leadDocumentsError, setLeadDocumentsError] = useState<string | null>(null);
  const [uploadingDocumentIds, setUploadingDocumentIds] = useState<Record<string, boolean>>({});
  const [reviewingDocumentIds, setReviewingDocumentIds] = useState<Record<string, boolean>>({});
  const [reviewModalDocument, setReviewModalDocument] = useState<LeadStageDocumentItem | null>(null);
  const [reviewModalMode, setReviewModalMode] = useState<"review" | "view">("review");
  const [reviewModalSource, setReviewModalSource] = useState<DocumentModalSource>("lead");
  const [reviewModalError, setReviewModalError] = useState<string | null>(null);
  const [reviewRejectReason, setReviewRejectReason] = useState("");
  const [ciqDocumentDetailsLoading, setCiqDocumentDetailsLoading] = useState(false);
  const [ciqDocumentDetailsError, setCiqDocumentDetailsError] = useState<string | null>(null);
  const [ciqDocumentDetailsResponse, setCiqDocumentDetailsResponse] =
    useState<DocumentDetailsResponse | null>(null);
  const [ciqDocumentDetailsDraft, setCiqDocumentDetailsDraft] = useState<Record<string, string>>(
    {}
  );
  const [ciqDocumentDetailsConfirmed, setCiqDocumentDetailsConfirmed] = useState(false);
  const ciqDocumentDetailsRequestIdRef = useRef(0);
  const [recentActivity, setRecentActivity] = useState<AdvisorCaseEventItem[]>([]);
  const [recentActivityLoading, setRecentActivityLoading] = useState(false);
  const [recentActivityError, setRecentActivityError] = useState<string | null>(null);
  const [transitioningCaseStage, setTransitioningCaseStage] = useState(false);
  const [caseTransitionError, setCaseTransitionError] = useState<string | null>(null);
  const [caseTransitionSuccess, setCaseTransitionSuccess] = useState<string | null>(null);
  const [internalReviewOutcome, setInternalReviewOutcome] = useState<
    InternalReviewOutcome | ""
  >("");
  const [clientReviewApproval, setClientReviewApproval] = useState<
    ClientReviewApproval | ""
  >("");
  const [clientReviewFeedback, setClientReviewFeedback] = useState("");
  const [decisionOutcome, setDecisionOutcome] = useState<DecisionOutcome | "">("");
  const [submissionReferenceNumber, setSubmissionReferenceNumber] = useState("");
  const [submissionDate, setSubmissionDate] = useState("");
  const [inzRfiPpiRequest, setInzRfiPpiRequest] = useState("");
  const [inzRfiPpiDeadline, setInzRfiPpiDeadline] = useState("");
  const [seniorAdvisors, setSeniorAdvisors] = useState<AdvisorDirectoryUser[]>([]);
  const [assignmentAdvisors, setAssignmentAdvisors] = useState<AdvisorDirectoryUser[]>([]);
  const [assignmentAssistants, setAssignmentAssistants] = useState<AdvisorDirectoryUser[]>([]);
  const [seniorAdvisorsLoading, setSeniorAdvisorsLoading] = useState(false);
  const [seniorAdvisorsError, setSeniorAdvisorsError] = useState<string | null>(null);
  const [selectedSeniorAdvisorId, setSelectedSeniorAdvisorId] = useState("");
  const [advisorFirmId, setAdvisorFirmId] = useState<string | null>(null);
  const [availableWorkflowTemplates, setAvailableWorkflowTemplates] = useState<
    WorkflowTemplateResponse[]
  >([]);
  const [workflowTemplatesLoading, setWorkflowTemplatesLoading] = useState(false);
  const [workflowTemplatesError, setWorkflowTemplatesError] = useState<string | null>(null);
  const [leadCaptureTemplateId, setLeadCaptureTemplateId] = useState("");
  const [leadCaptureSourceChannel, setLeadCaptureSourceChannel] = useState("web");
  const [leadCaptureContactOutcome, setLeadCaptureContactOutcome] =
    useState<InitialLeadContactOutcome | "">("");
  const [leadCaptureCallLoggedAt, setLeadCaptureCallLoggedAt] = useState(
    currentDateTimeLocalInputValue()
  );
  const [leadCapturePhone, setLeadCapturePhone] = useState("");
  const [leadCaptureAssignedAdvisorId, setLeadCaptureAssignedAdvisorId] = useState("");
  const [leadCaptureAssignedAssistantId, setLeadCaptureAssignedAssistantId] = useState("");
  const [leadCaptureSaving, setLeadCaptureSaving] = useState(false);
  const [leadCaptureError, setLeadCaptureError] = useState<string | null>(null);
  const [leadCaptureSuccess, setLeadCaptureSuccess] = useState<string | null>(null);
  const [
    contactedFreeEligibilityConsultationOffered,
    setContactedFreeEligibilityConsultationOffered,
  ] = useState<"YES" | "NO" | "">("");
  const [
    contactedFreeEligibilityConsultationDoneAt,
    setContactedFreeEligibilityConsultationDoneAt,
  ] = useState("");
  const [
    contactedFreeEligibilityConsultationDetails,
    setContactedFreeEligibilityConsultationDetails,
  ] = useState("");
  const [contactedDetailsSaving, setContactedDetailsSaving] = useState(false);
  const [sendQualifyingQuestionsEmailLoading, setSendQualifyingQuestionsEmailLoading] =
    useState(false);
  const [contactedDetailsError, setContactedDetailsError] = useState<string | null>(null);
  const [contactedDetailsSuccess, setContactedDetailsSuccess] = useState<string | null>(null);
  const [qualifyingQuestions, setQualifyingQuestions] =
    useState<LeadQualifyingQuestionsResponse | null>(null);
  const [qualifyingQuestionsLoading, setQualifyingQuestionsLoading] = useState(false);
  const [qualifyingQuestionsError, setQualifyingQuestionsError] = useState<string | null>(null);

  const caseloadCases = useMemo(() => caseload?.cases ?? [], [caseload]);
  const caseloadLeads = useMemo(() => caseload?.leads ?? [], [caseload]);
  const orderedAssignedTasks = useMemo(() => {
    return [...assignedTasks].sort((a, b) => {
      const aTime = a.due_at ? new Date(a.due_at).getTime() : Number.POSITIVE_INFINITY;
      const bTime = b.due_at ? new Date(b.due_at).getTime() : Number.POSITIVE_INFINITY;
      return aTime - bTime;
    });
  }, [assignedTasks]);
  const advisorKpis = useMemo(
    () => ({
      openCases: caseloadCases.length,
      assignedLeads: caseloadLeads.length,
      openTasks: orderedAssignedTasks.filter((task) => task.status === "OPEN").length,
      recentEvents: recentActivity.length,
    }),
    [caseloadCases.length, caseloadLeads.length, orderedAssignedTasks, recentActivity.length]
  );
  const leadNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const lead of caseloadLeads) {
      const name = `${lead.first_name} ${lead.last_name}`.trim();
      if (name) {
        map.set(lead.lead_id, name);
      }
    }
    return map;
  }, [caseloadLeads]);
  const caseNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const caseItem of caseloadCases) {
      const caseName = caseItem.title?.trim();
      if (caseName) {
        map.set(caseItem.case_id, caseName);
      }
    }
    return map;
  }, [caseloadCases]);
  const selectedCase =
    activeSelection === "case"
      ? caseloadCases.find((caseItem) => caseItem.case_id === selectedCaseId) ?? null
      : null;
  const selectedCaseTemplateId = selectedCase?.template_id ?? null;
  const selectedCaseTemplate = useMemo(() => {
    if (!selectedCaseTemplateId) return null;
    return workflowTemplatesById[selectedCaseTemplateId] ?? null;
  }, [selectedCaseTemplateId, workflowTemplatesById]);
  const selectedCaseWorkflow = useMemo(() => {
    if (!selectedCase) return null;
    return deriveCaseWorkflow(
      selectedCase.lifecycle_status,
      selectedCase.current_stage_label,
      selectedCaseTemplate
    );
  }, [selectedCase, selectedCaseTemplate]);
  const selectedLead =
    activeSelection === "lead"
      ? caseloadLeads.find((lead) => lead.lead_id === selectedLeadId) ?? null
      : null;
  const selectedLeadTemplateId = selectedLeadDetail?.template_id ?? selectedLead?.template_id ?? null;
  const selectedLeadTemplate = useMemo(() => {
    if (!selectedLeadTemplateId) return null;
    return workflowTemplatesById[selectedLeadTemplateId] ?? null;
  }, [selectedLeadTemplateId, workflowTemplatesById]);
  const selectedLeadWorkflow = useMemo(() => {
    if (!selectedLead) return null;
    const resolvedStatus = selectedLeadDetail?.status ?? selectedLead.status;
    return deriveLeadWorkflow(resolvedStatus ?? "", selectedLeadTemplate);
  }, [selectedLead, selectedLeadDetail?.status, selectedLeadTemplate]);
  const selectedLeadCurrentStageKey = (
    selectedLeadWorkflow?.currentStageKey ?? ""
  ).trim().toLowerCase();
  const selectedLeadIsLeadCaptureStage =
    selectedLeadCurrentStageKey === "lead_capture" ||
    selectedLeadCurrentStageKey === "lead_captured";
  const selectedLeadIsContactedStage = selectedLeadCurrentStageKey === "contacted";
  const ensureWorkflowTemplateLoaded = useCallback(
    async (templateId: string | null | undefined): Promise<WorkflowTemplateResponse | null> => {
      if (!templateId) return null;
      const existing = workflowTemplatesById[templateId];
      if (existing) return existing;

      const template = await advisorService.getWorkflowTemplate(templateId);
      setWorkflowTemplatesById((prev) =>
        prev[templateId] ? prev : { ...prev, [templateId]: template }
      );
      return template;
    },
    [workflowTemplatesById]
  );

  const fetchLeadStageTasks = useCallback(
    async (lead: LeadTaskContext | null) => {
      if (!lead) return [];

      const workflowTemplate = await ensureWorkflowTemplateLoaded(lead.template_id);
      const workflow = deriveLeadWorkflow(
        lead.status ?? "",
        workflowTemplate
      );
      const stageKey = workflow.currentStageKey;
      if (!stageKey) return [];
      const normalizedStageKey = normalizeCaseStageToken(stageKey);

      const taskData = await advisorService.getLeadTasks(lead.lead_id);
      return taskData.tasks.filter(
        (task) => normalizeCaseStageToken(task.stage_key) === normalizedStageKey
      );
    },
    [ensureWorkflowTemplateLoaded]
  );

  const loadLeadCurrentStageDocuments = useCallback(
    async (lead: { lead_id: string; template_id?: string | null } | null) => {
      const leadId = lead?.lead_id ?? null;
      const templateId = lead?.template_id ?? null;
      if (!leadId || !templateId) {
        setCurrentStageDocumentsData(null);
        setLeadDocumentsLoading(false);
        setLeadDocumentsError(null);
        return;
      }

      setLeadDocumentsLoading(true);
      setLeadDocumentsError(null);
      try {
        const data = await advisorService.getLeadCurrentStageDocuments(leadId);
        setCurrentStageDocumentsData(data);
      } catch (err) {
        const errorMessage = toErrorMessage(
          err,
          "Could not load current-stage documents."
        );
        setCurrentStageDocumentsData(null);
        if (
          errorMessage
            .toLowerCase()
            .includes("could not resolve current stage for lead status")
        ) {
          setLeadDocumentsError(null);
          return;
        }
        setLeadDocumentsError(errorMessage);
      } finally {
        setLeadDocumentsLoading(false);
      }
    },
    []
  );

  const fetchCaseStageTasks = useCallback(
    async (caseItem: AdvisorCaseListItem | null) => {
      if (!caseItem) return [];
      const workflowTemplate = await ensureWorkflowTemplateLoaded(caseItem.template_id);
      const workflow = deriveCaseWorkflow(
        caseItem.lifecycle_status,
        caseItem.current_stage_label,
        workflowTemplate
      );
      const stageKey = workflow.currentStageKey;
      if (!stageKey) return [];
      const normalizedStageKey = normalizeCaseStageToken(stageKey);

      const taskData = await advisorService.getCaseTasks(caseItem.case_id);
      return taskData.tasks.filter(
        (task) => normalizeCaseStageToken(task.stage_key) === normalizedStageKey
      );
    },
    [ensureWorkflowTemplateLoaded]
  );

  const loadCaseCurrentStageDocuments = useCallback(async (caseId: string | null) => {
    if (!caseId) {
      setCaseCurrentStageDocumentsData(null);
      setCaseDocumentsLoading(false);
      setCaseDocumentsError(null);
      return;
    }

    setCaseDocumentsLoading(true);
    setCaseDocumentsError(null);
    try {
      const data = await advisorService.getCaseCurrentStageDocuments(caseId);
      setCaseCurrentStageDocumentsData(data);
    } catch (err) {
      setCaseCurrentStageDocumentsData(null);
      setCaseDocumentsError(toErrorMessage(err, "Could not load current-stage documents."));
    } finally {
      setCaseDocumentsLoading(false);
    }
  }, []);

  const loadCaseCollatedDetails = useCallback(async (caseItem: AdvisorCaseListItem | null) => {
    if (!caseItem) {
      setCaseCollatedDetails(null);
      setCaseCollatedDetailsLoading(false);
      setCaseCollatedDetailsError(null);
      return;
    }

    const normalizedLifecycle = normalizeCaseStageToken(caseItem.lifecycle_status);
    if (normalizedLifecycle !== "visa_application") {
      setCaseCollatedDetails(null);
      setCaseCollatedDetailsLoading(false);
      setCaseCollatedDetailsError(null);
      return;
    }

    setCaseCollatedDetailsLoading(true);
    setCaseCollatedDetailsError(null);
    try {
      const data = await advisorService.getCaseCollatedDetails(caseItem.case_id);
      setCaseCollatedDetails(data);
    } catch (err) {
      setCaseCollatedDetails(null);
      setCaseCollatedDetailsError(toErrorMessage(err, "Could not load collated case details."));
    } finally {
      setCaseCollatedDetailsLoading(false);
    }
  }, []);

  const loadAssignedTasks = useCallback(async () => {
    if (!user?.id) {
      setAssignedTasks([]);
      setAssignedTasksError(null);
      setAssignedTasksLoading(false);
      setUpdatingAssignedTaskIds({});
      return;
    }

    setAssignedTasksLoading(true);
    setAssignedTasksError(null);
    try {
      const taskData = await advisorService.listAssignedTasks({
        status: "OPEN",
        limit: 200,
        offset: 0,
      });
      setAssignedTasks(taskData.tasks);
    } catch (err) {
      setAssignedTasks([]);
      setAssignedTasksError(toErrorMessage(err, "Could not load assigned tasks."));
    } finally {
      setAssignedTasksLoading(false);
    }
  }, [user?.id]);

  const loadRecentActivity = useCallback(async () => {
    if (!user?.id) {
      setRecentActivity([]);
      setRecentActivityLoading(false);
      setRecentActivityError(null);
      return;
    }

    setRecentActivityLoading(true);
    setRecentActivityError(null);
    try {
      const data = await advisorService.getAdvisorActivity({
        limit: 20,
        offset: 0,
      });
      setRecentActivity(data.events);
    } catch (err) {
      setRecentActivity([]);
      setRecentActivityError(toErrorMessage(err, "Could not load recent activity."));
    } finally {
      setRecentActivityLoading(false);
    }
  }, [user?.id]);

  const loadSeniorAdvisors = useCallback(async () => {
    if (!user?.id) {
      setAdvisorFirmId(null);
      setSeniorAdvisors([]);
      setAssignmentAdvisors([]);
      setAssignmentAssistants([]);
      setSeniorAdvisorsLoading(false);
      setSeniorAdvisorsError(null);
      return;
    }

    setSeniorAdvisorsLoading(true);
    setSeniorAdvisorsError(null);
    try {
      const advisorUser = await advisorService.getAdvisor(user.id);
      setAdvisorFirmId(advisorUser.firm_id);
      const [advisors, assistants] = await Promise.all([
        advisorService.listAdvisors(advisorUser.firm_id),
        advisorService.listAssistants(advisorUser.firm_id),
      ]);
      setAssignmentAdvisors(advisors);
      setAssignmentAssistants(assistants);
      const currentAdvisorId = user.id.toLowerCase();
      setSeniorAdvisors(
        advisors.filter((advisor) => advisor.id.toLowerCase() !== currentAdvisorId)
      );
    } catch (err) {
      setAdvisorFirmId(null);
      setSeniorAdvisors([]);
      setAssignmentAdvisors([]);
      setAssignmentAssistants([]);
      setSeniorAdvisorsError(toErrorMessage(err, "Could not load advisors."));
    } finally {
      setSeniorAdvisorsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    void loadAssignedTasks();
  }, [loadAssignedTasks]);

  useEffect(() => {
    void loadRecentActivity();
  }, [loadRecentActivity]);

  useEffect(() => {
    void loadSeniorAdvisors();
  }, [loadSeniorAdvisors]);

  useEffect(() => {
    if (!advisorFirmId) {
      setAvailableWorkflowTemplates([]);
      setWorkflowTemplatesLoading(false);
      setWorkflowTemplatesError(null);
      return;
    }

    let cancelled = false;
    const loadWorkflowTemplates = async () => {
      setWorkflowTemplatesLoading(true);
      setWorkflowTemplatesError(null);
      try {
        const templates = await advisorService.listWorkflowTemplates({
          firm_id: advisorFirmId,
          limit: 200,
          offset: 0,
        });
        if (cancelled) return;
        setAvailableWorkflowTemplates(templates);
        setWorkflowTemplatesById((prev) => {
          const next = { ...prev };
          for (const template of templates) {
            next[template.id] = template;
          }
          return next;
        });
      } catch (err) {
        if (cancelled) return;
        setAvailableWorkflowTemplates([]);
        setWorkflowTemplatesError(
          toErrorMessage(err, "Could not load workflow templates for lead capture.")
        );
      } finally {
        if (!cancelled) {
          setWorkflowTemplatesLoading(false);
        }
      }
    };

    void loadWorkflowTemplates();
    return () => {
      cancelled = true;
    };
  }, [advisorFirmId]);

  useEffect(() => {
    const templateIds = Array.from(
      new Set(
        [...caseloadLeads.map((lead) => lead.template_id), ...caseloadCases.map((caseItem) => caseItem.template_id)]
          .filter((templateId): templateId is string => typeof templateId === "string")
      )
    );
    if (templateIds.length === 0) return;

    void Promise.all(
      templateIds.map((templateId) =>
        ensureWorkflowTemplateLoaded(templateId).catch(() => null)
      )
    );
  }, [caseloadCases, caseloadLeads, ensureWorkflowTemplateLoaded]);

  useEffect(() => {
    if (!selectedLeadTemplateId) return;
    void ensureWorkflowTemplateLoaded(selectedLeadTemplateId).catch(() => null);
  }, [ensureWorkflowTemplateLoaded, selectedLeadTemplateId]);

  useEffect(() => {
    if (!selectedCaseTemplateId) return;
    void ensureWorkflowTemplateLoaded(selectedCaseTemplateId).catch(() => null);
  }, [ensureWorkflowTemplateLoaded, selectedCaseTemplateId]);

  useEffect(() => {
    if (activeSelection !== "case") return;
    if (!caseloadCases.length) {
      setSelectedCaseId("");
      if (caseloadLeads.length) {
        setActiveSelection("lead");
      }
      return;
    }
    if (!selectedCaseId || !caseloadCases.some((item) => item.case_id === selectedCaseId)) {
      setSelectedCaseId(caseloadCases[0].case_id);
    }
  }, [activeSelection, caseloadCases, caseloadLeads.length, selectedCaseId]);

  useEffect(() => {
    if (!caseloadLeads.length) {
      setSelectedLeadId(null);
      setSelectedLeadDetail(null);
      setLeadDetailError(null);
      setLeadStageTasks([]);
      setLeadTasksLoading(false);
      setLeadTasksError(null);
      setUpdatingLeadTaskIds({});
      setConsultationNotes("");
      setQualificationOutcome("PROCEED");
      setNoShowFollowupOutcome("REACHED");
      setNoShowReachedAt(currentDateTimeLocalInputValue());
      setEligibilityScore("");
      setPathway("");
      setOnboardingEligibility("ELIGIBLE");
      setSendingEngagementLetter(false);
      setConvertingLead(false);
      setConversionError(null);
      setConversionSuccess(null);
      setCurrentStageDocumentsData(null);
      setLeadDocumentsLoading(false);
      setLeadDocumentsError(null);
      setUploadingDocumentIds({});
      setReviewingDocumentIds({});
      setReviewModalDocument(null);
      setReviewModalMode("review");
      setReviewModalSource("lead");
      setReviewModalError(null);
      setReviewRejectReason("");
      setLeadCaptureTemplateId("");
      setLeadCaptureSourceChannel("web");
      setLeadCaptureContactOutcome("");
      setLeadCaptureCallLoggedAt(currentDateTimeLocalInputValue());
      setLeadCapturePhone("");
      setLeadCaptureAssignedAdvisorId("");
      setLeadCaptureAssignedAssistantId("");
      setLeadCaptureSaving(false);
      setLeadCaptureError(null);
      setLeadCaptureSuccess(null);
      setContactedFreeEligibilityConsultationOffered("");
      setContactedFreeEligibilityConsultationDoneAt("");
      setContactedFreeEligibilityConsultationDetails("");
      setContactedDetailsSaving(false);
      setSendQualifyingQuestionsEmailLoading(false);
      setContactedDetailsError(null);
      setContactedDetailsSuccess(null);
      if (activeSelection === "lead" && caseloadCases.length) {
        setActiveSelection("case");
      }
      return;
    }
    if (activeSelection !== "lead") return;
    if (!selectedLeadId || !caseloadLeads.some((lead) => lead.lead_id === selectedLeadId)) {
      setSelectedLeadId(caseloadLeads[0].lead_id);
    }
  }, [activeSelection, caseloadCases.length, caseloadLeads, selectedLeadId]);

  useEffect(() => {
    let cancelled = false;
    if (activeSelection !== "lead" || !selectedLeadId) return;

    setLeadDetailLoading(true);
    setSelectedLeadDetail(null);
    setLeadDetailError(null);
    setLeadStageTasks([]);
    setLeadTasksError(null);
    setUpdatingLeadTaskIds({});
    setSendingEngagementLetter(false);
    setConvertingLead(false);
    setConversionError(null);
    setConversionSuccess(null);
    setQualificationError(null);
    setQualificationSuccess(null);
    setOnboardingEligibility("ELIGIBLE");
    setNoShowFollowupOutcome("REACHED");
    setNoShowReachedAt(currentDateTimeLocalInputValue());
    setEligibilityScore("");
    setPathway("");
    setCurrentStageDocumentsData(null);
    setLeadDocumentsLoading(false);
    setLeadDocumentsError(null);
    setUploadingDocumentIds({});
    setReviewingDocumentIds({});
    setReviewModalDocument(null);
    setReviewModalMode("review");
    setReviewModalSource("lead");
    setReviewModalError(null);
    setReviewRejectReason("");
    setLeadCaptureTemplateId("");
    setLeadCaptureSourceChannel("web");
    setLeadCaptureContactOutcome("");
    setLeadCaptureCallLoggedAt(currentDateTimeLocalInputValue());
    setLeadCapturePhone("");
    setLeadCaptureAssignedAdvisorId("");
    setLeadCaptureAssignedAssistantId("");
    setLeadCaptureSaving(false);
    setLeadCaptureError(null);
    setLeadCaptureSuccess(null);
    setContactedFreeEligibilityConsultationOffered("");
    setContactedFreeEligibilityConsultationDoneAt("");
    setContactedFreeEligibilityConsultationDetails("");
    setContactedDetailsSaving(false);
    setSendQualifyingQuestionsEmailLoading(false);
    setContactedDetailsError(null);
    setContactedDetailsSuccess(null);
    setQualifyingQuestions(null);
    setQualifyingQuestionsLoading(true);
    setQualifyingQuestionsError(null);

    const loadLeadDetail = async () => {
      try {
        const detail = await advisorService.getLeadDetail(selectedLeadId);
        if (cancelled) return;
        setSelectedLeadDetail(detail);
        setConsultationNotes(detail.consultation_notes ?? "");
        setQualificationOutcome(
          normalizeQualificationOutcome(detail.qualification_outcome)
        );
        setNoShowFollowupOutcome(normalizeNoShowFollowupOutcome(detail.followup_outcome));
        setNoShowReachedAt(
          isoToDateTimeLocalInputValue(detail.followup_reached_at) ||
            currentDateTimeLocalInputValue()
        );
        const initialContactOutcome = String(
          detail.initial_contact_outcome ?? ""
        ).toUpperCase();
        if (
          initialContactOutcome === "CONTACTED" ||
          initialContactOutcome === "NO_ANSWER" ||
          initialContactOutcome === "WRONG_NUMBER"
        ) {
          setLeadCaptureContactOutcome(initialContactOutcome as InitialLeadContactOutcome);
        } else {
          setLeadCaptureContactOutcome("");
        }
        setLeadCaptureTemplateId(detail.template_id ?? "");
        setLeadCaptureSourceChannel((detail.source_channel ?? "web").trim() || "web");
        setLeadCaptureCallLoggedAt(currentDateTimeLocalInputValue());
        setLeadCapturePhone(detail.phone ?? "");
        setLeadCaptureAssignedAdvisorId(detail.assigned_advisor_id ?? "");
        setLeadCaptureAssignedAssistantId(detail.assigned_assistant_id ?? "");
        if (detail.free_eligibility_consultation_offered === true) {
          setContactedFreeEligibilityConsultationOffered("YES");
        } else if (detail.free_eligibility_consultation_offered === false) {
          setContactedFreeEligibilityConsultationOffered("NO");
        } else {
          setContactedFreeEligibilityConsultationOffered("");
        }
        setContactedFreeEligibilityConsultationDoneAt(
          isoToDateTimeLocalInputValue(detail.free_eligibility_consultation_done_at)
        );
        setContactedFreeEligibilityConsultationDetails(
          detail.free_eligibility_consultation_details ?? ""
        );
        setContactedDetailsError(null);
        setContactedDetailsSuccess(null);
        const parsedEligibility = parseEligibilityNotes(detail.consultation_notes);
        setEligibilityScore(parsedEligibility.eligibilityScore);
        setPathway(parsedEligibility.pathway);
        setOnboardingEligibility(toEligibilityAssessment(detail.qualification_outcome));
        void loadLeadCurrentStageDocuments(detail);
        void (async () => {
          try {
            const response = await qualifyingQuestionsService.getLeadQualifyingQuestions(
              detail.lead_id
            );
            if (cancelled) return;
            setQualifyingQuestions(response);
            setQualifyingQuestionsError(null);
          } catch (qualifyingQuestionsErr) {
            if (cancelled) return;
            setQualifyingQuestions(null);
            setQualifyingQuestionsError(
              toErrorMessage(
                qualifyingQuestionsErr,
                "Could not load qualifying question responses."
              )
            );
          } finally {
            if (!cancelled) {
              setQualifyingQuestionsLoading(false);
            }
          }
        })();

        setLeadTasksLoading(true);
        setLeadTasksError(null);
        try {
          const stageTasks = await fetchLeadStageTasks(detail);
          if (cancelled) return;
          setLeadStageTasks(stageTasks);
        } catch (taskErr) {
          if (cancelled) return;
          setLeadStageTasks([]);
          setLeadTasksError(toErrorMessage(taskErr, "Could not load stage tasks."));
        } finally {
          if (!cancelled) {
            setLeadTasksLoading(false);
          }
        }
      } catch (err) {
        if (cancelled) return;
        setSelectedLeadDetail(null);
        setQualifyingQuestions(null);
        setQualifyingQuestionsLoading(false);
        setQualifyingQuestionsError(null);
        setLeadStageTasks([]);
        setLeadTasksLoading(false);
        setLeadDetailError(toErrorMessage(err, "Could not load lead details."));
      } finally {
        if (!cancelled) {
          setLeadDetailLoading(false);
        }
      }
    };

    void loadLeadDetail();

    return () => {
      cancelled = true;
    };
  }, [activeSelection, fetchLeadStageTasks, loadLeadCurrentStageDocuments, selectedLeadId]);

  useEffect(() => {
    let cancelled = false;
    if (activeSelection !== "case" || !selectedCase) {
      setCaseStageTasks([]);
      setCaseTasksLoading(false);
      setCaseTasksError(null);
      setUpdatingCaseTaskIds({});
      setCaseCurrentStageDocumentsData(null);
      setCaseDocumentsLoading(false);
      setCaseDocumentsError(null);
      setCaseCollatedDetails(null);
      setCaseCollatedDetailsLoading(false);
      setCaseCollatedDetailsError(null);
      setTransitioningCaseStage(false);
      setCaseTransitionError(null);
      setCaseTransitionSuccess(null);
      setSelectedSeniorAdvisorId("");
      setInternalReviewOutcome("");
      setClientReviewApproval("");
      setClientReviewFeedback("");
      setDecisionOutcome("");
      setSubmissionReferenceNumber("");
      setSubmissionDate("");
      setInzRfiPpiRequest("");
      setInzRfiPpiDeadline("");
      return;
    }

    setCaseStageTasks([]);
    setCaseTasksLoading(true);
    setCaseTasksError(null);
    setUpdatingCaseTaskIds({});
    setCaseCurrentStageDocumentsData(null);
    setCaseDocumentsLoading(false);
    setCaseDocumentsError(null);
    setCaseCollatedDetails(null);
    setCaseCollatedDetailsLoading(false);
    setCaseCollatedDetailsError(null);
    setTransitioningCaseStage(false);
    setCaseTransitionError(null);
    setCaseTransitionSuccess(null);
    setSelectedSeniorAdvisorId(selectedCase.assigned_senior_advisor ?? "");
    setInternalReviewOutcome(
      normalizeInternalReviewOutcome(selectedCase.summary_json?.internal_review_outcome)
    );
    setClientReviewApproval(
      normalizeClientReviewApproval(selectedCase.summary_json?.client_review_approval)
    );
    setClientReviewFeedback(
      readSummaryString(selectedCase.summary_json ?? {}, "client_review_feedback")
    );
    setDecisionOutcome(
      normalizeDecisionOutcome(selectedCase.summary_json?.decision_outcome)
    );
    setSubmissionReferenceNumber(
      normalizeSubmissionReferenceNumber(
        selectedCase.summary_json?.submission_reference_number
      )
    );
    setSubmissionDate(normalizeSubmissionDate(selectedCase.summary_json?.submission_date));
    setInzRfiPpiRequest(
      normalizeInzRfiPpiRequest(selectedCase.summary_json?.inz_rfi_ppi_request)
    );
    setInzRfiPpiDeadline(
      normalizeInzRfiPpiDeadline(selectedCase.summary_json?.inz_rfi_ppi_deadline)
    );

    const loadCaseData = async () => {
      try {
        const [stageTasks] = await Promise.all([
          fetchCaseStageTasks(selectedCase),
          loadCaseCurrentStageDocuments(selectedCase.case_id),
          loadCaseCollatedDetails(selectedCase),
        ]);
        if (cancelled) return;
        setCaseStageTasks(stageTasks);
      } catch (err) {
        if (cancelled) return;
        setCaseStageTasks([]);
        setCaseTasksError(toErrorMessage(err, "Could not load stage tasks."));
      } finally {
        if (!cancelled) {
          setCaseTasksLoading(false);
        }
      }
    };

    void loadCaseData();

    return () => {
      cancelled = true;
    };
  }, [
    activeSelection,
    fetchCaseStageTasks,
    loadCaseCollatedDetails,
    loadCaseCurrentStageDocuments,
    selectedCase,
  ]);

  const handleSelectLead = useCallback((leadId: string) => {
    setActiveSelection("lead");
    setSelectedCaseId("");
    setCaseStageTasks([]);
    setCaseTasksLoading(false);
    setCaseTasksError(null);
    setUpdatingCaseTaskIds({});
    setCaseCurrentStageDocumentsData(null);
    setCaseDocumentsLoading(false);
    setCaseDocumentsError(null);
    setCaseCollatedDetails(null);
    setCaseCollatedDetailsLoading(false);
    setCaseCollatedDetailsError(null);
    setTransitioningCaseStage(false);
    setCaseTransitionError(null);
    setCaseTransitionSuccess(null);
    setSelectedSeniorAdvisorId("");
    setInternalReviewOutcome("");
    setClientReviewApproval("");
    setClientReviewFeedback("");
    setDecisionOutcome("");
    setSubmissionReferenceNumber("");
    setSubmissionDate("");
    setInzRfiPpiRequest("");
    setInzRfiPpiDeadline("");
    setSelectedLeadId(leadId);
    setUpdatingLeadTaskIds({});
    setSendingEngagementLetter(false);
    setConvertingLead(false);
    setConversionError(null);
    setConversionSuccess(null);
    setQualificationError(null);
    setQualificationSuccess(null);
    setNoShowFollowupOutcome("REACHED");
    setNoShowReachedAt(currentDateTimeLocalInputValue());
    setEligibilityScore("");
    setPathway("");
    setLeadTasksError(null);
    setLeadDocumentsError(null);
    setReviewModalDocument(null);
    setReviewModalMode("review");
    setReviewModalSource("lead");
    setReviewModalError(null);
    setLeadCaptureTemplateId("");
    setLeadCaptureSourceChannel("web");
    setLeadCaptureContactOutcome("");
    setLeadCaptureCallLoggedAt(currentDateTimeLocalInputValue());
    setLeadCapturePhone("");
    setLeadCaptureAssignedAdvisorId("");
    setLeadCaptureAssignedAssistantId("");
    setLeadCaptureSaving(false);
    setLeadCaptureError(null);
    setLeadCaptureSuccess(null);
    setContactedFreeEligibilityConsultationOffered("");
    setContactedFreeEligibilityConsultationDoneAt("");
    setContactedFreeEligibilityConsultationDetails("");
    setContactedDetailsSaving(false);
    setSendQualifyingQuestionsEmailLoading(false);
    setContactedDetailsError(null);
    setContactedDetailsSuccess(null);
  }, []);

  const handleSelectCase = useCallback((caseId: string) => {
    setActiveSelection("case");
    setSelectedCaseId(caseId);
    setCaseStageTasks([]);
    setCaseTasksLoading(false);
    setCaseTasksError(null);
    setUpdatingCaseTaskIds({});
    setCaseCurrentStageDocumentsData(null);
    setCaseDocumentsLoading(false);
    setCaseDocumentsError(null);
    setCaseCollatedDetails(null);
    setCaseCollatedDetailsLoading(false);
    setCaseCollatedDetailsError(null);
    setTransitioningCaseStage(false);
    setCaseTransitionError(null);
    setCaseTransitionSuccess(null);
    setSelectedSeniorAdvisorId("");
    setInternalReviewOutcome("");
    setClientReviewApproval("");
    setClientReviewFeedback("");
    setDecisionOutcome("");
    setSubmissionReferenceNumber("");
    setSubmissionDate("");
    setInzRfiPpiRequest("");
    setInzRfiPpiDeadline("");
    setSelectedLeadId(null);
    setSelectedLeadDetail(null);
    setLeadDetailLoading(false);
    setLeadDetailError(null);
    setLeadStageTasks([]);
    setLeadTasksLoading(false);
    setLeadTasksError(null);
    setUpdatingLeadTaskIds({});
    setConsultationNotes("");
    setQualificationOutcome("PROCEED");
    setNoShowFollowupOutcome("REACHED");
    setNoShowReachedAt(currentDateTimeLocalInputValue());
    setEligibilityScore("");
    setPathway("");
    setOnboardingEligibility("ELIGIBLE");
    setSendingEngagementLetter(false);
    setConvertingLead(false);
    setConversionError(null);
    setConversionSuccess(null);
    setQualificationError(null);
    setQualificationSuccess(null);
    setCurrentStageDocumentsData(null);
    setLeadDocumentsLoading(false);
    setLeadDocumentsError(null);
    setUploadingDocumentIds({});
    setReviewingDocumentIds({});
    setReviewModalDocument(null);
    setReviewModalMode("review");
    setReviewModalSource("lead");
    setReviewModalError(null);
    setReviewRejectReason("");
    setLeadCaptureTemplateId("");
    setLeadCaptureSourceChannel("web");
    setLeadCaptureContactOutcome("");
    setLeadCaptureCallLoggedAt(currentDateTimeLocalInputValue());
    setLeadCapturePhone("");
    setLeadCaptureAssignedAdvisorId("");
    setLeadCaptureAssignedAssistantId("");
    setLeadCaptureSaving(false);
    setLeadCaptureError(null);
    setLeadCaptureSuccess(null);
    setContactedFreeEligibilityConsultationOffered("");
    setContactedDetailsSaving(false);
    setSendQualifyingQuestionsEmailLoading(false);
    setContactedDetailsError(null);
    setContactedDetailsSuccess(null);
  }, []);

  const handleToggleLeadTask = useCallback(
    async (task: AdvisorLeadTaskItem) => {
      if (!selectedLeadDetail) return;
      const nextStatus = task.status === "DONE" ? "OPEN" : "DONE";

      setUpdatingLeadTaskIds((prev) => ({
        ...prev,
        [task.task_id]: true,
      }));
      setLeadTasksError(null);

      try {
        await advisorService.updateLeadTaskStatus(
          selectedLeadDetail.lead_id,
          task.task_id,
          nextStatus,
          user?.id
        );
        const refreshedStageTasks = await fetchLeadStageTasks(selectedLeadDetail);
        setLeadStageTasks(refreshedStageTasks);
        await loadRecentActivity();
        await loadAssignedTasks();
      } catch (err) {
        setLeadTasksError(toErrorMessage(err, "Could not update stage task."));
      } finally {
        setUpdatingLeadTaskIds((prev) => ({
          ...prev,
          [task.task_id]: false,
        }));
      }
    },
    [fetchLeadStageTasks, loadAssignedTasks, loadRecentActivity, selectedLeadDetail, user?.id]
  );

  const handleToggleCaseTask = useCallback(
    async (task: AdvisorCaseTaskItem) => {
      if (!selectedCase) return;
      const nextStatus = task.status === "DONE" ? "OPEN" : "DONE";

      setUpdatingCaseTaskIds((prev) => ({
        ...prev,
        [task.task_id]: true,
      }));
      setCaseTasksError(null);

      try {
        await advisorService.updateCaseTaskStatus(
          selectedCase.case_id,
          task.task_id,
          nextStatus,
          user?.id
        );
        const refreshedStageTasks = await fetchCaseStageTasks(selectedCase);
        setCaseStageTasks(refreshedStageTasks);
        await Promise.all([loadAssignedTasks(), loadRecentActivity()]);
      } catch (err) {
        setCaseTasksError(toErrorMessage(err, "Could not update stage task."));
      } finally {
        setUpdatingCaseTaskIds((prev) => ({
          ...prev,
          [task.task_id]: false,
        }));
      }
    },
    [fetchCaseStageTasks, loadAssignedTasks, loadRecentActivity, selectedCase, user?.id]
  );

  const normalizedSelectedCaseStageKey = normalizeCaseStageToken(
    selectedCaseWorkflow?.currentStageKey ?? selectedCase?.lifecycle_status
  );
  const nextCaseStageKey = selectedCaseWorkflow?.nextStageKey ?? null;
  const nextCaseStageLabel = selectedCaseWorkflow?.nextStageLabel ?? null;
  const previousCaseStageKey = selectedCaseWorkflow?.previousStageKey ?? null;
  const previousCaseStageLabel = selectedCaseWorkflow?.previousStageLabel ?? null;
  const hasNextCaseStage = Boolean(nextCaseStageKey);
  const advanceCaseButtonLabel = `Move to ${
    nextCaseStageLabel ?? toLabel(nextCaseStageKey ?? "next_stage")
  }`;
  const caseBlockingDocuments = (caseCurrentStageDocumentsData?.documents ?? []).filter(
    (document) => document.is_required && document.blocks_stage_exit
  );
  const allCaseBlockingDocumentsReviewed = caseBlockingDocuments.every(
    (document) => document.status === "VERIFIED" || document.status === "WAIVED"
  );
  const caseRequiredReviewDocuments = (caseCurrentStageDocumentsData?.documents ?? []).filter(
    (document) => document.is_required && document.verification_required
  );
  const allCaseRequiredReviewDocumentsReviewed = caseRequiredReviewDocuments.every(
    (document) => document.status === "VERIFIED" || document.status === "WAIVED"
  );
  const hasOpenCaseStageTasks = caseStageTasks.some((task) => task.status === "OPEN");
  const isUpdatingCaseTasks = useMemo(
    () => Object.values(updatingCaseTaskIds).some(Boolean),
    [updatingCaseTaskIds]
  );
  const canAdvanceCaseStage = Boolean(selectedCase) &&
    hasNextCaseStage &&
    !caseTasksLoading &&
    !caseDocumentsLoading &&
    !isUpdatingCaseTasks &&
    !hasOpenCaseStageTasks &&
    allCaseBlockingDocumentsReviewed;
  const canCloseCase = Boolean(
    selectedCase &&
      !selectedCase.closed_at &&
      !hasNextCaseStage &&
      !caseTasksLoading &&
      !caseDocumentsLoading &&
      !isUpdatingCaseTasks &&
      !hasOpenCaseStageTasks &&
      allCaseRequiredReviewDocumentsReviewed
  );

  const resolveCaseStageLabel = useCallback(
    (stageKey: string | null | undefined): string => {
      if (!stageKey) return "";
      const normalizedTargetKey = normalizeCaseStageToken(stageKey);
      const matched = selectedCaseWorkflow?.stages.find(
        (stage) => normalizeCaseStageToken(stage.key) === normalizedTargetKey
      );
      return matched?.label ?? toLabel(stageKey);
    },
    [selectedCaseWorkflow]
  );

  const transitionSelectedCaseStage = useCallback(
    async (
      targetLifecycleStatus: string,
      options?: {
        currentStageLabel?: string;
        stageStatus?: "ACTIVE" | "DONE";
        payload?: Record<string, unknown>;
        successMessage?: string;
        errorMessage?: string;
      }
    ) => {
      if (!selectedCase) {
        setCaseTransitionError("Select a case first.");
        return null;
      }

      const targetStageStatus =
        options?.stageStatus ??
        (normalizeCaseStageToken(targetLifecycleStatus).includes("clos") ? "DONE" : "ACTIVE");
      const targetStageLabel =
        options?.currentStageLabel ?? resolveCaseStageLabel(targetLifecycleStatus);

      setTransitioningCaseStage(true);
      try {
        const result = await advisorService.transitionCaseStage(selectedCase.case_id, {
          lifecycle_status: targetLifecycleStatus,
          stage_status: targetStageStatus,
          current_stage_label: targetStageLabel,
          ...(options?.payload ?? {}),
        });
        await Promise.all([refresh(), loadAssignedTasks(), loadRecentActivity()]);
        setCaseTransitionSuccess(
          options?.successMessage ??
            `Case moved to ${result.current_stage_label ?? toLabel(result.lifecycle_status)}.`
        );
        return result;
      } catch (err) {
        setCaseTransitionError(
          toErrorMessage(err, options?.errorMessage ?? "Could not move case to next stage.")
        );
        return null;
      } finally {
        setTransitioningCaseStage(false);
      }
    },
    [
      loadAssignedTasks,
      loadRecentActivity,
      refresh,
      resolveCaseStageLabel,
      selectedCase,
    ]
  );

  const handleMoveCaseToInternalReview = useCallback(async () => {
    if (!selectedCase) {
      setCaseTransitionError("Select a case first.");
      return;
    }

    setCaseTransitionError(null);
    setCaseTransitionSuccess(null);

    const isApplicationPreparationStage =
      normalizedSelectedCaseStageKey === "application_preparation";
    if (!isApplicationPreparationStage) {
      setCaseTransitionError(
        "This action is available only for Application Preparation stage."
      );
      return;
    }

    if (hasOpenCaseStageTasks) {
      setCaseTransitionError(
        "Complete all current-stage tasks before moving this case to Internal Review."
      );
      return;
    }
    if (!selectedSeniorAdvisorId) {
      setCaseTransitionError(
        "Select a Senior Advisor before moving this case to Internal Review."
      );
      return;
    }
    if (!nextCaseStageKey) {
      setCaseTransitionError("Could not resolve next stage from this workflow template.");
      return;
    }

    const result = await transitionSelectedCaseStage(nextCaseStageKey, {
      currentStageLabel: nextCaseStageLabel ?? resolveCaseStageLabel(nextCaseStageKey),
      payload: {
        assigned_senior_advisor: selectedSeniorAdvisorId,
      },
      errorMessage: "Could not move case to internal review.",
    });
    if (result) {
      setSelectedSeniorAdvisorId(result.assigned_senior_advisor ?? "");
    }
  }, [
    hasOpenCaseStageTasks,
    nextCaseStageKey,
    nextCaseStageLabel,
    normalizedSelectedCaseStageKey,
    resolveCaseStageLabel,
    selectedCase,
    selectedSeniorAdvisorId,
    transitionSelectedCaseStage,
  ]);

  const handleSubmitInternalReviewDecision = useCallback(async () => {
    if (!selectedCase) {
      setCaseTransitionError("Select a case first.");
      return;
    }

    setCaseTransitionError(null);
    setCaseTransitionSuccess(null);

    const isInternalReviewStage = normalizedSelectedCaseStageKey === "internal_review";
    if (!isInternalReviewStage) {
      setCaseTransitionError("This action is available only for Internal Review stage.");
      return;
    }
    if (!internalReviewOutcome) {
      setCaseTransitionError("Select a review outcome first.");
      return;
    }

    const shouldMoveForward = internalReviewOutcome === "PASSED";
    const targetLifecycleStatus = shouldMoveForward ? nextCaseStageKey : previousCaseStageKey;
    const targetStageLabel = shouldMoveForward ? nextCaseStageLabel : previousCaseStageLabel;
    if (!targetLifecycleStatus) {
      setCaseTransitionError("Could not resolve the target stage from this workflow template.");
      return;
    }

    const result = await transitionSelectedCaseStage(targetLifecycleStatus, {
      currentStageLabel: targetStageLabel ?? resolveCaseStageLabel(targetLifecycleStatus),
      payload: {
        review_outcome: internalReviewOutcome,
      },
      successMessage: shouldMoveForward
        ? "Review outcome captured as Passed."
        : "Review outcome captured as Rework required.",
      errorMessage: "Could not submit internal review outcome.",
    });
    if (result) {
      setInternalReviewOutcome(
        normalizeInternalReviewOutcome(result.review_outcome)
      );
    }
  }, [
    internalReviewOutcome,
    nextCaseStageKey,
    nextCaseStageLabel,
    normalizedSelectedCaseStageKey,
    previousCaseStageKey,
    previousCaseStageLabel,
    resolveCaseStageLabel,
    selectedCase,
    transitionSelectedCaseStage,
  ]);

  const handleSubmitClientReviewDecision = useCallback(async () => {
    if (!selectedCase) {
      setCaseTransitionError("Select a case first.");
      return;
    }

    setCaseTransitionError(null);
    setCaseTransitionSuccess(null);

    const isClientReviewStage = normalizedSelectedCaseStageKey === "client_review";
    if (!isClientReviewStage) {
      setCaseTransitionError("This action is available only for Client Review stage.");
      return;
    }
    if (!clientReviewApproval) {
      setCaseTransitionError("Select a client approval status first.");
      return;
    }

    if (hasOpenCaseStageTasks) {
      setCaseTransitionError(
        "Complete all current-stage tasks before moving this case to Submission."
      );
      return;
    }
    if (clientReviewApproval !== "APPROVED") {
      setCaseTransitionError(
        "Client approval must be set to Approved before moving to Submission."
      );
      return;
    }
    if (!nextCaseStageKey) {
      setCaseTransitionError("Could not resolve next stage from this workflow template.");
      return;
    }

    const result = await transitionSelectedCaseStage(nextCaseStageKey, {
      currentStageLabel: nextCaseStageLabel ?? resolveCaseStageLabel(nextCaseStageKey),
      payload: {
        client_approval: clientReviewApproval,
        client_feedback: clientReviewFeedback.trim() || null,
      },
      successMessage: `Client review captured. Case moved to ${
        nextCaseStageLabel ?? resolveCaseStageLabel(nextCaseStageKey)
      }.`,
      errorMessage: "Could not submit client review decision.",
    });
    if (result) {
      setClientReviewApproval(normalizeClientReviewApproval(result.client_approval));
      setClientReviewFeedback(result.client_feedback ?? "");
    }
  }, [
    clientReviewApproval,
    clientReviewFeedback,
    hasOpenCaseStageTasks,
    nextCaseStageKey,
    nextCaseStageLabel,
    normalizedSelectedCaseStageKey,
    resolveCaseStageLabel,
    selectedCase,
    transitionSelectedCaseStage,
  ]);

  const handleSubmitSubmissionDecision = useCallback(async () => {
    if (!selectedCase) {
      setCaseTransitionError("Select a case first.");
      return;
    }

    setCaseTransitionError(null);
    setCaseTransitionSuccess(null);

    const isSubmissionStage =
      normalizedSelectedCaseStageKey === "submission" ||
      normalizedSelectedCaseStageKey === "visa_application";
    if (!isSubmissionStage) {
      setCaseTransitionError(
        "This action is available only for Submission or Visa Application stage."
      );
      return;
    }

    const reference = submissionReferenceNumber.trim();
    if (!reference) {
      setCaseTransitionError("Reference number is required.");
      return;
    }
    const submissionDateValue = submissionDate.trim();
    if (!submissionDateValue) {
      setCaseTransitionError("Submission date is required.");
      return;
    }
    const inzRfiPpiRequestValue = inzRfiPpiRequest.trim();
    const inzRfiPpiDeadlineValue = inzRfiPpiDeadline.trim();

    if (hasOpenCaseStageTasks) {
      setCaseTransitionError(
        "Complete all current-stage tasks before moving this case to Follow-up & RFI."
      );
      return;
    }
    if (!nextCaseStageKey) {
      setCaseTransitionError("Could not resolve next stage from this workflow template.");
      return;
    }

    const result = await transitionSelectedCaseStage(nextCaseStageKey, {
      currentStageLabel: nextCaseStageLabel ?? resolveCaseStageLabel(nextCaseStageKey),
      payload: {
        submission_reference_number: reference,
        submission_date: submissionDateValue,
        inz_rfi_ppi_request: inzRfiPpiRequestValue || null,
        inz_rfi_ppi_deadline: inzRfiPpiDeadlineValue || null,
      },
      successMessage: `Submission details captured. Case moved to ${
        nextCaseStageLabel ?? resolveCaseStageLabel(nextCaseStageKey)
      }.`,
      errorMessage: "Could not move case to next stage.",
    });
    if (result) {
      setSubmissionReferenceNumber(result.submission_reference_number ?? reference);
      setSubmissionDate(result.submission_date ?? submissionDateValue);
      setInzRfiPpiRequest(result.inz_rfi_ppi_request ?? inzRfiPpiRequestValue);
      setInzRfiPpiDeadline(result.inz_rfi_ppi_deadline ?? inzRfiPpiDeadlineValue);
    }
  }, [
    hasOpenCaseStageTasks,
    inzRfiPpiDeadline,
    inzRfiPpiRequest,
    nextCaseStageKey,
    nextCaseStageLabel,
    normalizedSelectedCaseStageKey,
    resolveCaseStageLabel,
    selectedCase,
    submissionDate,
    submissionReferenceNumber,
    transitionSelectedCaseStage,
  ]);

  const handleSaveSubmissionDetails = useCallback(async () => {
    if (!selectedCase) {
      setCaseTransitionError("Select a case first.");
      return;
    }

    setCaseTransitionError(null);
    setCaseTransitionSuccess(null);

    const isSubmissionStage =
      normalizedSelectedCaseStageKey === "submission" ||
      normalizedSelectedCaseStageKey === "visa_application";
    if (!isSubmissionStage) {
      setCaseTransitionError(
        "This action is available only for Submission or Visa Application stage."
      );
      return;
    }

    const currentStageKey = selectedCaseWorkflow?.currentStageKey ?? selectedCase.lifecycle_status;
    if (!currentStageKey) {
      setCaseTransitionError("Could not resolve the current stage from this workflow template.");
      return;
    }

    const reference = submissionReferenceNumber.trim();
    const submissionDateValue = submissionDate.trim();
    const inzRfiPpiRequestValue = inzRfiPpiRequest.trim();
    const inzRfiPpiDeadlineValue = inzRfiPpiDeadline.trim();

    const result = await transitionSelectedCaseStage(currentStageKey, {
      currentStageLabel:
        selectedCaseWorkflow?.currentStageLabel ??
        selectedCase.current_stage_label ??
        resolveCaseStageLabel(currentStageKey),
      payload: {
        submission_reference_number: reference || null,
        submission_date: submissionDateValue || null,
        inz_rfi_ppi_request: inzRfiPpiRequestValue || null,
        inz_rfi_ppi_deadline: inzRfiPpiDeadlineValue || null,
      },
      successMessage: "Visa application details saved.",
      errorMessage: "Could not save visa application details.",
    });
    if (result) {
      setSubmissionReferenceNumber(result.submission_reference_number ?? reference);
      setSubmissionDate(result.submission_date ?? submissionDateValue);
      setInzRfiPpiRequest(result.inz_rfi_ppi_request ?? inzRfiPpiRequestValue);
      setInzRfiPpiDeadline(result.inz_rfi_ppi_deadline ?? inzRfiPpiDeadlineValue);
    }
  }, [
    inzRfiPpiDeadline,
    inzRfiPpiRequest,
    normalizedSelectedCaseStageKey,
    resolveCaseStageLabel,
    selectedCase,
    selectedCaseWorkflow,
    submissionDate,
    submissionReferenceNumber,
    transitionSelectedCaseStage,
  ]);

  const handleSubmitFollowUpDecision = useCallback(async () => {
    if (!selectedCase) {
      setCaseTransitionError("Select a case first.");
      return;
    }

    setCaseTransitionError(null);
    setCaseTransitionSuccess(null);

    const isFollowUpStage = normalizedSelectedCaseStageKey === "follow_up_and_rfi";
    if (!isFollowUpStage) {
      setCaseTransitionError("This action is available only for Follow-up & RFI stage.");
      return;
    }

    if (hasOpenCaseStageTasks) {
      setCaseTransitionError(
        "Complete all current-stage tasks before moving this case to Decision."
      );
      return;
    }
    if (!nextCaseStageKey) {
      setCaseTransitionError("Could not resolve next stage from this workflow template.");
      return;
    }

    await transitionSelectedCaseStage(nextCaseStageKey, {
      currentStageLabel: nextCaseStageLabel ?? resolveCaseStageLabel(nextCaseStageKey),
      successMessage: `Case moved to ${
        nextCaseStageLabel ?? resolveCaseStageLabel(nextCaseStageKey)
      }.`,
      errorMessage: "Could not move case to next stage.",
    });
  }, [
    hasOpenCaseStageTasks,
    nextCaseStageKey,
    nextCaseStageLabel,
    normalizedSelectedCaseStageKey,
    resolveCaseStageLabel,
    selectedCase,
    transitionSelectedCaseStage,
  ]);

  const handleAdvanceCaseStage = useCallback(async () => {
    if (!selectedCase) {
      setCaseTransitionError("Select a case first.");
      return;
    }

    setCaseTransitionError(null);
    setCaseTransitionSuccess(null);

    if (!nextCaseStageKey) {
      setCaseTransitionError("Could not resolve next stage from workflow progression.");
      return;
    }

    if (hasOpenCaseStageTasks || !allCaseBlockingDocumentsReviewed) {
      const pendingDocumentLabels = caseBlockingDocuments
        .filter((document) => document.status !== "VERIFIED" && document.status !== "WAIVED")
        .slice(0, 3)
        .map((document) => document.label)
        .join(", ");
      const pendingDocumentMessage = pendingDocumentLabels
        ? ` Pending required documents: ${pendingDocumentLabels}.`
        : "";
      setCaseTransitionError(
        "Complete all current-stage tasks and verify required blocking documents before moving to the next stage." +
          pendingDocumentMessage
      );
      return;
    }

    await transitionSelectedCaseStage(nextCaseStageKey, {
      currentStageLabel: nextCaseStageLabel ?? resolveCaseStageLabel(nextCaseStageKey),
      stageStatus: "ACTIVE",
      errorMessage: "Could not move case to next stage.",
    });
  }, [
    allCaseBlockingDocumentsReviewed,
    caseBlockingDocuments,
    hasOpenCaseStageTasks,
    nextCaseStageKey,
    nextCaseStageLabel,
    resolveCaseStageLabel,
    selectedCase,
    transitionSelectedCaseStage,
  ]);

  const handleSaveDecisionOutcome = useCallback(async () => {
    if (!selectedCase) {
      setCaseTransitionError("Select a case first.");
      return;
    }

    setCaseTransitionError(null);
    setCaseTransitionSuccess(null);

    if (hasNextCaseStage) {
      setCaseTransitionError(
        "Decision outcome can only be saved at the final workflow stage."
      );
      return;
    }
    if (!decisionOutcome) {
      setCaseTransitionError("Select decision outcome first.");
      return;
    }

    const currentStageKey = selectedCaseWorkflow?.currentStageKey ?? selectedCase.lifecycle_status;
    if (!currentStageKey) {
      setCaseTransitionError("Could not resolve the current stage from this workflow template.");
      return;
    }

    const result = await transitionSelectedCaseStage(currentStageKey, {
      currentStageLabel:
        selectedCaseWorkflow?.currentStageLabel ??
        selectedCase.current_stage_label ??
        resolveCaseStageLabel(currentStageKey),
      payload: {
        decision_outcome: decisionOutcome,
      },
      successMessage: "Decision outcome saved.",
      errorMessage: "Could not save decision outcome.",
    });

    if (result) {
      setDecisionOutcome(normalizeDecisionOutcome(result.decision_outcome));
    }
  }, [
    decisionOutcome,
    hasNextCaseStage,
    resolveCaseStageLabel,
    selectedCase,
    selectedCaseWorkflow,
    transitionSelectedCaseStage,
  ]);

  const handleCloseCase = useCallback(async () => {
    if (!selectedCase) {
      setCaseTransitionError("Select a case first.");
      return;
    }

    setCaseTransitionError(null);
    setCaseTransitionSuccess(null);

    if (hasNextCaseStage) {
      setCaseTransitionError(
        "Case can only be closed from the final workflow stage."
      );
      return;
    }
    if (selectedCase.closed_at) {
      setCaseTransitionError("This case is already closed.");
      return;
    }
    if (!decisionOutcome) {
      setCaseTransitionError(
        "Select decision outcome (Approved / Declined / Withdrawn) before closing the case."
      );
      return;
    }
    if (hasOpenCaseStageTasks || !allCaseRequiredReviewDocumentsReviewed) {
      const pendingDocumentLabels = caseRequiredReviewDocuments
        .filter((document) => document.status !== "VERIFIED" && document.status !== "WAIVED")
        .slice(0, 3)
        .map((document) => document.label)
        .join(", ");
      const pendingDocumentMessage = pendingDocumentLabels
        ? ` Pending required documents: ${pendingDocumentLabels}.`
        : "";
      setCaseTransitionError(
        "Complete all current-stage tasks and review required documents before closing this case." +
          pendingDocumentMessage
      );
      return;
    }

    setTransitioningCaseStage(true);
    try {
      await advisorService.closeCase(selectedCase.case_id, {
        closed_by: user?.id,
        decision_outcome: decisionOutcome,
      });
      await Promise.all([refresh(), loadAssignedTasks(), loadRecentActivity()]);
      setCaseTransitionSuccess("Case closed successfully.");
    } catch (err) {
      setCaseTransitionError(toErrorMessage(err, "Could not close case."));
    } finally {
      setTransitioningCaseStage(false);
    }
  }, [
    allCaseRequiredReviewDocumentsReviewed,
    caseRequiredReviewDocuments,
    decisionOutcome,
    hasNextCaseStage,
    hasOpenCaseStageTasks,
    loadAssignedTasks,
    loadRecentActivity,
    refresh,
    selectedCase,
    user?.id,
  ]);

  const handleSeniorAdvisorChange = useCallback((value: string) => {
    setSelectedSeniorAdvisorId(value);
    if (caseTransitionError) {
      setCaseTransitionError(null);
    }
  }, [caseTransitionError]);

  const handleInternalReviewOutcomeChange = useCallback(
    (value: InternalReviewOutcome | "") => {
      setInternalReviewOutcome(value);
      if (caseTransitionError) {
        setCaseTransitionError(null);
      }
    },
    [caseTransitionError]
  );

  const handleClientReviewApprovalChange = useCallback(
    (value: ClientReviewApproval | "") => {
      setClientReviewApproval(value);
      if (caseTransitionError) {
        setCaseTransitionError(null);
      }
    },
    [caseTransitionError]
  );

  const handleClientReviewFeedbackChange = useCallback(
    (value: string) => {
      setClientReviewFeedback(value);
      if (caseTransitionError) {
        setCaseTransitionError(null);
      }
    },
    [caseTransitionError]
  );

  const handleDecisionOutcomeChange = useCallback(
    (value: DecisionOutcome | "") => {
      setDecisionOutcome(value);
      if (caseTransitionError) {
        setCaseTransitionError(null);
      }
    },
    [caseTransitionError]
  );

  const handleSubmissionReferenceNumberChange = useCallback(
    (value: string) => {
      setSubmissionReferenceNumber(value);
      if (caseTransitionError) {
        setCaseTransitionError(null);
      }
    },
    [caseTransitionError]
  );

  const handleSubmissionDateChange = useCallback(
    (value: string) => {
      setSubmissionDate(value);
      if (caseTransitionError) {
        setCaseTransitionError(null);
      }
    },
    [caseTransitionError]
  );

  const handleInzRfiPpiRequestChange = useCallback(
    (value: string) => {
      setInzRfiPpiRequest(value);
      if (caseTransitionError) {
        setCaseTransitionError(null);
      }
    },
    [caseTransitionError]
  );

  const handleInzRfiPpiDeadlineChange = useCallback(
    (value: string) => {
      setInzRfiPpiDeadline(value);
      if (caseTransitionError) {
        setCaseTransitionError(null);
      }
    },
    [caseTransitionError]
  );

  const handleLeadCaptureTemplateChange = useCallback((value: string) => {
    setLeadCaptureTemplateId(value);
    if (leadCaptureError) {
      setLeadCaptureError(null);
    }
    if (leadCaptureSuccess) {
      setLeadCaptureSuccess(null);
    }
  }, [leadCaptureError, leadCaptureSuccess]);

  const handleLeadCaptureSourceChannelChange = useCallback((value: string) => {
    setLeadCaptureSourceChannel(value);
    if (leadCaptureError) {
      setLeadCaptureError(null);
    }
    if (leadCaptureSuccess) {
      setLeadCaptureSuccess(null);
    }
  }, [leadCaptureError, leadCaptureSuccess]);

  const handleLeadCaptureContactOutcomeChange = useCallback(
    (value: InitialLeadContactOutcome | "") => {
      setLeadCaptureContactOutcome(value);
      if (
        (value === "CONTACTED" || value === "NO_ANSWER") &&
        !leadCaptureCallLoggedAt.trim()
      ) {
        setLeadCaptureCallLoggedAt(currentDateTimeLocalInputValue());
      }
      if (leadCaptureError) {
        setLeadCaptureError(null);
      }
      if (leadCaptureSuccess) {
        setLeadCaptureSuccess(null);
      }
    },
    [leadCaptureCallLoggedAt, leadCaptureError, leadCaptureSuccess]
  );

  const handleLeadCaptureCallLoggedAtChange = useCallback((value: string) => {
    setLeadCaptureCallLoggedAt(value);
    if (leadCaptureError) {
      setLeadCaptureError(null);
    }
    if (leadCaptureSuccess) {
      setLeadCaptureSuccess(null);
    }
  }, [leadCaptureError, leadCaptureSuccess]);

  const handleLeadCapturePhoneChange = useCallback((value: string) => {
    setLeadCapturePhone(value);
    if (leadCaptureError) {
      setLeadCaptureError(null);
    }
    if (leadCaptureSuccess) {
      setLeadCaptureSuccess(null);
    }
  }, [leadCaptureError, leadCaptureSuccess]);

  const handleLeadCaptureAssignedAdvisorChange = useCallback((value: string) => {
    setLeadCaptureAssignedAdvisorId(value);
    if (leadCaptureError) {
      setLeadCaptureError(null);
    }
    if (leadCaptureSuccess) {
      setLeadCaptureSuccess(null);
    }
  }, [leadCaptureError, leadCaptureSuccess]);

  const handleLeadCaptureAssignedAssistantChange = useCallback((value: string) => {
    setLeadCaptureAssignedAssistantId(value);
    if (leadCaptureError) {
      setLeadCaptureError(null);
    }
    if (leadCaptureSuccess) {
      setLeadCaptureSuccess(null);
    }
  }, [leadCaptureError, leadCaptureSuccess]);

  const handleContactedFreeEligibilityConsultationOfferedChange = useCallback(
    (value: "YES" | "NO" | "") => {
      setContactedFreeEligibilityConsultationOffered(value);
      if (value === "YES" && !contactedFreeEligibilityConsultationDoneAt.trim()) {
        setContactedFreeEligibilityConsultationDoneAt(currentDateTimeLocalInputValue());
      }
      if (value !== "YES") {
        setContactedFreeEligibilityConsultationDoneAt("");
        setContactedFreeEligibilityConsultationDetails("");
      }
      if (contactedDetailsError) {
        setContactedDetailsError(null);
      }
      if (contactedDetailsSuccess) {
        setContactedDetailsSuccess(null);
      }
    },
    [
      contactedDetailsError,
      contactedDetailsSuccess,
      contactedFreeEligibilityConsultationDoneAt,
    ]
  );

  const handleContactedFreeEligibilityConsultationDoneAtChange = useCallback(
    (value: string) => {
      setContactedFreeEligibilityConsultationDoneAt(value);
      if (contactedDetailsError) {
        setContactedDetailsError(null);
      }
      if (contactedDetailsSuccess) {
        setContactedDetailsSuccess(null);
      }
    },
    [contactedDetailsError, contactedDetailsSuccess]
  );

  const handleContactedFreeEligibilityConsultationDetailsChange = useCallback(
    (value: string) => {
      setContactedFreeEligibilityConsultationDetails(value);
      if (contactedDetailsError) {
        setContactedDetailsError(null);
      }
      if (contactedDetailsSuccess) {
        setContactedDetailsSuccess(null);
      }
    },
    [contactedDetailsError, contactedDetailsSuccess]
  );

  const handleSaveContactedDetails = useCallback(async () => {
    if (!selectedLeadDetail) {
      setContactedDetailsError("Select a lead first.");
      return;
    }
    if (!selectedLeadIsContactedStage) {
      setContactedDetailsError(
        "Contacted details can only be saved in Stage 2 (Contacted)."
      );
      return;
    }
    if (!contactedFreeEligibilityConsultationOffered) {
      setContactedDetailsError("Select whether free eligibility consultation was offered.");
      return;
    }
    const isFreeEligibilityConsultationOffered =
      contactedFreeEligibilityConsultationOffered === "YES";
    const normalizedConsultationDoneAt = dateTimeLocalToIso(
      contactedFreeEligibilityConsultationDoneAt
    );
    const normalizedConsultationDetails =
      contactedFreeEligibilityConsultationDetails.trim();
    if (isFreeEligibilityConsultationOffered && !normalizedConsultationDoneAt) {
      setContactedDetailsError(
        "Select consultation date and time when consultation was offered."
      );
      return;
    }
    if (isFreeEligibilityConsultationOffered && !normalizedConsultationDetails) {
      setContactedDetailsError(
        "Enter consultation details when consultation was offered."
      );
      return;
    }

    setContactedDetailsSaving(true);
    setContactedDetailsError(null);
    setContactedDetailsSuccess(null);
    setQualificationError(null);
    setQualificationSuccess(null);

    try {
      await advisorService.updateContactedLeadDetails(selectedLeadDetail.lead_id, {
        free_eligibility_consultation_offered: isFreeEligibilityConsultationOffered,
        free_eligibility_consultation_done_at: isFreeEligibilityConsultationOffered
          ? normalizedConsultationDoneAt
          : null,
        free_eligibility_consultation_details: isFreeEligibilityConsultationOffered
          ? normalizedConsultationDetails
          : "",
        updated_by: user?.id,
      });
      const refreshedLeadDetail = await advisorService.getLeadDetail(selectedLeadDetail.lead_id);
      setSelectedLeadDetail(refreshedLeadDetail);
      if (refreshedLeadDetail.free_eligibility_consultation_offered === true) {
        setContactedFreeEligibilityConsultationOffered("YES");
      } else if (refreshedLeadDetail.free_eligibility_consultation_offered === false) {
        setContactedFreeEligibilityConsultationOffered("NO");
      } else {
        setContactedFreeEligibilityConsultationOffered("");
      }
      setContactedFreeEligibilityConsultationDoneAt(
        isoToDateTimeLocalInputValue(refreshedLeadDetail.free_eligibility_consultation_done_at)
      );
      setContactedFreeEligibilityConsultationDetails(
        refreshedLeadDetail.free_eligibility_consultation_details ?? ""
      );
      const movedToNextStage = refreshedLeadDetail.status !== selectedLeadDetail.status;
      if (movedToNextStage) {
        setContactedDetailsSuccess(
          "Contacted details saved. Lead moved to next stage and booking link was shared with lead."
        );
      } else {
        setContactedDetailsSuccess(
          "Contacted details saved. Complete remaining stage requirements to move to next stage."
        );
      }
      await Promise.all([refresh(), loadAssignedTasks(), loadRecentActivity()]);
    } catch (err) {
      setContactedDetailsError(toErrorMessage(err, "Could not save contacted details."));
    } finally {
      setContactedDetailsSaving(false);
    }
  }, [
    contactedFreeEligibilityConsultationDetails,
    contactedFreeEligibilityConsultationDoneAt,
    contactedFreeEligibilityConsultationOffered,
    loadAssignedTasks,
    loadRecentActivity,
    refresh,
    selectedLeadDetail,
    selectedLeadIsContactedStage,
    user?.id,
  ]);

  const handleSendQualifyingQuestionsEmail = useCallback(async () => {
    if (!selectedLeadDetail) {
      setContactedDetailsError("Select a lead first.");
      return;
    }
    if (!selectedLeadIsContactedStage) {
      setContactedDetailsError(
        "Qualifying questions email can only be sent in Stage 2 (Contacted)."
      );
      return;
    }

    setSendQualifyingQuestionsEmailLoading(true);
    setContactedDetailsError(null);
    setContactedDetailsSuccess(null);
    setQualificationError(null);
    setQualificationSuccess(null);

    try {
      await advisorService.sendQualifyingQuestionsEmail(selectedLeadDetail.lead_id, {
        sent_by: user?.id,
      });
      const refreshedLeadDetail = await advisorService.getLeadDetail(selectedLeadDetail.lead_id);
      setSelectedLeadDetail(refreshedLeadDetail);
      if (refreshedLeadDetail.free_eligibility_consultation_offered === true) {
        setContactedFreeEligibilityConsultationOffered("YES");
      } else if (refreshedLeadDetail.free_eligibility_consultation_offered === false) {
        setContactedFreeEligibilityConsultationOffered("NO");
      } else {
        setContactedFreeEligibilityConsultationOffered("");
      }
      setContactedFreeEligibilityConsultationDoneAt(
        isoToDateTimeLocalInputValue(refreshedLeadDetail.free_eligibility_consultation_done_at)
      );
      setContactedFreeEligibilityConsultationDetails(
        refreshedLeadDetail.free_eligibility_consultation_details ?? ""
      );
      const movedToNextStage = refreshedLeadDetail.status !== selectedLeadDetail.status;
      setContactedDetailsSuccess(
        movedToNextStage
          ? "Qualifying questions email sent. Lead moved to next stage."
          : "Qualifying questions email sent. Lead will move to the next stage once qualifying questions are submitted."
      );
      await Promise.all([refresh(), loadAssignedTasks(), loadRecentActivity()]);
    } catch (err) {
      setContactedDetailsError(
        toErrorMessage(err, "Could not send qualifying questions email.")
      );
    } finally {
      setSendQualifyingQuestionsEmailLoading(false);
    }
  }, [
    loadAssignedTasks,
    loadRecentActivity,
    refresh,
    selectedLeadDetail,
    selectedLeadIsContactedStage,
    user?.id,
  ]);

  const handleSaveLeadCapture = useCallback(async () => {
    if (!selectedLeadDetail) {
      setLeadCaptureError("Select a lead first.");
      return;
    }
    if (!selectedLeadIsLeadCaptureStage) {
      setLeadCaptureError("Lead capture update is available only in lead capture stage.");
      return;
    }

    const trimmedTemplateId = leadCaptureTemplateId.trim();
    const trimmedSourceChannel = leadCaptureSourceChannel.trim();
    const trimmedAssignedAdvisorId = leadCaptureAssignedAdvisorId.trim();
    const trimmedAssignedAssistantId = leadCaptureAssignedAssistantId.trim();
    const needsAdvisorAssignment = !selectedLeadDetail.assigned_advisor_id;
    const needsAssistantAssignment = !selectedLeadDetail.assigned_assistant_id;
    const hasRequiredAssignments =
      (!needsAdvisorAssignment || trimmedAssignedAdvisorId.length > 0) &&
      (!needsAssistantAssignment || trimmedAssignedAssistantId.length > 0);

    const hasRequiredCaptureDetails =
      trimmedTemplateId.length > 0 &&
      trimmedSourceChannel.length > 0 &&
      hasRequiredAssignments;

    const hasOutcome = Boolean(leadCaptureContactOutcome);
    const hasWrongNumberPhone =
      leadCaptureContactOutcome !== "WRONG_NUMBER" || leadCapturePhone.trim().length > 0;
    const requiresCallLoggedAt =
      leadCaptureContactOutcome === "CONTACTED" ||
      leadCaptureContactOutcome === "NO_ANSWER";
    const normalizedCallLoggedAt = dateTimeLocalToIso(leadCaptureCallLoggedAt);
    const hasRequiredCallLoggedAt = !requiresCallLoggedAt || Boolean(normalizedCallLoggedAt);
    const hasOpenLeadStageTasks = leadStageTasks.some((task) => task.status === "OPEN");
    const canSubmitOutcome =
      hasOutcome &&
      hasRequiredCaptureDetails &&
      hasWrongNumberPhone &&
      hasRequiredCallLoggedAt &&
      !hasOpenLeadStageTasks;

    setLeadCaptureSaving(true);
    setLeadCaptureError(null);
    setLeadCaptureSuccess(null);
    setQualificationError(null);
    setQualificationSuccess(null);

    try {
      await advisorService.updateLeadCaptureDetails(selectedLeadDetail.lead_id, {
        template_id: trimmedTemplateId || undefined,
        source_channel: trimmedSourceChannel || undefined,
        assigned_advisor_id: trimmedAssignedAdvisorId || undefined,
        assigned_assistant_id: trimmedAssignedAssistantId || undefined,
        updated_by: user?.id,
      });

      let outcomeResult: { status: string } | null = null;
      if (canSubmitOutcome && leadCaptureContactOutcome) {
        outcomeResult = await advisorService.recordInitialLeadContactOutcome(
          selectedLeadDetail.lead_id,
          {
            outcome: leadCaptureContactOutcome,
            phone:
              leadCaptureContactOutcome === "WRONG_NUMBER"
                ? leadCapturePhone.trim()
                : undefined,
            call_logged_at:
              requiresCallLoggedAt ? normalizedCallLoggedAt ?? undefined : undefined,
            contacted_by: user?.id,
          }
        );
      }

      const refreshedLeadDetail = await advisorService.getLeadDetail(selectedLeadDetail.lead_id);
      setSelectedLeadDetail(refreshedLeadDetail);
      setLeadCaptureTemplateId(refreshedLeadDetail.template_id ?? trimmedTemplateId);
      const resolvedSourceChannel =
        refreshedLeadDetail.source_channel ?? (trimmedSourceChannel || "web");
      setLeadCaptureSourceChannel(
        resolvedSourceChannel.trim() || "web"
      );
      const refreshedInitialOutcome = String(
        refreshedLeadDetail.initial_contact_outcome ?? ""
      ).toUpperCase();
      if (
        refreshedInitialOutcome === "CONTACTED" ||
        refreshedInitialOutcome === "NO_ANSWER" ||
        refreshedInitialOutcome === "WRONG_NUMBER"
      ) {
        setLeadCaptureContactOutcome(
          refreshedInitialOutcome as InitialLeadContactOutcome
        );
      }
      setLeadCaptureAssignedAdvisorId(refreshedLeadDetail.assigned_advisor_id ?? "");
      setLeadCaptureAssignedAssistantId(refreshedLeadDetail.assigned_assistant_id ?? "");
      setLeadCapturePhone(refreshedLeadDetail.phone ?? leadCapturePhone);
      setConsultationNotes(refreshedLeadDetail.consultation_notes ?? "");
      setQualificationOutcome(
        normalizeQualificationOutcome(refreshedLeadDetail.qualification_outcome)
      );
      const parsedEligibility = parseEligibilityNotes(refreshedLeadDetail.consultation_notes);
      setEligibilityScore(parsedEligibility.eligibilityScore);
      setPathway(parsedEligibility.pathway);
      setOnboardingEligibility(
        toEligibilityAssessment(refreshedLeadDetail.qualification_outcome)
      );

      void loadLeadCurrentStageDocuments(refreshedLeadDetail);
      setLeadTasksLoading(true);
      setLeadTasksError(null);
      try {
        const refreshedStageTasks = await fetchLeadStageTasks(refreshedLeadDetail);
        setLeadStageTasks(refreshedStageTasks);
      } catch (taskErr) {
        setLeadStageTasks([]);
        setLeadTasksError(toErrorMessage(taskErr, "Could not load stage tasks."));
      } finally {
        setLeadTasksLoading(false);
      }

      const movedToNextStage =
        outcomeResult !== null && outcomeResult.status !== selectedLeadDetail.status;
      if (movedToNextStage) {
        setLeadCaptureSuccess("Lead capture details saved. Lead moved to next stage.");
      } else if (outcomeResult && leadCaptureContactOutcome === "NO_ANSWER") {
        setLeadCaptureSuccess("Lead capture saved. No-answer outcome recorded.");
      } else if (outcomeResult && leadCaptureContactOutcome === "WRONG_NUMBER") {
        setLeadCaptureSuccess("Lead capture saved. Wrong-number outcome recorded.");
      } else if (hasOutcome && !canSubmitOutcome) {
        setLeadCaptureSuccess(
          "Lead capture details saved. Complete remaining required fields and tasks to save contact outcome and move stage."
        );
      } else {
        setLeadCaptureSuccess("Lead capture details saved.");
      }

      await Promise.all([refresh(), loadAssignedTasks(), loadRecentActivity()]);
    } catch (err) {
      setLeadCaptureError(toErrorMessage(err, "Could not save lead capture details."));
    } finally {
      setLeadCaptureSaving(false);
    }
  }, [
    fetchLeadStageTasks,
    leadCaptureCallLoggedAt,
    leadCaptureAssignedAdvisorId,
    leadCaptureAssignedAssistantId,
    leadCaptureContactOutcome,
    leadCapturePhone,
    leadCaptureSourceChannel,
    leadCaptureTemplateId,
    leadStageTasks,
    loadAssignedTasks,
    loadLeadCurrentStageDocuments,
    loadRecentActivity,
    refresh,
    selectedLeadDetail,
    selectedLeadIsLeadCaptureStage,
    user?.id,
  ]);

  const handleNoShowFollowupOutcomeChange = useCallback(
    (value: NoShowFollowupOutcome) => {
      setNoShowFollowupOutcome(value);
      if (value === "REACHED" && !noShowReachedAt.trim()) {
        setNoShowReachedAt(currentDateTimeLocalInputValue());
      }
      setQualificationError(null);
    },
    [noShowReachedAt]
  );

  const handleNoShowReachedAtChange = useCallback((value: string) => {
    setNoShowReachedAt(value);
    setQualificationError(null);
  }, []);

  const handleSaveConsultationOutcome = useCallback(async () => {
    if (!selectedLeadDetail) return;
    const isNoShowAction = noShowFollowupOutcome !== "REACHED";
    const normalizedNoShowReachedAt = dateTimeLocalToIso(noShowReachedAt);
    if (!isNoShowAction && !normalizedNoShowReachedAt) {
      setQualificationError(
        "Reached date and time is required when no-show follow-up is marked as Reached."
      );
      return;
    }

    setSavingQualification(true);
    setQualificationError(null);
    setQualificationSuccess(null);

    try {
      let successMessage = "Consultation details saved.";
      if (isNoShowAction) {
        const result = await advisorService.markConsultationNoShow(selectedLeadDetail.lead_id, {
          followup_outcome: noShowFollowupOutcome,
          consultation_notes: consultationNotes,
        });

        successMessage = result.followup_task_created
          ? `No-show marked (${noShowFollowupOutcomeLabels[result.followup_outcome]}). Assistant task created.`
          : `No-show marked (${noShowFollowupOutcomeLabels[result.followup_outcome]}). Existing assistant task remains open.`;
      } else {
        await advisorService.updateLeadQualification(selectedLeadDetail.lead_id, {
          consultation_notes: consultationNotes,
          qualification_outcome: qualificationOutcome,
          followup_reached_at: normalizedNoShowReachedAt ?? undefined,
        });
      }

      const refreshedLeadDetail = await advisorService.getLeadDetail(
        selectedLeadDetail.lead_id
      );
      setSelectedLeadDetail(refreshedLeadDetail);
      setConsultationNotes(refreshedLeadDetail.consultation_notes ?? "");
      setQualificationOutcome(
        normalizeQualificationOutcome(refreshedLeadDetail.qualification_outcome)
      );
      setNoShowFollowupOutcome(
        normalizeNoShowFollowupOutcome(refreshedLeadDetail.followup_outcome)
      );
      setNoShowReachedAt(
        isoToDateTimeLocalInputValue(refreshedLeadDetail.followup_reached_at) ||
          currentDateTimeLocalInputValue()
      );
      void loadLeadCurrentStageDocuments(refreshedLeadDetail);
      setLeadTasksLoading(true);
      setLeadTasksError(null);
      try {
        const refreshedStageTasks = await fetchLeadStageTasks(refreshedLeadDetail);
        setLeadStageTasks(refreshedStageTasks);
      } catch (taskErr) {
        setLeadStageTasks([]);
        setLeadTasksError(toErrorMessage(taskErr, "Could not load stage tasks."));
      } finally {
        setLeadTasksLoading(false);
      }
      if (
        !isNoShowAction &&
        refreshedLeadDetail.status !== selectedLeadDetail.status &&
        successMessage === "Consultation details saved."
      ) {
        successMessage = "Consultation details saved. Lead moved to next stage.";
      }
      setQualificationSuccess(successMessage);
      await Promise.all([refresh(), loadAssignedTasks(), loadRecentActivity()]);
    } catch (err) {
      setQualificationError(
        toErrorMessage(
          err,
          isNoShowAction
            ? "Could not mark consultation no-show."
            : "Could not save consultation details."
        )
      );
    } finally {
      setSavingQualification(false);
    }
  }, [
    consultationNotes,
    fetchLeadStageTasks,
    loadAssignedTasks,
    loadLeadCurrentStageDocuments,
    loadRecentActivity,
    noShowReachedAt,
    noShowFollowupOutcome,
    qualificationOutcome,
    refresh,
    selectedLeadDetail,
  ]);

  const handleSaveEligibilityDetails = useCallback(async () => {
    if (!selectedLeadDetail) return;

    setSavingQualification(true);
    setQualificationError(null);
    setQualificationSuccess(null);

    try {
      await advisorService.updateLeadQualification(selectedLeadDetail.lead_id, {
        consultation_notes: composeEligibilityNotes(eligibilityScore, pathway),
        qualification_outcome: toQualificationOutcomeFromEligibility(onboardingEligibility),
      });

      const refreshedLeadDetail = await advisorService.getLeadDetail(
        selectedLeadDetail.lead_id
      );
      setSelectedLeadDetail(refreshedLeadDetail);
      setConsultationNotes(refreshedLeadDetail.consultation_notes ?? "");
      setQualificationOutcome(
        normalizeQualificationOutcome(refreshedLeadDetail.qualification_outcome)
      );
      const parsedEligibility = parseEligibilityNotes(refreshedLeadDetail.consultation_notes);
      setEligibilityScore(parsedEligibility.eligibilityScore);
      setPathway(parsedEligibility.pathway);
      setOnboardingEligibility(
        toEligibilityAssessment(refreshedLeadDetail.qualification_outcome)
      );
      void loadLeadCurrentStageDocuments(refreshedLeadDetail);

      setLeadTasksLoading(true);
      setLeadTasksError(null);
      try {
        const refreshedStageTasks = await fetchLeadStageTasks(refreshedLeadDetail);
        setLeadStageTasks(refreshedStageTasks);
      } catch (taskErr) {
        setLeadStageTasks([]);
        setLeadTasksError(toErrorMessage(taskErr, "Could not load stage tasks."));
      } finally {
        setLeadTasksLoading(false);
      }

      setQualificationSuccess("Eligibility details saved.");
      await Promise.all([refresh(), loadAssignedTasks(), loadRecentActivity()]);
    } catch (err) {
      setQualificationError(
        toErrorMessage(err, "Could not save eligibility details.")
      );
    } finally {
      setSavingQualification(false);
    }
  }, [
    eligibilityScore,
    pathway,
    fetchLeadStageTasks,
    loadAssignedTasks,
    loadRecentActivity,
    onboardingEligibility,
    refresh,
    selectedLeadDetail,
    loadLeadCurrentStageDocuments,
  ]);

  const handleSendEngagementLetter = useCallback(async () => {
    if (!selectedLeadDetail) return;
    if (onboardingEligibility !== "ELIGIBLE") {
      setQualificationError(
        "Set eligibility outcome to Eligible before sending fee proposal and service agreement."
      );
      return;
    }

    setSendingEngagementLetter(true);
    setQualificationError(null);
    setQualificationSuccess(null);

    try {
      await advisorService.sendEngagementLetter(selectedLeadDetail.lead_id);

      const refreshedLeadDetail = await advisorService.getLeadDetail(
        selectedLeadDetail.lead_id
      );
      setSelectedLeadDetail(refreshedLeadDetail);
      setConsultationNotes(refreshedLeadDetail.consultation_notes ?? "");
      setQualificationOutcome(
        normalizeQualificationOutcome(refreshedLeadDetail.qualification_outcome)
      );
      const parsedEligibility = parseEligibilityNotes(refreshedLeadDetail.consultation_notes);
      setEligibilityScore(parsedEligibility.eligibilityScore);
      setPathway(parsedEligibility.pathway);
      setOnboardingEligibility(
        toEligibilityAssessment(refreshedLeadDetail.qualification_outcome)
      );
      void loadLeadCurrentStageDocuments(refreshedLeadDetail);

      setLeadTasksLoading(true);
      setLeadTasksError(null);
      try {
        const refreshedStageTasks = await fetchLeadStageTasks(refreshedLeadDetail);
        setLeadStageTasks(refreshedStageTasks);
      } catch (taskErr) {
        setLeadStageTasks([]);
        setLeadTasksError(toErrorMessage(taskErr, "Could not load stage tasks."));
      } finally {
        setLeadTasksLoading(false);
      }

      let successMessage = "Fee proposal and service agreement sent.";
      if (refreshedLeadDetail.status !== selectedLeadDetail.status) {
        successMessage = "Fee proposal and service agreement sent. Lead moved to next stage.";
      }
      setQualificationSuccess(successMessage);
      await Promise.all([refresh(), loadAssignedTasks(), loadRecentActivity()]);
    } catch (err) {
      setQualificationError(
        toErrorMessage(err, "Could not send fee proposal and service agreement.")
      );
    } finally {
      setSendingEngagementLetter(false);
    }
  }, [
    fetchLeadStageTasks,
    loadAssignedTasks,
    loadLeadCurrentStageDocuments,
    loadRecentActivity,
    onboardingEligibility,
    refresh,
    selectedLeadDetail,
  ]);

  const handleConvertLeadToClient = useCallback(async () => {
    if (!selectedLeadDetail) return;

    setConvertingLead(true);
    setConversionError(null);
    setConversionSuccess(null);

    try {
      const result = await assistantService.convertLeadToClient(selectedLeadDetail.lead_id);
      setConversionSuccess(
        `Lead converted to client case ${result.case_number}. Client user created.`
      );
      await Promise.all([refresh(), loadAssignedTasks(), loadRecentActivity()]);
    } catch (err) {
      setConversionError(toErrorMessage(err, "Could not convert lead to client."));
    } finally {
      setConvertingLead(false);
    }
  }, [loadAssignedTasks, loadRecentActivity, refresh, selectedLeadDetail]);

  const updateDocumentInState = useCallback((nextDocument: LeadStageDocumentItem) => {
    setCurrentStageDocumentsData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        documents: prev.documents.map((item) =>
          item.document_item_id === nextDocument.document_item_id ? nextDocument : item
        ),
      };
    });
  }, []);

  const updateCaseDocumentInState = useCallback((nextDocument: LeadStageDocumentItem) => {
    setCaseCurrentStageDocumentsData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        documents: prev.documents.map((item) =>
          item.document_item_id === nextDocument.document_item_id ? nextDocument : item
        ),
      };
    });
  }, []);

  const handleUploadDocument = useCallback(
    async (document: LeadStageDocumentItem, file: File) => {
      if (!selectedLeadDetail) return;

      setLeadDocumentsError(null);
      setUploadingDocumentIds((prev) => ({
        ...prev,
        [document.document_item_id]: true,
      }));
      try {
        const result = await advisorService.uploadLeadDocument(
          selectedLeadDetail.lead_id,
          document.document_item_id,
          file,
          user?.id
        );
        updateDocumentInState(result.document);
        await loadRecentActivity();
      } catch (err) {
        setLeadDocumentsError(toErrorMessage(err, "Could not upload document."));
      } finally {
        setUploadingDocumentIds((prev) => ({
          ...prev,
          [document.document_item_id]: false,
        }));
      }
    },
    [loadRecentActivity, selectedLeadDetail, updateDocumentInState, user?.id]
  );

  const handleUploadCaseDocument = useCallback(
    async (document: LeadStageDocumentItem, file: File) => {
      if (!selectedCase) return;

      setCaseDocumentsError(null);
      setUploadingDocumentIds((prev) => ({
        ...prev,
        [document.document_item_id]: true,
      }));
      try {
        const result = await advisorService.uploadCaseDocument(
          selectedCase.case_id,
          document.document_item_id,
          file,
          user?.id
        );
        updateCaseDocumentInState(result.document);
        await loadRecentActivity();
      } catch (err) {
        setCaseDocumentsError(toErrorMessage(err, "Could not upload document."));
      } finally {
        setUploadingDocumentIds((prev) => ({
          ...prev,
          [document.document_item_id]: false,
        }));
      }
    },
    [loadRecentActivity, selectedCase, updateCaseDocumentInState, user?.id]
  );

  const handleCiqDocumentDetailsFieldChange = useCallback((key: string, value: string) => {
    setCiqDocumentDetailsDraft((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const loadCiqDocumentDetails = useCallback(
    async (document: LeadStageDocumentItem, source: DocumentModalSource) => {
      const requestId = ciqDocumentDetailsRequestIdRef.current + 1;
      ciqDocumentDetailsRequestIdRef.current = requestId;
      const modalFile = latestDocumentFile(document);
      const ciqFileName = modalFile?.blob_path?.trim() || modalFile?.file_name?.trim() || null;
      const ciqDocumentType = resolveCiqDocumentType(document);
      const selectedCaseLeadId = resolveCaseLeadId(selectedCase);
      const azureContainerName =
        source === "case"
          ? selectedCaseLeadId
          : selectedLeadDetail?.lead_id ?? selectedLead?.lead_id ?? null;

      setCiqDocumentDetailsLoading(false);
      setCiqDocumentDetailsConfirmed(false);
      setCiqDocumentDetailsResponse(null);
      setCiqDocumentDetailsDraft({});
      setCiqDocumentDetailsError(null);

      if (!modalFile) {
        setCiqDocumentDetailsError("No uploaded file available to fetch document details.");
        return;
      }

      if (!ciqFileName) {
        setCiqDocumentDetailsError("Unable to resolve file path for CIQ details request.");
        return;
      }

      if (!azureContainerName) {
        setCiqDocumentDetailsError("Unable to resolve container id for CIQ details request.");
        return;
      }

      setCiqDocumentDetailsLoading(true);
      try {
        const details = await advisorService.getDocumentDetails({
          azure_container: azureContainerName,
          file_name: ciqFileName,
          file_type: ciqDocumentType,
        });
        if (ciqDocumentDetailsRequestIdRef.current !== requestId) {
          return;
        }
        setCiqDocumentDetailsResponse(details);
        setCiqDocumentDetailsDraft(ciqResponseToDraft(details));
      } catch (err) {
        if (ciqDocumentDetailsRequestIdRef.current !== requestId) {
          return;
        }
        setCiqDocumentDetailsError(
          toErrorMessage(err, "Could not fetch extracted details from CIQ.")
        );
      } finally {
        if (ciqDocumentDetailsRequestIdRef.current === requestId) {
          setCiqDocumentDetailsLoading(false);
        }
      }
    },
    [selectedCase, selectedLead?.lead_id, selectedLeadDetail?.lead_id]
  );

  const handleOpenDocumentReview = useCallback(
    (document: LeadStageDocumentItem, source: DocumentModalSource = "lead") => {
      setReviewModalSource(source);
      setReviewModalDocument(document);
      setReviewModalMode("review");
      setReviewRejectReason(document.rejection_reason ?? "");
      setReviewModalError(null);
      setCiqDocumentDetailsDraft(extractedDataToDraft(document.extracted_data));
      if (isAiReviewDocument(document)) {
        void loadCiqDocumentDetails(document, source);
      } else {
        ciqDocumentDetailsRequestIdRef.current += 1;
        setCiqDocumentDetailsLoading(false);
        setCiqDocumentDetailsError(null);
        setCiqDocumentDetailsResponse(null);
        setCiqDocumentDetailsDraft({});
        setCiqDocumentDetailsConfirmed(false);
      }
    },
    [loadCiqDocumentDetails]
  );

  const handleOpenDocumentView = useCallback(
    (document: LeadStageDocumentItem, source: DocumentModalSource = "lead") => {
      setReviewModalSource(source);
      setReviewModalDocument(document);
      setReviewModalMode("view");
      setReviewRejectReason(document.rejection_reason ?? "");
      setReviewModalError(null);
      ciqDocumentDetailsRequestIdRef.current += 1;
      setCiqDocumentDetailsLoading(false);
      setCiqDocumentDetailsError(null);
      setCiqDocumentDetailsResponse(null);
      setCiqDocumentDetailsDraft(extractedDataToDraft(document.extracted_data));
      setCiqDocumentDetailsConfirmed(false);
    },
    []
  );

  const handleCloseDocumentModal = useCallback(() => {
    setReviewModalDocument(null);
    setReviewModalMode("review");
    setReviewModalSource("lead");
    setReviewModalError(null);
    ciqDocumentDetailsRequestIdRef.current += 1;
    setCiqDocumentDetailsLoading(false);
    setCiqDocumentDetailsError(null);
    setCiqDocumentDetailsResponse(null);
    setCiqDocumentDetailsDraft({});
    setCiqDocumentDetailsConfirmed(false);
  }, []);

  const handleDocumentReviewAction = useCallback(
    async (action: LeadDocumentReviewAction) => {
      if (!reviewModalDocument) return;
      if (reviewModalSource === "case" && !selectedCase) {
        setReviewModalError("Case context is unavailable. Close and reopen document review.");
        return;
      }
      const leadIdForReview = selectedLeadDetail?.lead_id ?? selectedLead?.lead_id ?? null;
      if (reviewModalSource === "lead" && !leadIdForReview) {
        setReviewModalError("Lead context is unavailable. Close and reopen document review.");
        return;
      }
      const requiresAiReviewConfirmation = isAiReviewDocument(reviewModalDocument);
      if (
        action === "VERIFY" &&
        requiresAiReviewConfirmation &&
        (ciqDocumentDetailsLoading ||
          ciqDocumentDetailsError !== null ||
          ciqDocumentDetailsResponse === null ||
          !ciqDocumentDetailsConfirmed)
      ) {
        setReviewModalError(
          "Fetch CIQ extracted details and confirm them before completing document review."
        );
        return;
      }

      setReviewModalError(null);
      setReviewingDocumentIds((prev) => ({
        ...prev,
        [reviewModalDocument.document_item_id]: true,
      }));
      try {
        const reviewPayload: LeadDocumentReviewPayload = {
          action,
          reviewed_by: user?.id,
          rejection_reason: action === "REJECT" ? reviewRejectReason.trim() || undefined : undefined,
        };
        if (action === "VERIFY" && requiresAiReviewConfirmation) {
          reviewPayload.extracted_data = ciqDraftToPayload(ciqDocumentDetailsDraft);
        }

        if (reviewModalSource === "case" && selectedCase) {
          const result = await advisorService.reviewCaseDocument(
            selectedCase.case_id,
            reviewModalDocument.document_item_id,
            reviewPayload
          );
          updateCaseDocumentInState(result.document);
        } else if (leadIdForReview) {
          const result = await advisorService.reviewLeadDocument(
            leadIdForReview,
            reviewModalDocument.document_item_id,
            reviewPayload
          );
          updateDocumentInState(result.document);
        }
        setReviewModalDocument(null);
        setReviewModalMode("review");
        setReviewModalSource("lead");
        setReviewRejectReason("");
        ciqDocumentDetailsRequestIdRef.current += 1;
        setCiqDocumentDetailsLoading(false);
        setCiqDocumentDetailsError(null);
        setCiqDocumentDetailsResponse(null);
        setCiqDocumentDetailsDraft({});
        setCiqDocumentDetailsConfirmed(false);
        await loadRecentActivity();
      } catch (err) {
        setReviewModalError(toErrorMessage(err, "Could not update document review."));
      } finally {
        setReviewingDocumentIds((prev) => ({
          ...prev,
          [reviewModalDocument.document_item_id]: false,
        }));
      }
    },
    [
      loadRecentActivity,
      reviewModalDocument,
      reviewModalSource,
      reviewRejectReason,
      selectedCase,
      selectedLead,
      selectedLeadDetail,
      ciqDocumentDetailsLoading,
      ciqDocumentDetailsError,
      ciqDocumentDetailsResponse,
      ciqDocumentDetailsDraft,
      ciqDocumentDetailsConfirmed,
      updateCaseDocumentInState,
      updateDocumentInState,
      user?.id,
    ]
  );

  const handleToggleTask = useCallback(
    async (task: AdvisorAssignedTaskItem) => {
      if (!user?.id) return;

      const nextStatus = task.status === "DONE" ? "OPEN" : "DONE";
      setAssignedTasksError(null);
      setUpdatingAssignedTaskIds((prev) => ({
        ...prev,
        [task.task_id]: true,
      }));

      try {
        await advisorService.updateAssignedTaskStatus(task, nextStatus, user.id);
        if (
          task.source === "LEAD" &&
          selectedLeadDetail &&
          task.source_id.toLowerCase() === selectedLeadDetail.lead_id.toLowerCase()
        ) {
          setLeadTasksError(null);
          const refreshedStageTasks = await fetchLeadStageTasks(selectedLeadDetail);
          setLeadStageTasks(refreshedStageTasks);
        }
        await loadRecentActivity();
        await loadAssignedTasks();
      } catch (err) {
        setAssignedTasksError(toErrorMessage(err, "Could not update task status."));
      } finally {
        setUpdatingAssignedTaskIds((prev) => ({
          ...prev,
          [task.task_id]: false,
        }));
      }
    },
    [fetchLeadStageTasks, loadAssignedTasks, loadRecentActivity, selectedLeadDetail, user?.id]
  );

  const resolveTaskFullName = useCallback(
    (task: AdvisorAssignedTaskItem) =>
      resolveAssignedTaskFullName(task, leadNameById, caseNameById),
    [caseNameById, leadNameById]
  );

  return (
    <div className="app">
      <nav className="topnav">
        <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
          <div className="logo">
            <div className="logo-mark">M</div>
            CaseFlow
          </div>
          <div className="nav-tabs">
            {/* <button className="nav-tab active">
              <i className="ti ti-briefcase" /> Immigration adviser
            </button>
            <button className="nav-tab">
              <i className="ti ti-clipboard-list" /> Assistant workspace
            </button>
            <button className="nav-tab">
              <i className="ti ti-user" /> Client portal
            </button>
            <button className="nav-tab">
              <i className="ti ti-chart-bar" /> Firm owner
            </button> */}
          </div>
        </div>
        <div className="nav-right">
          <button
            className="btn btn-sm"
            onClick={() => {
              window.location.reload();
            }}
          >
            <i className="ti ti-refresh" /> Reload page
          </button>
          <UserMenu />
        </div>
      </nav>

      <div id="view-adviser" className="page active">
        <div className="adv-layout">
          <AdviserSidebar
            selectedCaseId={selectedCaseId}
            onSelectCase={handleSelectCase}
            selectedLeadId={selectedLeadId}
            onSelectLead={handleSelectLead}
            cases={caseloadCases}
            leads={caseloadLeads}
            loadingCaseload={loading}
            caseloadError={error}
            leadPagination={caseload?.lead_pagination ?? null}
            casePagination={caseload?.case_pagination ?? null}
            onNextLeadPage={nextLeadPage}
            onPrevLeadPage={prevLeadPage}
            onNextCasePage={nextCasePage}
            onPrevCasePage={prevCasePage}
            workflowTemplatesById={workflowTemplatesById}
          />
          <div className="adv-main">
            <KpiGrid
              openCases={advisorKpis.openCases}
              assignedLeads={advisorKpis.assignedLeads}
              openTasks={advisorKpis.openTasks}
              recentEvents={advisorKpis.recentEvents}
            />
            {activeSelection === "lead" ? (
              <LeadNextActionCard
                selectedLead={selectedLead}
                leadDetail={selectedLeadDetail}
                leadWorkflow={selectedLeadWorkflow}
                currentAdvisorId={user?.id ?? null}
                currentAdvisorName={user?.name ?? null}
                leadStageTasks={leadStageTasks}
                leadTasksLoading={leadTasksLoading}
                leadTasksError={leadTasksError}
                updatingLeadTaskIds={updatingLeadTaskIds}
                loading={leadDetailLoading}
                error={leadDetailError}
                consultationNotes={consultationNotes}
                qualificationOutcome={qualificationOutcome}
                noShowFollowupOutcome={noShowFollowupOutcome}
                noShowReachedAt={noShowReachedAt}
                savingQualification={savingQualification}
                sendingEngagementLetter={sendingEngagementLetter}
                convertingLead={convertingLead}
                conversionError={conversionError}
                conversionSuccess={conversionSuccess}
                agreementSentAt={selectedLeadDetail?.agreement_sent_at ?? null}
                eligibilityScore={eligibilityScore}
                pathway={pathway}
                onboardingEligibility={onboardingEligibility}
                qualificationError={qualificationError}
                qualificationSuccess={qualificationSuccess}
                currentStageDocumentsData={currentStageDocumentsData}
                leadDocumentsLoading={leadDocumentsLoading}
                leadDocumentsError={leadDocumentsError}
                uploadingDocumentIds={uploadingDocumentIds}
                reviewingDocumentIds={reviewingDocumentIds}
                reviewModalDocument={reviewModalDocument}
                reviewModalMode={reviewModalMode}
                reviewModalSource={reviewModalSource}
                reviewModalError={reviewModalError}
                reviewRejectReason={reviewRejectReason}
                ciqDocumentDetailsLoading={ciqDocumentDetailsLoading}
                ciqDocumentDetailsError={ciqDocumentDetailsError}
                ciqDocumentDetailsResponse={ciqDocumentDetailsResponse}
                ciqDocumentDetailsDraft={ciqDocumentDetailsDraft}
                ciqDocumentDetailsConfirmed={ciqDocumentDetailsConfirmed}
                onCiqDocumentDetailsConfirmedChange={setCiqDocumentDetailsConfirmed}
                onCiqDocumentDetailsFieldChange={handleCiqDocumentDetailsFieldChange}
                availableWorkflowTemplates={availableWorkflowTemplates}
                workflowTemplatesLoading={workflowTemplatesLoading}
                workflowTemplatesError={workflowTemplatesError}
                assignmentAdvisors={assignmentAdvisors}
                assignmentAssistants={assignmentAssistants}
                assigneesLoading={seniorAdvisorsLoading}
                assigneesError={seniorAdvisorsError}
                leadCaptureTemplateId={leadCaptureTemplateId}
                leadCaptureSourceChannel={leadCaptureSourceChannel}
                leadCaptureContactOutcome={leadCaptureContactOutcome}
                leadCaptureCallLoggedAt={leadCaptureCallLoggedAt}
                leadCapturePhone={leadCapturePhone}
                leadCaptureAssignedAdvisorId={leadCaptureAssignedAdvisorId}
                leadCaptureAssignedAssistantId={leadCaptureAssignedAssistantId}
                contactedFreeEligibilityConsultationOffered={
                  contactedFreeEligibilityConsultationOffered
                }
                contactedFreeEligibilityConsultationDoneAt={
                  contactedFreeEligibilityConsultationDoneAt
                }
                contactedFreeEligibilityConsultationDetails={
                  contactedFreeEligibilityConsultationDetails
                }
                contactedDetailsSaving={contactedDetailsSaving}
                sendQualifyingQuestionsEmailLoading={sendQualifyingQuestionsEmailLoading}
                contactedDetailsError={contactedDetailsError}
                contactedDetailsSuccess={contactedDetailsSuccess}
                qualifyingQuestions={qualifyingQuestions}
                qualifyingQuestionsLoading={qualifyingQuestionsLoading}
                qualifyingQuestionsError={qualifyingQuestionsError}
                leadCaptureSaving={leadCaptureSaving}
                leadCaptureError={leadCaptureError}
                leadCaptureSuccess={leadCaptureSuccess}
                onToggleLeadTask={handleToggleLeadTask}
                onConsultationNotesChange={setConsultationNotes}
                onQualificationOutcomeChange={setQualificationOutcome}
                onNoShowFollowupOutcomeChange={handleNoShowFollowupOutcomeChange}
                onNoShowReachedAtChange={handleNoShowReachedAtChange}
                onEligibilityScoreChange={setEligibilityScore}
                onPathwayChange={setPathway}
                onOnboardingEligibilityChange={setOnboardingEligibility}
                onSaveConsultationOutcome={handleSaveConsultationOutcome}
                onSaveEligibilityDetails={handleSaveEligibilityDetails}
                onSendEngagementLetter={() => {
                  void handleSendEngagementLetter();
                }}
                onUploadDocument={(document, file) => {
                  void handleUploadDocument(document, file);
                }}
                onOpenDocumentReview={handleOpenDocumentReview}
                onOpenDocumentView={handleOpenDocumentView}
                onCloseDocumentModal={handleCloseDocumentModal}
                onReviewRejectReasonChange={setReviewRejectReason}
                onReviewAction={(action) => {
                  void handleDocumentReviewAction(action);
                }}
                onLeadCaptureTemplateChange={handleLeadCaptureTemplateChange}
                onLeadCaptureSourceChannelChange={handleLeadCaptureSourceChannelChange}
                onLeadCaptureContactOutcomeChange={handleLeadCaptureContactOutcomeChange}
                onLeadCaptureCallLoggedAtChange={handleLeadCaptureCallLoggedAtChange}
                onLeadCapturePhoneChange={handleLeadCapturePhoneChange}
                onLeadCaptureAssignedAdvisorChange={handleLeadCaptureAssignedAdvisorChange}
                onLeadCaptureAssignedAssistantChange={handleLeadCaptureAssignedAssistantChange}
                onContactedFreeEligibilityConsultationOfferedChange={
                  handleContactedFreeEligibilityConsultationOfferedChange
                }
                onContactedFreeEligibilityConsultationDoneAtChange={
                  handleContactedFreeEligibilityConsultationDoneAtChange
                }
                onContactedFreeEligibilityConsultationDetailsChange={
                  handleContactedFreeEligibilityConsultationDetailsChange
                }
                onSaveLeadCapture={() => {
                  void handleSaveLeadCapture();
                }}
                onSaveContactedDetails={() => {
                  void handleSaveContactedDetails();
                }}
                onSendQualifyingQuestionsEmail={() => {
                  void handleSendQualifyingQuestionsEmail();
                }}
                onConvertLeadToClient={() => {
                  void handleConvertLeadToClient();
                }}
              />
            ) : null}
            {activeSelection === "case" ? (
              <CaseWorkflowCard
                selectedCase={selectedCase}
                currentAdvisorId={user?.id ?? null}
                currentAdvisorName={user?.name ?? null}
                seniorAdvisors={seniorAdvisors}
                selectedSeniorAdvisorId={selectedSeniorAdvisorId}
                advisorsLoading={seniorAdvisorsLoading}
                advisorsError={seniorAdvisorsError}
                caseStageTasks={caseStageTasks}
                caseTasksLoading={caseTasksLoading}
                caseTasksError={caseTasksError}
                updatingCaseTaskIds={updatingCaseTaskIds}
                caseCurrentStageDocumentsData={caseCurrentStageDocumentsData}
                caseDocumentsLoading={caseDocumentsLoading}
                caseDocumentsError={caseDocumentsError}
                caseCollatedDetails={caseCollatedDetails}
                caseCollatedDetailsLoading={caseCollatedDetailsLoading}
                caseCollatedDetailsError={caseCollatedDetailsError}
                uploadingDocumentIds={uploadingDocumentIds}
                recentActivity={recentActivity}
                onToggleCaseTask={(task) => {
                  void handleToggleCaseTask(task);
                }}
                onUploadDocument={(document, file) => {
                  void handleUploadCaseDocument(document, file);
                }}
                onOpenDocumentReview={handleOpenDocumentReview}
                onOpenDocumentView={handleOpenDocumentView}
                reviewModalDocument={reviewModalDocument}
                reviewModalMode={reviewModalMode}
                reviewModalSource={reviewModalSource}
                reviewModalError={reviewModalError}
                reviewRejectReason={reviewRejectReason}
                ciqDocumentDetailsLoading={ciqDocumentDetailsLoading}
                ciqDocumentDetailsError={ciqDocumentDetailsError}
                ciqDocumentDetailsResponse={ciqDocumentDetailsResponse}
                ciqDocumentDetailsDraft={ciqDocumentDetailsDraft}
                ciqDocumentDetailsConfirmed={ciqDocumentDetailsConfirmed}
                onCiqDocumentDetailsConfirmedChange={setCiqDocumentDetailsConfirmed}
                onCiqDocumentDetailsFieldChange={handleCiqDocumentDetailsFieldChange}
                reviewingDocumentIds={reviewingDocumentIds}
                onCloseDocumentModal={handleCloseDocumentModal}
                onReviewRejectReasonChange={setReviewRejectReason}
                onReviewAction={(action) => {
                  void handleDocumentReviewAction(action);
                }}
                transitioningCaseStage={transitioningCaseStage}
                caseTransitionError={caseTransitionError}
                caseTransitionSuccess={caseTransitionSuccess}
                selectedCaseWorkflow={selectedCaseWorkflow}
                hasNextCaseStage={hasNextCaseStage}
                canAdvanceCaseStage={canAdvanceCaseStage}
                canCloseCase={canCloseCase}
                advanceCaseButtonLabel={advanceCaseButtonLabel}
                internalReviewOutcome={internalReviewOutcome}
                clientReviewApproval={clientReviewApproval}
                clientReviewFeedback={clientReviewFeedback}
                decisionOutcome={decisionOutcome}
                submissionReferenceNumber={submissionReferenceNumber}
                submissionDate={submissionDate}
                inzRfiPpiRequest={inzRfiPpiRequest}
                inzRfiPpiDeadline={inzRfiPpiDeadline}
                onSelectedSeniorAdvisorChange={handleSeniorAdvisorChange}
                onInternalReviewOutcomeChange={handleInternalReviewOutcomeChange}
                onClientReviewApprovalChange={handleClientReviewApprovalChange}
                onClientReviewFeedbackChange={handleClientReviewFeedbackChange}
                onDecisionOutcomeChange={handleDecisionOutcomeChange}
                onSubmissionReferenceNumberChange={handleSubmissionReferenceNumberChange}
                onSubmissionDateChange={handleSubmissionDateChange}
                onInzRfiPpiRequestChange={handleInzRfiPpiRequestChange}
                onInzRfiPpiDeadlineChange={handleInzRfiPpiDeadlineChange}
                onMoveCaseToInternalReview={() => {
                  void handleMoveCaseToInternalReview();
                }}
                onSubmitInternalReviewDecision={() => {
                  void handleSubmitInternalReviewDecision();
                }}
                onSubmitClientReviewDecision={() => {
                  void handleSubmitClientReviewDecision();
                }}
                onSaveSubmissionDetails={() => {
                  void handleSaveSubmissionDetails();
                }}
                onSubmitSubmissionDecision={() => {
                  void handleSubmitSubmissionDecision();
                }}
                onSubmitFollowUpDecision={() => {
                  void handleSubmitFollowUpDecision();
                }}
                onSaveDecisionOutcome={() => {
                  void handleSaveDecisionOutcome();
                }}
                onAdvanceCaseStage={() => {
                  void handleAdvanceCaseStage();
                }}
                onCloseCase={() => {
                  void handleCloseCase();
                }}
              />
            ) : null}
          </div>
          <AdviserRightPanel
            assignedTasks={orderedAssignedTasks}
            assignedTasksLoading={assignedTasksLoading}
            assignedTasksError={assignedTasksError}
            updatingAssignedTaskIds={updatingAssignedTaskIds}
            caseNameById={caseNameById}
            resolveTaskFullName={resolveTaskFullName}
            onToggleTask={handleToggleTask}
            recentActivity={recentActivity}
            recentActivityLoading={recentActivityLoading}
            recentActivityError={recentActivityError}
          />
        </div>
      </div>
    </div>
  );
}
